import {
  Form,
  FormInstance,
  Input,
  InputNumber,
  Typography,
  Col,
  Row,
  Card,
  Radio,
  Button,
  Alert,
  notification,
  message,
  Modal,
  Select,
  Tooltip,
} from "antd";
import React, { useEffect, useState, Fragment, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import UploadImages from "components/UploadImages";
import { allowFileExtensionsImage } from "config";
import { useAppSelector } from "store";
import { createInputNumberGuards } from "utils";
import { checkSpeedShippingPrice, CheckPriceCarrierItem } from "services/apiSpeedShipping";
import { checkThailandPostPrice } from "services/apiThailandPost";
import { getTransportBrands, getSelfShippingBrands } from "services/ordersApi";
import { useGetSpeedShippingConfig } from "hooks/useShipping";
import { useGetFactoryById } from "hooks/useFactory";
import { useReplaceDefaultMerchant } from "hooks/useMerchant";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetMasterProvince, useGetMasterPackagingType, useGetMasterPackagingTypeById, useGetMasterProductUnit } from "hooks/useMasterData";
import { useGetAllMasterSkuTags } from "hooks/useMasterSkuTag";
import TransportLogo from "./TransportLogo";
import {
  formatWithCommas,
  formatWithCommasFixed2,
  parseNumberWithoutCommas,
  removeLeadingZeros,
  formatDeliveryTimeTh,
  normalizeSpeedLogo,
  parseDeliveryMinDays,
  parseTotal,
  pickTransportBreakdown,
  isBasePriceOrTransportField,
  round2,
  calculateCommissionLevels,
  toFiniteNumberOr0,
  reverseCalculateBasePrice,
} from "./helpers";

import type { UploadFile } from "antd/es/upload/interface";
import { NumericFormat } from "react-number-format";

interface VariationOptionFormProps {
  name: number;
  form: FormInstance;
  skuItem: VariationChoice[];
  variants?: VariantsInput;
  skuImage: UploadFile[] | undefined;
  setSkuImage: (skuImage: UploadFile[] | undefined) => void;
  editMode?: boolean;
  copyMode?: boolean;
  isDraftSave?: boolean;
  editData?: { variant: Variant; price: Price; index: number } | null;
  originalPrices?: PricesInput; // ✅ เก็บค่าจากฐานข้อมูลเพื่อใช้สำหรับ restore c1-c3 (optional)
  originalVariants?: VariantsInput; // ✅ เก็บค่าจากฐานข้อมูลเพื่อใช้ restore shipping_method + dimensions (optional)
  speedHasSearched: boolean;
  setSpeedHasSearched: (speedHasSearched: boolean) => void;
  shouldBlinkButton: boolean; // ✅ สำหรับควบคุมการกระพริบของปุ่มค้นหาบริษัทขนส่ง
  setShouldBlinkButton: (shouldBlinkButton: boolean) => void; // ✅ สำหรับควบคุมการกระพริบของปุ่มค้นหาบริษัทขนส่ง
  factoryId?: string; // ✅ รับ factoryId จาก parent (กรณี admin edit)
  percentCommission?: number; // ✅ percent_commission จากประเภทสินค้าที่เลือก (ใช้ auto-fill total_commission)
  categoryName?: string; // ✅ ชื่อประเภทสินค้าที่เลือก (แสดงใต้ input ชื่อ SKU)
}

const VariationOptionForm: React.FC<VariationOptionFormProps> = ({
  name,
  form,
  variants,
  skuItem,
  skuImage,
  setSkuImage,
  isDraftSave = false,
  editMode = false,
  copyMode = false,
  editData = null,
  originalPrices = [],
  originalVariants = [],
  speedHasSearched,
  setSpeedHasSearched,
  shouldBlinkButton,
  setShouldBlinkButton,
  factoryId,
  percentCommission = 0,
  categoryName = "",
}) => {
  // ✅ watch shipping_method เพื่อใช้ disable/enable fields โดยไม่ต้องซ้อน Form.Item (ซ้อนแล้ว Form จะไม่ bind ค่า)
  const watchedShippingMethod = Form.useWatch(["sku_items", name, "shipping_method"], form);
  const watchedIsCod = Form.useWatch(["sku_items", name, "is_cod"], form);
  const watchedWidth = Form.useWatch(["sku_items", name, "width"], form);
  const watchedLength = Form.useWatch(["sku_items", name, "length"], form);
  const watchedHeight = Form.useWatch(["sku_items", name, "height"], form);
  const watchedWeight = Form.useWatch(["sku_items", name, "weight"], form);
  const watchedApiTransportLogo = Form.useWatch(["sku_items", name, "api_transport_logo"], form);
  const watchedBaseFactoryPrice = Form.useWatch(["sku_items", name, "base_factory_price"], form);
  const watchedSelfTransportPrice = Form.useWatch(["sku_items", name, "self_transport_price"], form);
  const watchedSelfTransportCarrierId = Form.useWatch(["sku_items", name, "self_transport_carrier_id"], form);
  // ✅ เพิ่ม: watch ราคาขาย (factory_price) และราคาท้องตลาด (retail_price) เพื่อใช้คำนวณราคาต่อหน่วยอัตโนมัติ
  //    — ใช้ร่วมกับ quantity_per_unit ใน form field ด้านล่าง (ส่งขึ้น API ได้)
  const watchedFactoryPriceForUnit = Form.useWatch(["sku_items", name, "factory_price"], form);
  const watchedRetailPriceForUnit = Form.useWatch(["sku_items", name, "retail_price"], form);
  const watchedTotalCommission = Form.useWatch(["sku_items", name, "total_commission"], form);
  const watchedLv1 = Form.useWatch(["sku_items", name, "commistion_lv1"], form);
  const watchedLv2 = Form.useWatch(["sku_items", name, "commistion_lv2"], form);
  const watchedLv3 = Form.useWatch(["sku_items", name, "commistion_lv3"], form);
  const watchedLv4 = Form.useWatch(["sku_items", name, "commistion_lv4"], form);
  const watchedLv5 = Form.useWatch(["sku_items", name, "commistion_lv5"], form);
  /** คงเหลือ = ยอดรวม − (c1+c2+c3+c4+c5) — แสดงเป็นตัวเลขอ่านอย่างเดียว ไม่ใช่ input */
  const commissionRemaining = useMemo(
    () =>
      round2(
        toFiniteNumberOr0(watchedTotalCommission) -
          toFiniteNumberOr0(watchedLv1) -
          toFiniteNumberOr0(watchedLv2) -
          toFiniteNumberOr0(watchedLv3) -
          toFiniteNumberOr0(watchedLv4) -
          toFiniteNumberOr0(watchedLv5)
      ),
    [watchedTotalCommission, watchedLv1, watchedLv2, watchedLv3, watchedLv4, watchedLv5]
  );
  const watchedQuantityPerUnit = Form.useWatch(["sku_items", name, "quantity_per_unit"], form);
  const watchedProductUnit = Form.useWatch(["sku_items", name, "product_unit"], form);
  const isSelfShipping = (watchedShippingMethod ?? "self") === "self";
  const [imageName, setImageName] = useState<string[]>([]);
  // ✅ เพิ่ม: ฟิลด์ "จำนวนต่อหน่วย" (`quantity_per_unit`) เก็บใน form โดยตรง
  //    เพื่อใช้คำนวณ unit price อัตโนมัติ และส่งขึ้น API ได้
  const [userRole, setUserRole] = useState<string | null>(null);
  const userProfile = useAppSelector((state) => state.auth.profile);
  const userContact = useAppSelector((state) => state.auth.contact);
  const { data: speedShippingConfig, isLoading: speedConfigLoading } = useGetSpeedShippingConfig();

  // ✅ ดึงข้อมูล Factory เพื่อเอาที่อยู่ Origin (รองรับทั้ง Admin edit และ Factory user)
  const targetFactoryId = factoryId || (userProfile?.user?.role?.ref_type === 'factory' ? userProfile?.user?.role?.ref_id : "");
  // console.log("targetFactoryId", targetFactoryId);
  const { data: factoryData } = useGetFactoryById(targetFactoryId || "");
  const { data: masterProvinceData } = useGetMasterProvince();
  const { data: packagingTypeData, isLoading: packagingTypeLoading } = useGetMasterPackagingType();
  const { data: productUnitData, isLoading: productUnitLoading } = useGetMasterProductUnit();
  // ✅ master_sku_tag กลุ่ม sale_channel (wholesale/retail) สำหรับ Radio "ประเภทการขาย"
  const { data: saleChannelTagsData } = useGetAllMasterSkuTags({
    exclusive_group: "sale_channel",
    is_active: true,
    row_per_page: 50,
  });
  const saleChannelTags = saleChannelTagsData?.data?.master_sku_tags ?? [];

  // ✅ ดึงรายการขนส่งสำหรับ self shipping จาก master_all_type (process_type='self_shipping_carrier')
  const { data: selfCarriersData } = useQuery({
    queryKey: ["selfShippingCarriers"],
    queryFn: getSelfShippingBrands,
    staleTime: 5 * 60 * 1000,
  });
  const selfCarriers: { id: string; uuid: string; description: string; image: string; sort_index: number }[] =
    selfCarriersData?.data ?? [];
  const selectedSelfCarrier = useMemo(
    () => selfCarriers.find((c) => c.id === watchedSelfTransportCarrierId) ?? null,
    [selfCarriers, watchedSelfTransportCarrierId]
  );

  // ✅ แสดงข้อความหน่วย (ชิ้น / ต่อชิ้น) ตาม product_unit ที่เลือก — fallback "ชิ้น"
  const selectedProductUnitLabel = useMemo(() => {
    if (!watchedProductUnit || !productUnitData?.data?.length) {
      return "ชิ้น";
    }
    const matched = productUnitData.data.find((u) => u.uuid === watchedProductUnit);
    return matched?.description || "ชิ้น";
  }, [watchedProductUnit, productUnitData?.data]);

  // ✅ default product_unit = master value "piece" (ชิ้น) — เฉพาะ create mode, ถ้ายังไม่มีค่า
  useEffect(() => {
    if (editMode || copyMode || !productUnitData?.data?.length) return;

    const currentProductUnit = form.getFieldValue(["sku_items", name, "product_unit"]);
    if (currentProductUnit) return;

    const pieceUnit = productUnitData.data.find((u) => u.value === "piece");
    if (pieceUnit?.uuid) {
      form.setFieldValue(["sku_items", name, "product_unit"], pieceUnit.uuid);
    }
  }, [productUnitData?.data, editMode, copyMode, form, name]);

  // ✅ edit mode: restore product_unit จาก originalPrices / editData (กัน mapping หลุดตอนโหลด)
  useEffect(() => {
    if (!editMode || !editData) return;

    const itemId = editData.variant.items[0]?.id;
    const originalPrice = originalPrices.find((p) => p.variant_1_item_id === itemId);
    const productUnit = originalPrice?.product_unit ?? editData.price?.product_unit;
    if (productUnit) {
      form.setFieldValue(["sku_items", name, "product_unit"], productUnit);
    }
  }, [editMode, editData, originalPrices, form, name]);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate: replaceMerchant, isPending: isReplacingMerchant } = useReplaceDefaultMerchant(targetFactoryId || "");

  const [isMissingEmailModalOpen, setIsMissingEmailModalOpen] = useState(false);
  const [missingEmailInput, setMissingEmailInput] = useState("");

  const handleRegisterMerchant = (customEmail?: string | React.MouseEvent) => {
    const emailToUse = typeof customEmail === 'string' ? customEmail : "";
    if (!userContact?.address_fac) {
      message.error("กรุณาระบุที่อยู่โรงงานให้ครบถ้วนในเมนูตั้งค่าโรงงานก่อนลงทะเบียน");
      return;
    }

    if (!userContact.address_fac.province_id || !userContact.address_fac.district_id || !userContact.address_fac.sub_district_id || !userContact.address_fac.zip_code || !userContact.address_fac.line_1) {
      message.error("ข้อมูลที่อยู่ (บ้านเลขที่, จังหวัด, อำเภอ, ตำบล, หรือรหัสไปรษณีย์) ของโรงงานไม่ครบถ้วน กรุณาไปตั้งค่าที่อยู่ให้ครบถ้วนเสียก่อน");
      return;
    }

    if (!userContact?.telephone_number) {
      message.error("โรงงานยังไม่ได้ระบุเบอร์โทรศัพท์ กรุณาไปตั้งค่าข้อมูลให้ครบถ้วนในเมนูตั้งค่าโรงงานเสียก่อน");
      return;
    }

    const payload = {
      pickup_name: userContact?.name || "โรงงาน",
      email: emailToUse || userProfile?.user?.email || "",
      address: userContact.address_fac.line_1 || "",
      province_id: userContact.address_fac.province_id,
      district_id: userContact.address_fac.district_id,
      sub_district_id: userContact.address_fac.sub_district_id,
      postcode: String(userContact.address_fac.zip_code || ""),
      mobile: userContact?.telephone_number || "",
    };

    replaceMerchant(payload, {
      onSuccess: (res: any) => {
        if (res?.status === "failed") {
          let errorMessage = "ลงทะเบียนไม่สำเร็จ กรุณาตั้งค่าที่อยู่ให้ครบถ้วนในเมนูตั้งค่าที่อยู่รับสินค้า";
          if (res?.error_message && Array.isArray(res.error_message) && res.error_message.length > 0) {
            errorMessage = res.error_message[0];
            if (errorMessage.includes("email")) {
              setIsMissingEmailModalOpen(true);
              if (emailToUse) {
                // If they just tried to submit an email and it failed via API
                message.error("อีเมลที่ระบุไม่ถูกต้อง รูปแบบผิด หรือมีการใช้งานอื่นอยู่ กรุณาระบุอีเมลใหม่");
              }
              return;
            }
            else if (errorMessage.includes("postcode")) errorMessage = "รหัสไปรษณีย์ของโรงงานไม่ถูกต้อง";
          }
          message.error(errorMessage);
          return;
        }

        // สำเร็จ
        if (isMissingEmailModalOpen) setIsMissingEmailModalOpen(false);
        message.success("ลงทะเบียนสำเร็จ สามารถใช้งานขนส่งแบ่งเบาได้แล้ว");
        queryClient.invalidateQueries({ queryKey: ["factoryById"] });

        // Auto select 'bangbow' (ส่งกับ Partner แบ่งเบา)
        form.setFieldValue(["sku_items", name, "shipping_method"], "bangbow");
      },
      onError: (err: any) => {
        let isHandled = false;

        // ลองดึง Data error จากฝั่ง Speed API
        const speedResponse = err?.response?.data?.response;
        if (speedResponse && typeof speedResponse.error === 'string') {
          try {
            const parsedError = JSON.parse(speedResponse.error);
            if (parsedError?.message?.includes("Email already exists")) {
              setIsMissingEmailModalOpen(true);
              message.error("อีเมลนี้มีการลงทะเบียนในระบบแบ่งเบาไปแล้ว กรุณาระบุอีเมลใหม่");
              isHandled = true;
            } else if (parsedError?.message) {
              message.error(`ระบบส่งขนส่งแจ้งเตือน: ${parsedError.message}`);
              isHandled = true;
            }
          } catch (e) {
            // parse ปลอมหรือขัดข้อง ให้ข้ามไป
          }
        }

        if (!isHandled) {
          message.error("ลงทะเบียนไม่สำเร็จ กรุณาตรวจสอบข้อมูลอีกครั้ง (หรือระบบขนส่งขัดข้อง)");
        }
      },
    });
  };

  // ✅ State สำหรับเก็บข้อมูล PartnerTransportBrand จาก API Master Data
  const [partnerTransportBrands, setPartnerTransportBrands] = useState<any[]>([]);

  // ✅ ดึงข้อมูล Master Data ของแบรนด์ขนส่งมาเก็บไว้ล่วงหน้า
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await getTransportBrands();
        if (response?.data) {
          setPartnerTransportBrands(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch transport brands", error);
      }
    };
    fetchBrands();
  }, []);

  // ✅ หาข้อมูล child packaging กรณีที่ currentPackagingTypeId ถูกส่งมาในโหมด edit/copy แต่หาไม่เจอใน parent list
  const watchedPackagingTypeId = Form.useWatch(["sku_items", name, "packaging_type_id"], form);
  const watchedBrandId = Form.useWatch(["sku_items", name, "brand_id"], form);
  const currentPackagingTypeId =
    watchedPackagingTypeId ||
    form.getFieldValue(["sku_items", name, "packaging_type_id"]) ||
    form.getFieldValue("sku_items")?.[name]?.packaging_type_id;
  // ✅ ไม่ fallback ไปที่ skuItem prop อีกต่อไป เพราะเมื่อ user interact แล้ว
  // skuItem เป็นค่าเดิมจาก backend ที่อาจ stale → ทำให้ child dropdown เห็นค่าผิด

  // ✅ _parent_packaging_type_id: hidden form field ที่เก็บ parent ที่ user กำลัง browse
  // ใช้แยกออกจาก packaging_type_id เพราะตอน has_child=true, packaging_type_id จะถูก set เป็น null ไว้ก่อนจนกว่าจะเลือก child
  const watchedParentPackagingTypeId = Form.useWatch(["sku_items", name, "_parent_packaging_type_id"], form);

  // ✅ isMissingChild: true ถ้า packaging_type_id เป็น child (ไม่อยู่ใน parent list)
  const isMissingChild = useMemo(() => {
    if (!packagingTypeData?.data?.master_packaging_types || !currentPackagingTypeId) return false;
    const isDirectParent = packagingTypeData.data.master_packaging_types.some(
      (p) => p.id === currentPackagingTypeId
    );
    return !isDirectParent;
  }, [packagingTypeData, currentPackagingTypeId]);

  const { data: missingChildData } = useGetMasterPackagingTypeById(isMissingChild ? currentPackagingTypeId : null);

  // ✅ selectedParentPackagingTypeId:
  // 1. ใช้ _parent_packaging_type_id ก่อน (set โดย parent dropdown onChange — stable แม้ตอน has_child=true)
  // 2. ถ้าไม่มี → ลอง resolve จาก currentPackagingTypeId ว่าเป็น parent เอง
  // 3. ถ้า currentPackagingTypeId เป็น child → ดึง ref_id จาก missingChildData
  const selectedParentPackagingTypeId = useMemo(() => {
    // Case 1: มี parent ที่ชัดเจนจาก hidden field (user กำลัง browse)
    if (watchedParentPackagingTypeId) return watchedParentPackagingTypeId;
    // Case 2 & 3: ยังไม่มี _parent_packaging_type_id (เช่น edit/copy initial load)
    if (!packagingTypeData?.data?.master_packaging_types || !currentPackagingTypeId) return null;
    const directParent = packagingTypeData.data.master_packaging_types.find(
      (p) => p.id === currentPackagingTypeId
    );
    if (directParent) return directParent.id;
    // Case 3: เป็น child → ดึง parent จาก missingChildData
    if (isMissingChild && missingChildData?.data) {
      return (missingChildData.data as any).ref_id ?? null;
    }
    return null;
  }, [watchedParentPackagingTypeId, packagingTypeData, currentPackagingTypeId, isMissingChild, missingChildData]);

  // ✅ หา Brand ปัจจุบันที่ถูกเลือกเพื่อใช้แสดง Logo
  const currentBrandId = useMemo(() => {
    return watchedBrandId ||
      form.getFieldValue(["sku_items", name, "brand_id"]) ||
      form.getFieldValue("sku_items")?.[name]?.brand_id ||
      (skuItem?.[0] as any)?.brand_id ||
      ((factoryData?.factory?.merchants?.speed?.find((m: { is_default: boolean }) => m.is_default) as any)?.brand_id ?? null);
  }, [watchedBrandId, form, name, skuItem, factoryData]);

  // หามาสเตอร์ดาต้าของ brand นั้นๆ
  const selectedMasterBrand = useMemo(() => {
    if (!currentBrandId) return null;
    return partnerTransportBrands.find((b) => b.id === currentBrandId);
  }, [currentBrandId, partnerTransportBrands]);

  // ✅ one-shot ref: backfill packaging_type_id จาก prop เฉพาะครั้งแรก (initial load)
  // ❌ ห้ามใช้ useEffect([watchedPackagingTypeId]) เพราะจะ re-inject ค่า DB ทุกครั้งที่ user clear ฟิลด์
  const hasInitPackagingRef = useRef(false);
  const passwordInputRef = useRef<any>(null); // ✅ เพิ่ม ref สำหรับ Input.Password
  useEffect(() => {
    if (!hasInitPackagingRef.current && skuItem?.[0]?.packaging_type_id && !form.getFieldValue(["sku_items", name, "packaging_type_id"])) {
      hasInitPackagingRef.current = true;
      form.setFieldValue(["sku_items", name, "packaging_type_id"], skuItem[0].packaging_type_id);
    }
  }, [skuItem, form, name]);

  // ✅ เพิ่ม: useEffect คำนวณ unit_factory_price / unit_retail_price อัตโนมัติจาก "จำนวนต่อหน่วย"
  //    สูตร:
  //      unit_factory_price = factory_price / quantity_per_unit
  //      unit_retail_price  = retail_price  / quantity_per_unit
  //    เงื่อนไข:
  //      - ถ้า quantity_per_unit ว่าง/<=0/NaN → ไม่ override ค่าเดิม (คงค่า DB ในโหมด edit)
  //      - ใช้ round2 แล้ว toFixed(2) เพื่อรักษา format "0.00" ให้เหมือนเดิม (field เป็น string)
  useEffect(() => {
    const qty = Number(String(watchedQuantityPerUnit ?? "").replace(/,/g, ""));
    if (!qty || qty <= 0 || !Number.isFinite(qty)) return;
    const fp = toFiniteNumberOr0(watchedFactoryPriceForUnit);
    const rp = toFiniteNumberOr0(watchedRetailPriceForUnit);
    form.setFieldValue(
      ["sku_items", name, "unit_factory_price"],
      round2(fp / qty).toFixed(2)
    );
    form.setFieldValue(
      ["sku_items", name, "unit_retail_price"],
      round2(rp / qty).toFixed(2)
    );
  }, [watchedQuantityPerUnit, watchedFactoryPriceForUnit, watchedRetailPriceForUnit, form, name]);

  // ✅ Backfill _parent_packaging_type_id ลง form field เมื่อ selectedParentPackagingTypeId ถูก resolve
  // จำเป็นสำหรับ edit/copy mode ที่ packaging_type_id โหลดจาก backend แต่ _parent_packaging_type_id ไม่มีใน payload
  useEffect(() => {
    if (
      selectedParentPackagingTypeId &&
      !watchedParentPackagingTypeId &&
      !form.getFieldValue(["sku_items", name, "_parent_packaging_type_id"])
    ) {
      form.setFieldValue(["sku_items", name, "_parent_packaging_type_id"], selectedParentPackagingTypeId);
    }
  }, [selectedParentPackagingTypeId, watchedParentPackagingTypeId, form, name]);

  // ✅ Hook ดึงข้อมูล child packaging type (ขึ้นอยู่กับ selectedParentPackagingTypeId ซึ่ง derived แล้ว)
  const { data: childPackagingTypeData, isLoading: childPackagingTypeLoading } = useGetMasterPackagingType(selectedParentPackagingTypeId || undefined);

  // ✅ หา parent ที่ถูกเลือก
  const selectedParentPackaging = packagingTypeData?.data?.master_packaging_types?.find(p => p.id === selectedParentPackagingTypeId);
  const showChildDropdown = selectedParentPackaging?.has_child === true;

  /** bangbow: ยังไม่เลือกประเภทบรรจุภัณฑ์ หรือยังไม่เลือกย่อย (เมื่อ parent มีลูก) → ปิดการกรอกมิติ/น้ำหนัก */
  const packagingSelectionIncomplete = useMemo(() => {
    if (isSelfShipping) return false;
    if (!selectedParentPackagingTypeId) return true;
    if (showChildDropdown && !watchedPackagingTypeId) return true;
    return false;
  }, [isSelfShipping, selectedParentPackagingTypeId, showChildDropdown, watchedPackagingTypeId]);

  // กำหนดว่าต้องซ่อน input กว้างยาวสูงหรือไม่
  // ต้องซ่อนเมื่อเลือก has_child=true และได้เลือกตัวลูกแล้ว (ตัวลูกจะมีความกว้างยาวสูงมาให้)
  // หรือถ้า parent ไม่มีลูก (has_child=false) อาจจะมีความกว้างยาวสูงมาให้เลยไหม? (จาก requirements ให้ซ่อน "และ auto-fill จาก selected child packaging type's dimensions" ถือว่าซ่อนถ้ามี child selected อย่างเดียว)
  // ปรับให้: ถ้ามีการเลือกบรรจุภัณฑ์ (ไม่ว่าจะ parent has_child=false หรือ child) แล้วบรรจุภัณฑ์นั้นๆ มี dimensions -> ให้ disable/hide แล้ว auto-fill.
  // จาก requirement: "Dropdown ถ้าเลือก has_child=true ให้สร้าง Dropdown child ขึ้นมา และให้ซ่อน field กว้าง ยาว สูง"
  // แปลว่า ถ้าเลือกอันที่มี/เป็นขนาดมาตรฐานมาให้เลย ก็ซ่อนคู่ไปเลย หรือจะซ่อนเสมอถ้าไม่ใช่ manual?
  // ให้ flag `isManualDimension` = true ถ้าไม่ได้เลือกอะไรเลย หรือเลือก "กำหนดเอง"

  const currentPackagingTypeIdWatcher = Form.useWatch(["sku_items", name, "packaging_type_id"], form);

  // หาว่า item ที่เลือกคืออะไรเพื่อเอาค่า dimensions
  const selectedPackagingItem = useMemo(() => {
    if (!currentPackagingTypeIdWatcher) return null;
    let found;
    if (childPackagingTypeData?.data?.master_packaging_types?.length) {
      found = childPackagingTypeData?.data?.master_packaging_types?.find(c => c.id === currentPackagingTypeIdWatcher);
    } else {
      found = packagingTypeData?.data?.master_packaging_types?.find(p => p.id === currentPackagingTypeIdWatcher);
    }
    return found;
  }, [currentPackagingTypeIdWatcher, childPackagingTypeData, packagingTypeData]);

  // ซ่อนเฉพาะ ที่มี has_child=true (หมายถึงต้องเลือก child)
  // แต่ถ้า child ที่เลือกมี is_other=true ก็ไม่ต้องซ่อน
  // ซ่อนเฉพาะ กว้าง ยาว สูง น้ำหนักไม่ต้องซ่อน
  const isChildOther = selectedPackagingItem?.is_other === true;
  const hideDimensionFields = !isSelfShipping && showChildDropdown && !isChildOther;

  // ✅ State สำหรับควบคุมการแสดง field ระดับคอมมิชชั่น
  // showCommissionFields = true เมื่อกรอก password ใดก็ได้ (1111 หรือ 414428)
  // showAllCommissionFields = true เมื่อกรอก 414428 เท่านั้น (แสดง การตลาด + ผู้แนะนำร้าน ด้วย)
  const [showCommissionFields, setShowCommissionFields] = useState<boolean>(false);
  const [showAllCommissionFields, setShowAllCommissionFields] = useState<boolean>(false);
  // ✅ Task 2: เมื่อยืนยันรหัส 414428 (flow เดิม) ให้ปลดล็อกการกรอกค่าขนส่งเอง (เฉพาะส่งกับแบ่งเบา)
  // หมายเหตุ: ไม่ล้าง/ไม่แตะ api_transport_* (คงไว้ตาม requirement)
  const [manualBangbowTransportUnlocked, setManualBangbowTransportUnlocked] = useState<boolean>(false);

  // ✅ State สำหรับ inline password form
  const [showPasswordForm, setShowPasswordForm] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");

  // ✅ Task 2: ปลดล็อกนี้ใช้เฉพาะตอน shipping_method=bangbow เท่านั้น
  // ถ้าผู้ใช้เปลี่ยนเป็น self ให้ปิด unlock ทันทีเพื่อคง behavior เดิม
  useEffect(() => {
    const shippingMethod = (watchedShippingMethod ?? "self") as string;
    if (shippingMethod !== "bangbow" && manualBangbowTransportUnlocked) {
      setManualBangbowTransportUnlocked(false);
    }
  }, [watchedShippingMethod, manualBangbowTransportUnlocked]);

  // ✅ useRef สำหรับเก็บค่า total_commission และ c1-c3 จากฐานข้อมูลและค่าตอนโหลด form
  const databaseTotalCommissionRef = useRef<number | null>(null);
  const initialFormTotalCommissionRef = useRef<number | null>(null);
  const databaseC1Ref = useRef<number | null>(null);
  const databaseC2Ref = useRef<number | null>(null);
  const databaseC3Ref = useRef<number | null>(null);
  const databaseC4Ref = useRef<number | null>(null);
  const databaseC5Ref = useRef<number | null>(null);
  // ✅ create mode: ใช้คู่กับ isSameAsDb เพื่อ restore c1–c5 จาก snapshot ref เมื่อยอดรวมยังเท่าเดิมและยังไม่แก้ช่องนี้
  // ✅ edit/copy: ตั้งเป็น true ทันทีที่ handleTotalCommissionChange ถูกเรียก (ทุกครั้งที่มีการแก้ไข total_commission) → คำนวณระดับใหม่ทันที ไม่ restore จาก API
  const userAdjustedTotalCommissionAwayFromDbRef = useRef(false);
  const commissionRowKeyRef = useRef<string | null>(null);
  // ✅ edit/copy: debounce อ่านยอดรวมจากฟอร์มแล้วคำนวณแยกระดับ (กรณี float เท่าเดิมแต่ผู้ใช้พิมพ์ / NumericFormat ไม่ยิง onValueChange)
  const totalCommissionRecalcTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ✅ NOTE: เดิมเคยมี requirement ให้ "ถ้าผู้ใช้แก้ราคาท้องตลาดเองแล้ว ห้าม override"
  // แต่ requirement ล่าสุดเปลี่ยนเป็นให้ "ราคาท้องตลาดเปลี่ยนอัตโนมัติเมื่อ factory_price เปลี่ยน"
  // จึงไม่ใช้ flag นี้แล้ว (คง comment ไว้เพื่ออ้างอิงในอนาคต)

  // ✅ เก็บค่า shipping/dimensions/transport จากฐานข้อมูล (หรือค่าเริ่มต้นตอนเปิด copy) เพื่อ restore เหมือน c1-c3
  const databaseShippingMethodRef = useRef<string | null>(null);
  const databaseWidthRef = useRef<number | null>(null);
  const databaseLengthRef = useRef<number | null>(null);
  const databaseHeightRef = useRef<number | null>(null);
  const databaseWeightRef = useRef<number | null>(null);
  const databaseTransportPriceRef = useRef<number | null>(null);
  const databaseApiTransportPriceRef = useRef<number | null>(null);
  const databaseAddOnApiTransportPriceRef = useRef<number | null>(null);
  const databaseTransportCarrierCodeRef = useRef<string | null>(null);
  const databaseTransportLogoRef = useRef<string | null>(null);
  const databasePartnerIdRef = useRef<string | null>(null);
  const databaseBrandIdRef = useRef<string | null>(null);
  // ✅ snapshot บรรจุภัณฑ์สำหรับ restore เมื่อสลับ self → bangbow
  const databasePackagingTypeIdRef = useRef<string | null>(null);
  const databaseParentPackagingTypeIdRef = useRef<string | null>(null);
  const shippingRestoreInitializedRef = useRef(false);
  const shippingUserSelectedRef = useRef(false);
  const prevShippingMethodRef = useRef<string | null>(null);
  const isCodInitializedRef = useRef(false);
  // ✅ edit/copy: ยิง check-price อัตโนมัติครั้งเดียวเมื่อพร้อม
  const editAutoSearchOnceRef = useRef(false);
  // is_cod จาก DB (snapshot ตอน init) — ใช้เปรียบเทียบเพื่อ restore เมื่อ toggle กลับค่าเดิม
  const databaseIsCodRef = useRef<boolean | null>(null);
  // true เมื่อ user toggle is_cod ออกจากค่า DB → ให้ existingCarrier ไม่ใช้ databaseRef เป็น fallback
  const codUserToggledFromDbRef = useRef(false);
  // ✅ รอ auto-search หลัง toggle COD (ยิงครั้งเดียวเมื่อ canSearch พร้อม)
  const codAutoSearchPendingRef = useRef(false);

  // ✅ ตัวระบุ SKU item ของ editData ปัจจุบัน — ใช้ reset guard ด้านล่างให้ทำงาน "ครั้งเดียวต่อ 1 ชิ้น"
  //    แทนที่จะเป็น "ครั้งเดียวต่อการ mount component" เฉยๆ (ดูเหตุผลที่ effect ด้านล่าง)
  const editItemId = editData?.variant?.items?.[0]?.id ?? null;

  // เมื่อ mode เปลี่ยน (edit → copy หรือ copy → create) หรือเปลี่ยน SKU item ที่กำลังแก้ไข (กรณี SKU
  // ตัวเดียวกันมีหลายชิ้นแล้วเปิดแก้ไขชิ้นอื่นต่อโดย modal ไม่ได้ unmount จริง — editData เปลี่ยนแต่
  // name/editMode/copyMode ค่าเดิม ทำให้ guard ref พวกนี้ไม่เคย reset เลยถ้าไม่ใส่ editItemId ไว้ที่นี่
  // ด้วย เคยเจอบั๊กจริง: แก้ไขชิ้นที่ 2 แล้วข้อมูลขนส่ง/COD ของชิ้นแรกเหลือค้างอยู่ หรือถูกเข้าใจผิดว่า
  // user เพิ่ง toggle COD เอง) ให้ reset guards เพื่อไม่ให้ watchedIsCod effect ล้างข้อมูลขนส่งที่โหลดมา
  // จาก server ผิดชิ้น และให้ copy mode / shipping restore snapshot baseline ใหม่ให้ตรงกับชิ้นที่เปิดอยู่
  useEffect(() => {
    isCodInitializedRef.current = false;
    shippingRestoreInitializedRef.current = false;
    codUserToggledFromDbRef.current = false;
    codAutoSearchPendingRef.current = false;
    databaseIsCodRef.current = null;
    editAutoSearchOnceRef.current = false;
  }, [editMode, copyMode, name, editItemId]);

  // เมื่อ is_cod เปลี่ยน (COD ↔ Non-COD):
  // - toggle กลับค่า DB เดิม → restore ขนส่ง baseline (เหมือน self → bangbow ของ shipping_method)
  // - toggle ออกจากค่า DB → ล้างขนส่งและรอ search ใหม่
  // หมายเหตุ: auto re-search ย้ายไป effect ด้านล่างหลัง canSearchSpeedShipping (กันยิงตอนยังไม่พร้อม)
  useEffect(() => {
    if (!isCodInitializedRef.current) {
      isCodInitializedRef.current = true;
      // ✅ create mode: snapshot baseline จากค่าปัจจุบัน เพื่อให้ toggle ถัดไปเข้า branch ล้าง/ค้นหาได้
      //    (edit/copy จะถูกตั้งจาก shipping restore init อยู่แล้ว)
      if (!editMode && !copyMode && databaseIsCodRef.current === null) {
        databaseIsCodRef.current = !!watchedIsCod;
      }
      return;
    }

    const isBackToOriginal =
      databaseIsCodRef.current !== null &&
      watchedIsCod === databaseIsCodRef.current;

    if (isBackToOriginal) {
      // restore baseline — เหมือนที่ shipping_method: self → bangbow ทำ
      codUserToggledFromDbRef.current = false;
      const restoreCarrier = databaseTransportCarrierCodeRef.current;
      if (restoreCarrier) {
        form.setFieldValue(["sku_items", name, "transport_price"], databaseTransportPriceRef.current ?? 0);
        form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], restoreCarrier);
        form.setFieldValue(["sku_items", name, "api_transport_logo"], databaseTransportLogoRef.current);
        form.setFieldValue(["sku_items", name, "api_transport_price"], databaseApiTransportPriceRef.current ?? 0);
        form.setFieldValue(
          ["sku_items", name, "add_on_api_transport_price"],
          databaseAddOnApiTransportPriceRef.current ?? 0,
        );
        form.setFieldValue(["sku_items", name, "partner_id"], databasePartnerIdRef.current ?? "");
        form.setFieldValue(["sku_items", name, "brand_id"], databaseBrandIdRef.current ?? "");
        // ✅ normalize legacy partner code → brand code เพื่อให้การ์ดไฮไลต์ตรง
        const restoreSelectCode =
          restoreCarrier === "THAILAND_POST" ? "THAIPOST" : restoreCarrier;
        setSelectedSpeedCarrierCode(restoreSelectCode);
        // อนุญาต auto-search อีกรอบเพื่อดึงราคาล่าสุด
        editAutoSearchOnceRef.current = false;
      }
    } else if (databaseIsCodRef.current === null) {
      // baseline ยังไม่โหลด (setFieldsValue ยิงก่อน shipping restore init)
      // ถือเป็น init-time fire ไม่ใช่ user action → ไม่ set flag ไม่ล้างข้อมูล
    } else {
      // toggle ออกจากค่า DB → ล้าง (ไม่แตะ databaseRef เพื่อให้ restore ได้ภายหลัง)
      codUserToggledFromDbRef.current = true;
      setSpeedItems([]);
      setSpeedHasSearched(false);
      setSelectedSpeedCarrierCode(null);
      form.setFieldValue(["sku_items", name, "partner_id"], "");
      form.setFieldValue(["sku_items", name, "brand_id"], "");
      form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], null);
      form.setFieldValue(["sku_items", name, "api_transport_logo"], null);
      form.setFieldValue(["sku_items", name, "api_transport_price"], 0);
      form.setFieldValue(["sku_items", name, "add_on_api_transport_price"], 0);
      form.setFieldValue(["sku_items", name, "transport_price"], 0);
      editAutoSearchOnceRef.current = false;
      // ✅ ตั้ง pending — effect ด้านล่างจะยิง search เมื่อ canSearch พร้อม
      if (watchedIsCod) {
        codAutoSearchPendingRef.current = true;
      }
    }
  }, [watchedIsCod, editMode, copyMode, form, name]);

  // ✅ ดึง sell_through_line และ payment_gateway_fee จาก API response
  const sellThroughLine = factoryData?.factory?.sell_through_line ?? false;
  // console.log("sellThroughLine", sellThroughLine);
  const paymentGatewayFee = factoryData?.factory?.payment_gateway_fee ?? null;
  // console.log("paymentGatewayFee", paymentGatewayFee)
  // ✅ คำนวณ payment gateway percent จาก payment_gateway_fee (ถ้าเป็น null ใช้ 0.03 เป็น default)
  const paymentGatewayPercent = paymentGatewayFee !== null && paymentGatewayFee !== undefined
    ? paymentGatewayFee / 100
    : 0.03; // Default 3% ถ้าไม่มีค่า

  // ✅ เมื่อ paymentGatewayPercent เปลี่ยนแปลง (เช่น โหลดข้อมูล Factory เสร็จ) ให้คำนวณราคาสุดท้ายใหม่
  // ✅ ส่ง overrideRetailPrice=false → ไม่ให้ override retail_price ที่โหลดจาก DB ตอน initial mount
  useEffect(() => {
    const formValues = form.getFieldValue(["sku_items", name]);
    if (formValues) {
      calculateFinalPrices(formValues, false);
    }
  }, [paymentGatewayPercent, sellThroughLine]);


  // ✅ Guard กลาง: กันพิมพ์ตัวอักษรใน InputNumber (ยกเว้นราคาต่อหน่วย)
  // - transport_price: integer
  // - ช่องอื่น ๆ ที่เป็นเงิน/ทศนิยม: decimal 2 ตำแหน่ง
  const integerGuards = createInputNumberGuards({ mode: "integer" });
  const decimal2Guards = createInputNumberGuards({ mode: "decimal2" });

  // ✅ formatWithCommas, formatWithCommasFixed2, parseNumberWithoutCommas
  // ย้ายไปอยู่ใน helpers.ts แล้ว (bundle-barrel-imports / rerender-memo best practice)

  // ✅ UI requirement:
  // 1) ปรับสี label commission_lv ให้เป็นสีเทาเข้ม
  // 2) ให้ label ของ commission_lv1-4 เริ่มแสดงในแนวเดียวกับ input ของ total_commission
  const commissionLvLabelStyle: React.CSSProperties = {
    color: "#595959", // เทาเข้ม
  };

  // ✅ UI requirement: ให้ input commission_lv1 - 4 ติดกับ label เลย
  const commissionLvInputStyle: React.CSSProperties = {
    width: 160,
    display: "inline-block",
    padding: "4px 8px",
    textAlign: "right",
  };

  // ✅ UI requirement: ให้ input commission_lv เริ่มที่แนวเดียวกันทุกแถว
  // หมายเหตุ: กำหนดความกว้าง label คงที่ เพื่อให้จุดเริ่ม input ไม่เลื่อนตามความยาวข้อความ
  const commissionLvLabelBoxStyle: React.CSSProperties = {
    flex: "0 0 170px",
    whiteSpace: "nowrap",
  };

  /**
   * ✅ Create mode: ให้ค่าขนส่งเริ่มต้นเป็น 0 (และกรอกไม่ได้ทุกกรณีตาม requirement ล่าสุด)
   * - self: ล็อก (disabled) และคงค่า 0
   * - bangbow: แสดงค่าได้ แต่กรอกไม่ได้ (readOnly) โดยถ้ายังไม่มีค่าให้ set เป็น 0 ก่อน
   *
   * หมายเหตุ: editMode/copyMode ยังคง "แสดงค่าแต่ห้ามแก้" เช่นเดิม (readOnly)
   */
  useEffect(() => {
    if (editMode || copyMode) return;

    const current = (watchedShippingMethod ?? "self") as string;
    const currentTransport = form.getFieldValue(["sku_items", name, "transport_price"]);

    if (current === "self") {
      if (currentTransport !== 0) {
        form.setFieldValue(["sku_items", name, "transport_price"], 0);
      }
      return;
    }

    if (current === "bangbow") {
      if (currentTransport === undefined || currentTransport === null) {
        form.setFieldValue(["sku_items", name, "transport_price"], 0);
      }
    }
  }, [watchedShippingMethod, editMode, copyMode, form, name]);

  /**
   * State สำหรับควบคุมการเปิด-ปิดฟิลด์แบบขั้นตอน (Progressive Form)
   *
   * Flow การทำงาน:
   * 1. เริ่มต้น: เปิดเฉพาะ ราคาตั้งต้น (4 ฟิลด์)
   * 2. กรอกราคาตั้งต้นครบ → เปิด ขนาด & น้ำหนัก (4 ฟิลด์)
   * 3. กรอกขนาดและน้ำหนักครบ → เปิด ค่าขนส่ง (1 ฟิลด์)
   * 4. กรอกค่าขนส่งเสร็จ → เปิด ราคาสุดท้าย (4 ฟิลด์ คำนวณอัตโนมัติ)
   *
   * หมายเหตุ: ในโหมด editMode หรือ copyMode จะเปิดทุกฟิลด์ทันที
   *
   * เริ่มต้นที่ false ทั้งหมด แล้วให้ useEffect จัดการตาม mode ที่ถูกต้อง
   */
  const [enableDimensions, setEnableDimensions] = useState(false); // ควบคุมฟิลด์ขนาด & น้ำหนัก
  const [enableTransport, setEnableTransport] = useState(false); // ควบคุมฟิลด์ค่าขนส่ง
  const [enableFinalPrices, setEnableFinalPrices] = useState(false); // ควบคุมฟิลด์ราคาสุดท้าย

  // ✅ Speed Shipping recommendation (ยิง check-price 2 รอบ north/south แล้ว merge/sort)
  const [speedLoading, setSpeedLoading] = useState(false);
  const [speedError, setSpeedError] = useState<string | null>(null);
  // ✅ Task T1: ถ้า speed shipping timeout → ไม่ lock มิติ + ไม่ block การบันทึก
  const [speedTimeout, setSpeedTimeout] = useState(false);
  const [speedNotice, setSpeedNotice] = useState<string | null>(null);
  const [speedStaleHint, setSpeedStaleHint] = useState<string | null>(null); // ✅ G1: เปลี่ยนมิติแล้วให้กดค้นหาใหม่

  const [speedItems, setSpeedItems] = useState<
    Array<{
      carrier: string;
      carrier_code: string;
      logo?: string;
      delivery_time?: string;
      total: number;
      /** ชื่อตรง DB — ใช้เป็นหลักหลัง refactor add-on */
      api_transport_price?: number;
      add_on_api_transport_price?: number;
      transport_price?: number;
      /** alias เดิม */
      add_on_price?: number;
      total_with_add_on?: number;
      raw: CheckPriceCarrierItem;
      name?: string;
      minimum_time?: number;
      maximum_time?: number;
      is_pickup?: number;
    }>
  >([]);
  const [selectedSpeedCarrierCode, setSelectedSpeedCarrierCode] = useState<string | null>(null);
  const [controlSpeedNotice, setControlSpeedNotice] = useState(false);
  const [speedSearchTrigger, setSpeedSearchTrigger] = useState(0); // ✅ ยิง check-price หลังคลิกปุ่ม "ค้นหา"
  const lastProcessedSpeedSearchTriggerRef = useRef(0);
  const lastSearchedDimsRef = useRef<{ w: number; l: number; h: number; wt: number } | null>(null);
  // ✅ [requirement ใหม่ 2026-10-15] true เฉพาะตอน user กดปุ่ม "ตรวจสอบราคาและขนส่ง" เองตรงๆ —
  //    ไม่ตั้งค่านี้ตอน auto-search ตอนเปิดหน้า edit mode (editAutoSearchOnceRef) เพื่อให้ auto-search
  //    ยังคง auto-match/auto-fill carrier ที่เคยบันทึกไว้เหมือนเดิม ส่วนกดปุ่มเองจะเคลียร์ค่าขนส่งเดิม
  //    (ราคา+แบรนด์) ทิ้งแล้วบังคับให้เลือกใหม่จากรายการเสมอ — อ่านแล้ว reset เป็น false ทันทีในเอฟเฟกต์
  //    เดียวกัน ให้มีผลแค่ trigger รอบนั้นรอบเดียว
  const manualSearchTriggeredRef = useRef(false);
  // ✅ [requirement 2026-10-15] true ตั้งแต่กดปุ่ม "ตรวจสอบราคาและขนส่ง" เอง จนกว่าจะเลือกขนส่งใหม่จริง ๆ
  //    กัน effect rehydrate (บรรทัดด้านล่าง) ดึง carrier code เดิมจาก databaseTransportCarrierCodeRef
  //    กลับมาใส่ selectedSpeedCarrierCode ทันทีหลังเคลียร์ ทำให้ logo/ขนส่งเดิมโผล่กลับมา
  const manualTransportClearedRef = useRef(false);

  const selectedSpeedItem = useMemo(() => {
    if (!selectedSpeedCarrierCode) return null;
    // ✅ match ทั้ง brand code ใหม่ (THAIPOST) และ legacy partner code (THAILAND_POST)
    return (
      speedItems.find((x) => x.carrier_code === selectedSpeedCarrierCode) ??
      (selectedSpeedCarrierCode === "THAILAND_POST"
        ? speedItems.find((x) => x.carrier_code === "THAIPOST" || x.name === "ไปรษณีย์ไทย")
        : undefined) ??
      null
    );
  }, [selectedSpeedCarrierCode, speedItems]);

  // ✅ ย้าย side-effect ออกจาก useMemo (เดิม setSpeedNotice ใน selectedSpeedItem)
  useEffect(() => {
    if (selectedSpeedCarrierCode) {
      setSpeedNotice(null);
    } else if (controlSpeedNotice) {
      setSpeedNotice(
        "SKU นี้ยังไม่ได้ผูกค่าขนส่งกับระบบ (สามารถเลือกค่าขนส่งจากรายการด้านล่าง)",
      );
    } else {
      setSpeedNotice(null);
    }
  }, [selectedSpeedCarrierCode, controlSpeedNotice]);

  // ✅ formatDeliveryTimeTh, normalizeSpeedLogo
  // ย้ายไปอยู่ใน helpers.ts แล้ว

  // ✅ เงื่อนไขการแสดงผล Logo 
  // ⚠️ ใช้ watchedApiTransportLogo (Form.useWatch) แทน form.getFieldValue เพื่อให้ useMemo
  //    recompute อัตโนมัติเมื่อผู้ใช้คลิกเลือกขนส่งแล้ว setFieldValue ("api_transport_logo")
  //    (ก่อนหน้านี้ไม่ได้ใส่ใน deps ทำให้ logo ไม่อัปเดตหลังเลือก)
  const displayTransportLogo = useMemo(() => {
    // 0. ✅ [requirement 2026-10-15] ไม่ใช่ส่งเอง และยังไม่ได้เลือกขนส่งใด ๆ อยู่
    //    (เช่น เพิ่งกดปุ่ม "ตรวจสอบราคาและขนส่ง" เองแล้วถูกเคลียร์ค่าขนส่งเดิมทิ้ง)
    //    → ไม่แสดง logo เดิมค้างไว้ แม้ api_transport_logo ใน form จะยัง sync ไม่ทัน
    if (!isSelfShipping && !selectedSpeedCarrierCode) {
      return null;
    }

    // 1. ถ้ามี logo จาก API speed shipping (โหลดมาจากการค้นหาราคา)
    // ✅ อ่านจาก watchedApiTransportLogo เพื่อให้ reactive (form.useWatch) แทน getFieldValue ที่ไม่ reactive
    const apiLogo = watchedApiTransportLogo;
    if (apiLogo) return apiLogo;

    // 2. ถ้าเป็น "ส่งเอง" (Self Shipping) -> บังคับแสดงโลโก้จริงจาก master_data เสมอ (ถ้ามี)
    if (isSelfShipping) {
      if (selectedMasterBrand?.brand_logo) return selectedMasterBrand.brand_logo;
      // ถ้าไม่มีจริงๆ ให้ fallback กลับไปหากล่อง
      return null;
    }

    // 3. ❌ เดิม: create mode ก่อนค้นหา โชว์ logo master เหมือนเลือกแล้ว ทั้งที่ยังไม่ persist
    //    → ไม่โชว์จนกว่าจะมี api_transport_carrier_code / selectedSpeedItem
    // if (!editMode && !copyMode && !speedHasSearched && selectedMasterBrand?.brand_logo) {
    //   return selectedMasterBrand.brand_logo;
    // }

    // 4. ✅ Fallback: ถ้าผู้ใช้ "เลือกขนส่งจากรายการ speed shipping แล้ว" ให้ใช้ logo จาก selectedSpeedItem
    //    (เผื่อกรณีที่ api_transport_logo ยังไม่ได้ sync ทันใน state ของ form)
    if (!isSelfShipping && selectedSpeedItem?.logo) {
      return selectedSpeedItem.logo;
    }

    // 5. Fallback โหมดอื่นๆ (Edit/Copy Bangbow) ที่ไม่มี logo ใน API 
    // หรือพยายามดึงจาก master แต่ไม่มี -> คืนค่ารูปกล่อง 
    return null;
  }, [
    // ✅ เพิ่ม watchedApiTransportLogo + selectedSpeedItem เป็น deps เพื่อให้ logo อัปเดตทันทีเมื่อเลือกขนส่ง
    watchedApiTransportLogo,
    isSelfShipping,
    selectedMasterBrand,
    // editMode / copyMode / speedHasSearched — ไม่ใช้ใน display อีกแล้ว (คง comment ข้อ 3 ไว้เป็นประวัติ)
    selectedSpeedItem,
    // ✅ [requirement 2026-10-15] gate ข้อ 0 ด้านบน
    selectedSpeedCarrierCode,
  ]);

  // ✅ เงื่อนไขการแสดงชื่อขนส่ง
  const displayTransportName = useMemo(() => {
    // 1. ถ้ามีชื่อจาก selected speed item (มาจากการค้นหา bangbow)
    if (!isSelfShipping && selectedSpeedItem?.name) return selectedSpeedItem.name;

    // 2. ถ้าเป็น "ส่งเอง" หรือ "สร้างใหม่เพิ่งเปิด" ให้ดึงชื่อจริงจาก master 
    if (isSelfShipping || (!editMode && !copyMode && !speedHasSearched)) {
      if (selectedMasterBrand?.brand_name) return selectedMasterBrand.brand_name;
    }

    // 3. Fallback
    return " ";
  }, [isSelfShipping, selectedSpeedItem, editMode, copyMode, speedHasSearched, selectedMasterBrand]);

  // ✅ TransportLogo ย้ายไปเป็น top-level component ใน TransportLogo.tsx แล้ว
  // ตาม Vercel best practice: rerender-no-inline-components — ห้าม define component ภายใน component อื่น


  /**
   * ฟังก์ชันสำหรับคำนวณราคาสุดท้ายอัตโนมัติ
   * สูตร: (ราคาตั้งต้น + ค่าขนส่ง + คอมมิชชั่น) + [(ราคาตั้งต้น + ค่าขนส่ง + คอมมิชชั่น) × Payment Gateway]
   *
   * @param formValues - ค่าจาก form ที่ต้องใช้ในการคำนวณ
   * @param overrideRetailPrice - ถ้า true → override retail_price = factory_price * 1.05 เสมอ (default true)
   *                               ใช้ false เฉพาะตอน initial mount/restore เพื่อคงค่า retail_price จาก DB
   *
   * ตัวอย่าง:
   * - ราคาตั้งต้น (A) = 1,000 บาท
   * - ค่าขนส่ง (S) = 50 บาท
   * - คอมมิชชั่น (C) = total_commission
   * - Payment Gateway (G) = ใช้ค่าจากระบบ (payment_gateway_fee) หรือ default 3%
   *
   * การคำนวณ (สูตรใหม่ 2026-08-20 — "gross แล้วหัก"):
   * 1. grossSum = A + S + C            ← รวมค่าขนส่ง "เสมอ" ทั้ง 2 โหมด
   * 2. gateway  = grossSum × G         (default G = 3%)
   * 3. gross    = Math.ceil(grossSum + gateway)   → ปัดขึ้นเป็นจำนวนเต็ม
   * 4. ส่งฟรี       → factory_price = gross
   *    แยกค่าขนส่ง → factory_price = gross − S   (ค่าขนส่งไปแสดงแยกที่หน้าแอพ)
   *
   * ตัวอย่าง A=1,000 / S=50 / C=100 / G=3%:
   * - ส่งฟรี       → ceil(1,150 × 1.03) = 1,185
   * - แยกค่าขนส่ง → 1,185 − 50        = 1,135  (ลูกค้าจ่ายรวม 1,185 เท่ากันทั้ง 2 โหมด)
   *
   * สูตรเก่า (เก็บไว้เป็นประวัติ ห้ามลบ):
   * - แยกค่าขนส่ง เคยตัด S ออกจากฐานก่อนคิด gateway → ceil((A + 0 + C) × (1+G)) = 1,133
   *   ทำให้ gateway ของค่าขนส่งหายไป และราคารวม 2 โหมดไม่เท่ากัน
   */
  const calculateFinalPrices = (formValues: any, overrideRetailPrice: boolean = true) => {
    // ดึงค่าราคาตั้งต้น ค่าขนส่ง และคอมมิชชั่นจาก form
    const baseFactoryPrice = toFiniteNumberOr0(formValues.base_factory_price);
    const baseVipPrice = toFiniteNumberOr0(formValues.base_vip_price);
    const baseWholesalePrice = toFiniteNumberOr0(formValues.base_wholesale_price);
    // ✅ ไม่ลบค่า transport_price แต่ถ้า shipping_method = self ให้ "ไม่นำ S ไปคำนวณ"
    const shippingMethod = (formValues.shipping_method ?? watchedShippingMethod ?? "self") as string;
    const rawTransportPrice = toFiniteNumberOr0(formValues.transport_price);
    // include_transport_price: true = ส่งฟรี (รวมใน factory_price), false = แยกค่าขนส่ง
    const includeTransportInPrice = formValues.include_transport_price !== false;
    // ✅ S (ค่าขนส่ง) — รวมเข้าฐานคำนวณ "เสมอ" ทั้ง 2 โหมด
    //    transport_price (ขนส่งผ่านระบบ) กับ self_transport_price (ส่งเอง) ไม่มีทางมีค่าพร้อมกัน
    //    เพราะถูกแยกด้วย shipping_method → บวกรวมกันได้ปลอดภัย
    const transportPriceForGateway = shippingMethod === "self" ? 0 : rawTransportPrice;
    const selfTransportPrice = shippingMethod === "self" ? toFiniteNumberOr0(formValues.self_transport_price) : 0;
    const totalTransport = transportPriceForGateway + selfTransportPrice;
    // ✅ ใช้ total_commission เท่านั้น
    const totalCommission = toFiniteNumberOr0(formValues.total_commission);
    const commissionSum = totalCommission;
    // ✅ ใช้ paymentGatewayPercent จาก Redux แทน hardcode

    /**
     * ✅ สูตรใหม่ (2026-08-20) "gross แล้วหัก" — คิด payment gateway บนยอดรวมค่าขนส่งเสมอ
     *   gross        = Math.ceil((A + S + C) × (1 + G))
     *   ส่งฟรี       → factory_price = gross
     *   แยกค่าขนส่ง → factory_price = gross − S   (ค่าขนส่งไปแสดงแยกที่หน้าแอพ)
     *   ⇒ ลูกค้าจ่ายรวมเท่ากันทั้ง 2 โหมด และผลต่าง 2 โหมด = ค่าขนส่งพอดี
     *
     * สูตรเก่า (เก็บไว้เป็นประวัติ ห้ามลบ) — แยกค่าขนส่งเคยตัด S ออกจากฐาน "ก่อน" คิด gateway
     * ทำให้ gateway ของค่าขนส่งหายไป (A=1,000/S=50/C=100/G=3% ได้ 1,133 แทน 1,135):
     *   const transportPrice = shippingMethod === "self" ? 0 : (includeTransportInPrice ? rawTransportPrice : 0);
     *   const effectiveBaseFactoryPrice = baseFactoryPrice + (includeTransportInPrice ? selfTransportPrice : 0);
     *   factory_price = Math.ceil((effectiveBaseFactoryPrice + transportPrice + C) × (1 + G))
     */
    const grossSum = baseFactoryPrice + totalTransport + commissionSum; // (A + S + C) — รวมค่าขนส่งเสมอ

    // payment_gateway = (A + S + C) × G — คิดบน transaction จริงรวม transport เสมอ (Option B)
    const paymentGateway = Math.round(grossSum * paymentGatewayPercent * 100) / 100; // ✅ แสดง 2 ตำแหน่ง
    form.setFieldValue(
      ["sku_items", name, "payment_getway"],
      paymentGateway
    ); // บันทึกค่า Payment Gateway (ทศนิยม)

    // ✅ คำนวณและอัปเดตราคาสุดท้าย
    //    หักค่าขนส่ง "หลัง" ปัดขึ้น เพื่อให้ (ราคาแยกค่าขนส่ง + ค่าขนส่ง) = ราคาส่งฟรี พอดี
    //    Math.max(0, …) กันกรณีค่าขนส่งสูงกว่าราคาสินค้า → validation "ราคาขายต้องมากกว่า 0" จะเตะต่อเอง
    const grossFinalPrice = Math.ceil(grossSum + grossSum * paymentGatewayPercent);
    const calculatedFactoryPrice = Math.max(
      0,
      includeTransportInPrice ? grossFinalPrice : grossFinalPrice - totalTransport
    );
    form.setFieldValue(
      ["sku_items", name, "factory_price"],
      calculatedFactoryPrice
    ); // ราคาตัวแทน - ใช้สูตรคำนวณ (แสดงเสมอ)

    // ✅ auto-sync ราคาตลาดที่แสดง (retail_price) เมื่อ factory_price ถูกคำนวณใหม่
    // เงื่อนไข:
    //   - ถ้า input_market_price < factory_price → retail_price = Math.ceil(factory_price * 1.05) (บวก 5%)
    //   - ถ้า input_market_price >= factory_price → retail_price = input_market_price (ใช้ค่าที่ผู้ใช้กรอก)
    // retail_price (ด้านล่าง) แยกจาก input_market_price (ด้านบน) — ไม่ sync กลับขึ้นด้านบน
    // หมายเหตุ: ผู้ใช้ยังแก้ retail_price เองได้จากช่องด้านล่าง (ผ่าน onChange) จนกว่า factory_price จะถูกคำนวณใหม่ครั้งถัดไป
    // ✅ ถ้า overrideRetailPrice = false (initial mount/restore) → ไม่ override ให้คงค่า retail_price จาก DB
    if (overrideRetailPrice && calculatedFactoryPrice > 0) {
      const inputMarketPrice = toFiniteNumberOr0(formValues.input_market_price);
      if (inputMarketPrice < calculatedFactoryPrice) {
        const corrected = Math.ceil(calculatedFactoryPrice * 1.05);
        form.setFieldValue(["sku_items", name, "retail_price"], corrected);
      } else {
        form.setFieldValue(["sku_items", name, "retail_price"], inputMarketPrice);
      }
    }

    // ✅ ราคาท้องตลาด (retail_price): ให้ผู้ใช้กรอกเอง (ไม่คำนวณอัตโนมัติ)

    // ✅ แสดงเฉพาะเมื่อ sell_through_line = true
    if (sellThroughLine) {
      // ✅ ตั้งค่าเฉพาะเมื่อ base_vip_price และ base_wholesale_price มีค่า (ไม่ใช่ 0)
      // ✅ ถ้า base_vip_price และ base_wholesale_price เป็น 0 (ถูกซ่อน) ไม่ต้อง set ค่า (ให้คงค่าที่ผู้ใช้กรอกไว้)
      if (baseVipPrice > 0) {
        form.setFieldValue(
          ["sku_items", name, "vip_price"],
          baseVipPrice
        ); // ราคา VIP - ใช้ข้อมูลจากราคาตั้งต้นโดยตรง (ไม่คำนวณ)
      }
      // ✅ ถ้า base_vip_price เป็น 0 (ถูกซ่อน) ไม่ต้อง set ค่า (ให้คงค่าที่ผู้ใช้กรอกไว้)

      if (baseWholesalePrice > 0) {
        form.setFieldValue(
          ["sku_items", name, "wholesale_price"],
          baseWholesalePrice
        ); // ราคาที่ขายใน Line - ใช้ข้อมูลจากราคาตั้งต้นโดยตรง
      }
      // ✅ ถ้า base_wholesale_price เป็น 0 (ถูกซ่อน) ไม่ต้อง set ค่า (ให้คงค่าที่ผู้ใช้กรอกไว้)
    } else {
      // ✅ ถ้า sell_through_line = false ให้ set เป็น null หรือ 0
      form.setFieldValue(["sku_items", name, "vip_price"], null);
      form.setFieldValue(["sku_items", name, "wholesale_price"], null);
    }
  };



  useEffect(() => {
    if (variants) {
      let newImageName: string[] = [];
      variants.forEach((item) => {
        item.items.forEach((item) => {
          newImageName.push(item.name);
        });
      });
      setImageName(newImageName);
    }
  }, [variants]);

  // ✅ เปลี่ยนแถว SKU / โหมด → รีเซ็ต flag (ยังไม่เรียก handleTotalCommissionChange ของแถวนี้ → edit/copy ยังแสดง c1–c5 จากโหลดฟอร์ม/API)
  useEffect(() => {
    let nextKey: string;
    if (editMode && editData?.variant?.items?.[0]?.id) {
      nextKey = `e:${editData.variant.items[0].id}`;
    } else if (copyMode) {
      nextKey = `c:${String(name)}`;
    } else {
      nextKey = `n:${String(name)}`;
    }
    if (commissionRowKeyRef.current !== nextKey) {
      if (totalCommissionRecalcTimerRef.current) {
        clearTimeout(totalCommissionRecalcTimerRef.current);
        totalCommissionRecalcTimerRef.current = null;
      }
      commissionRowKeyRef.current = nextKey;
      userAdjustedTotalCommissionAwayFromDbRef.current = false;
    }
  }, [editMode, copyMode, name, editData?.variant?.items?.[0]?.id]);

  useEffect(() => {
    return () => {
      if (totalCommissionRecalcTimerRef.current) {
        clearTimeout(totalCommissionRecalcTimerRef.current);
        totalCommissionRecalcTimerRef.current = null;
      }
    };
  }, []);

  /**
   * useEffect สำหรับตั้งค่าเริ่มต้นเมื่อเป็น editMode หรือ copyMode
   *
   * ในโหมดแก้ไข (Edit) หรือคัดลอก (Copy) ต้องเปิดทุกฟิลด์ให้แก้ไขได้ทันที
   * เพราะข้อมูลมีอยู่แล้วครบถ้วน ไม่ต้องกรอกเป็นขั้นตอน
   *
   * ในโหมดสร้างใหม่ (ไม่ใช่ edit/copy) ต้อง reset ให้กลับสู่เงื่อนไข disable แบบเดิม
   */
  useEffect(() => {
    if (editMode || copyMode) {
      // โหมดแก้ไข/คัดลอก: เปิดทุกฟิลด์ทันที
      setEnableDimensions(true); // เปิดฟิลด์ขนาด & น้ำหนัก
      setEnableTransport(true); // เปิดฟิลด์ค่าขนส่ง
      setEnableFinalPrices(true); // เปิดฟิลด์ราคาสุดท้าย

      // ✅ สำหรับ copyMode: คำนวณราคาสุดท้ายเฉพาะตัวแทนและ VIP เท่านั้น
      if (copyMode) {
        // เพิ่ม timeout และตรวจสอบข้อมูลหลายครั้งเพื่อให้แน่ใจว่าได้ข้อมูลครบ
        const checkAndCalculate = (attempts = 0) => {
          setTimeout(() => {
            const formValues = form.getFieldValue(["sku_items", name]);
            // ไม่ใช้ log debug

            if (formValues) {
              // ตรวจสอบว่ามีข้อมูลครบสำหรับการคำนวณหรือไม่
              // ใน copyMode ตรวจสอบเฉพาะว่ามีข้อมูลราคาสุดท้ายหรือไม่
              const hasRequiredData =
                (formValues.factory_price !== undefined &&
                  formValues.factory_price > 0) ||
                (formValues.vip_price !== undefined &&
                  formValues.vip_price > 0) ||
                (formValues.wholesale_price !== undefined &&
                  formValues.wholesale_price > 0) ||
                (formValues.retail_price !== undefined &&
                  formValues.retail_price > 0);

              // ไม่ใช้ log debug

              // ถ้ามีข้อมูลราคา ให้คำนวณราคาสุดท้ายเฉพาะตัวแทนและ VIP
              // ✅ ส่ง overrideRetailPrice=false → ไม่ให้ override retail_price ที่ copy มาจาก DB
              if (hasRequiredData) {
                calculateFinalPrices(formValues, false);
              } else if (attempts < 5) {
                // ถ้ายังไม่มีข้อมูลครบ และยังไม่เกิน 5 ครั้ง ให้ลองใหม่
                checkAndCalculate(attempts + 1);
              }
            } else if (attempts < 5) {
              // ถ้ายังไม่มี formValues และยังไม่เกิน 5 ครั้ง ให้ลองใหม่
              checkAndCalculate(attempts + 1);
            }
          }, 200 + attempts * 100); // เพิ่ม delay แต่ละครั้ง
        };

        checkAndCalculate();
      }

      // ✅ สำหรับ editMode: แก้ไขราคาตั้งต้นที่เป็น 0 และจัดการคอมมิชชั่น
      if (editMode && editData) {
        // ✅ หาค่าจากฐานข้อมูลจริงๆ จาก originalPrices (ไม่ใช่จาก editData.price ที่อาจถูกอัพเดทแล้ว)
        const itemId = editData.variant.items[0]?.id;
        const originalPrice = originalPrices.find(p => p.variant_1_item_id === itemId);

        // ✅ ใช้ค่าจากฐานข้อมูลจริงๆ (originalPrices) เพื่อเก็บไว้ใน ref สำหรับเปรียบเทียบ
        const dbTotalCommission = originalPrice?.total_commission ?? editData.price?.total_commission ?? 0;
        const dbC1 = originalPrice?.commistion_lv1 ?? editData.price?.commistion_lv1 ?? 0;
        const dbC2 = originalPrice?.commistion_lv2 ?? editData.price?.commistion_lv2 ?? 0;
        const dbC3 = originalPrice?.commistion_lv3 ?? editData.price?.commistion_lv3 ?? 0;
        const dbC4 = originalPrice?.commistion_lv4 ?? editData.price?.commistion_lv4 ?? 0;
        const dbC5 = originalPrice?.commistion_lv5 ?? editData.price?.commistion_lv5 ?? 0;

        // ✅ เก็บค่า total_commission และ c1-c5 จากฐานข้อมูลไว้ใน ref (ใช้สำหรับเปรียบเทียบ)
        databaseTotalCommissionRef.current = dbTotalCommission;
        initialFormTotalCommissionRef.current = dbTotalCommission;
        databaseC1Ref.current = dbC1;
        databaseC2Ref.current = dbC2;
        databaseC3Ref.current = dbC3;
        databaseC4Ref.current = dbC4;
        databaseC5Ref.current = dbC5;

        // ✅ ใช้ค่าจาก editData.price.total_commission (ค่าที่อัพเดทแล้ว) เพื่อตั้งค่า form field
        const currentTotalCommission = editData.price?.total_commission ?? 0;

        // ✅ ตั้งค่า form field สำหรับ total_commission
        // ใช้ timeout หลายครั้งเพื่อให้แน่ใจว่า form โหลดข้อมูลเสร็จแล้ว
        setTimeout(() => {
          form.setFieldValue(
            ["sku_items", name, "total_commission"],
            currentTotalCommission
          );
        }, 200);
        setTimeout(() => {
          form.setFieldValue(
            ["sku_items", name, "total_commission"],
            currentTotalCommission
          );
        }, 400);
      } else if (copyMode) {
        // ✅ สำหรับ copyMode: อ่านค่า total_commission จาก form values ที่ถูกโหลดแล้ว
        setTimeout(() => {
          const formValues = form.getFieldValue(["sku_items", name]);
          if (formValues) {
            // ✅ อ่านค่า total_commission จาก form values (ถูกโหลดจาก currentSkuData แล้ว)
            const copiedTotalCommission = formValues.total_commission || 0;
            databaseTotalCommissionRef.current = copiedTotalCommission;
            initialFormTotalCommissionRef.current = copiedTotalCommission;
            // ✅ snapshot c1-c5 จากข้อมูลที่คัดลอกมา (ใช้สำหรับ restore เมื่อ total_commission กลับมาเท่าเดิม)
            databaseC1Ref.current = formValues.commistion_lv1 ?? 0;
            databaseC2Ref.current = formValues.commistion_lv2 ?? 0;
            databaseC3Ref.current = formValues.commistion_lv3 ?? 0;
            databaseC4Ref.current = formValues.commistion_lv4 ?? 0;
            databaseC5Ref.current = formValues.commistion_lv5 ?? 0;

            // ตรวจสอบและแก้ไขราคาตั้งต้นที่เป็น 0
            if (
              (formValues.base_factory_price === 0 ||
                !formValues.base_factory_price) &&
              formValues.factory_price > 0
            ) {
              form.setFieldValue(
                ["sku_items", name, "base_factory_price"],
                formValues.factory_price
              );
            }
            if (
              (formValues.base_vip_price === 0 || !formValues.base_vip_price) &&
              formValues.vip_price > 0
            ) {
              form.setFieldValue(
                ["sku_items", name, "base_vip_price"],
                formValues.vip_price
              );
            }
            if (
              (formValues.base_wholesale_price === 0 ||
                !formValues.base_wholesale_price) &&
              formValues.wholesale_price > 0
            ) {
              form.setFieldValue(
                ["sku_items", name, "base_wholesale_price"],
                formValues.wholesale_price
              );
            }
            // ✅ ราคาท้องตลาด (retail_price) - ไม่ต้องตั้งค่า base_retail_price จาก retail_price

            // ✅ สำหรับ copyMode: ซ่อน field ระดับคอมมิชชั่นและตั้งค่า total_commission
            setShowCommissionFields(false);
            // ✅ ตั้งค่า total_commission จากข้อมูลที่คัดลอก (อ่านจาก form values)
            if (copiedTotalCommission > 0) {
              form.setFieldValue(
                ["sku_items", name, "total_commission"],
                copiedTotalCommission
              );
            }

            // ไม่ต้องคำนวณราคาสุดท้ายใหม่ - เพียงแค่อัปเดตค่าใน form
          }
        }, 200); // รอให้ form โหลดข้อมูลเสร็จ

        // ✅ ตั้งค่า total_commission อีกครั้งเพื่อให้แน่ใจว่าไม่ถูก override
        setTimeout(() => {
          const formValues = form.getFieldValue(["sku_items", name]);
          if (formValues) {
            const copiedTotalCommission = formValues.total_commission || 0;
            if (copiedTotalCommission > 0) {
              form.setFieldValue(
                ["sku_items", name, "total_commission"],
                copiedTotalCommission
              );
            }
          }
        }, 400);
      } else {
        // ✅ สำหรับ newMode: ตั้งค่า total_commission = 0
        databaseTotalCommissionRef.current = 0;
        initialFormTotalCommissionRef.current = 0;
        databaseC1Ref.current = 0;
        databaseC2Ref.current = 0;
        databaseC3Ref.current = 0;
        databaseC4Ref.current = 0;
        databaseC5Ref.current = 0;
      }
    } else {
      // โหมดสร้างใหม่: reset ให้กลับสู่เงื่อนไขเริ่มต้น (ปิดทุกอย่างยกเว้นราคาตั้งต้น)
      setEnableDimensions(false); // ปิดฟิลด์ขนาด & น้ำหนัก
      setEnableTransport(false); // ปิดฟิลด์ค่าขนส่ง
      setEnableFinalPrices(false); // ปิดฟิลด์ราคาสุดท้าย
      setShowCommissionFields(false); // ✅ ซ่อน field ระดับคอมมิชชั่น
    }
  }, [editMode, copyMode, form, name, editData, originalPrices]);

  /**
   * ✅ Init baseline สำหรับ restore shipping meta (เหมือน originalPrices ที่ใช้ restore c1-c3)
   * - editMode: ใช้ originalVariants + originalPrices (snapshot จาก API ตอนโหลดหน้า)
   * - copyMode: snapshot จากค่าเริ่มต้นใน form (ค่าที่คัดลอกมา)
   */
  useEffect(() => {
    if (shippingRestoreInitializedRef.current) return;
    if (!editMode && !copyMode) return;

    if (editMode && editData) {
      const itemId = editData.variant.items[0]?.id;
      const originalItem =
        originalVariants.find((v) => v.items?.[0]?.id === itemId)?.items?.[0] ??
        originalVariants.find((v) => v.id === itemId)?.items?.[0];
      const originalPrice = originalPrices.find((p) => p.variant_1_item_id === itemId);

      const fallbackItem = editData.variant.items[0] as any;
      const sourceItem = (originalItem as any) ?? fallbackItem;

      databaseShippingMethodRef.current = sourceItem?.shipping_method ?? "self";
      databaseWidthRef.current = Number(sourceItem?.width ?? 0);
      databaseLengthRef.current = Number(sourceItem?.length ?? 0);
      databaseHeightRef.current = Number(sourceItem?.height ?? 0);
      databaseWeightRef.current = Number(sourceItem?.weight ?? 0);
      databaseTransportPriceRef.current = Number(originalPrice?.transport_price ?? editData.price?.transport_price ?? 0);
      databaseApiTransportPriceRef.current = Number(
        originalPrice?.api_transport_price ?? editData.price?.api_transport_price ?? 0,
      );
      databaseAddOnApiTransportPriceRef.current = Number(
        originalPrice?.add_on_api_transport_price ??
          editData.price?.add_on_api_transport_price ??
          0,
      );
      databaseTransportCarrierCodeRef.current =
        originalPrice?.api_transport_carrier_code ??
        editData.price?.api_transport_carrier_code ??
        null;
      databaseTransportLogoRef.current =
        originalPrice?.api_transport_logo ??
        editData.price?.api_transport_logo ??
        null;
      databasePartnerIdRef.current =
        (sourceItem as any)?.partner_transport_id ?? null;
      databaseBrandIdRef.current =
        (sourceItem as any)?.partner_transport_brand_id ?? null;
      databaseIsCodRef.current = (sourceItem as any)?.is_cod ?? false;
      // ✅ snapshot บรรจุภัณฑ์สำหรับ restore ตอน self → bangbow
      databasePackagingTypeIdRef.current = (sourceItem as any)?.packaging_type_id ?? null;
      databaseParentPackagingTypeIdRef.current = null; // resolve จาก packaging ตอน restore ถ้าจำเป็น

      shippingRestoreInitializedRef.current = true;
      prevShippingMethodRef.current =
        (form.getFieldValue(["sku_items", name, "shipping_method"]) as string | undefined) ??
        databaseShippingMethodRef.current ??
        "self";
      return;
    }

    if (copyMode) {
      // รอให้ form ถูก setFieldsValue แล้วค่อย snapshot baseline
      setTimeout(() => {
        if (shippingRestoreInitializedRef.current) return;
        const v = form.getFieldValue(["sku_items", name]);
        if (!v) return;
        databaseShippingMethodRef.current = v.shipping_method ?? "self";
        databaseWidthRef.current = Number(v.width ?? 0);
        databaseLengthRef.current = Number(v.length ?? 0);
        databaseHeightRef.current = Number(v.height ?? 0);
        databaseWeightRef.current = Number(v.weight ?? 0);
        databaseTransportPriceRef.current = Number(v.transport_price ?? 0);
        databaseApiTransportPriceRef.current = Number(v.api_transport_price ?? 0);
        databaseAddOnApiTransportPriceRef.current = Number(v.add_on_api_transport_price ?? 0);
        databaseTransportCarrierCodeRef.current = v.api_transport_carrier_code ?? null;
        databaseTransportLogoRef.current = v.api_transport_logo ?? null;
        databasePartnerIdRef.current = v.partner_id ?? null;
        databaseBrandIdRef.current = v.brand_id ?? null;
        databaseIsCodRef.current = v.is_cod ?? false;
        databasePackagingTypeIdRef.current = v.packaging_type_id ?? null;
        databaseParentPackagingTypeIdRef.current = v._parent_packaging_type_id ?? null;
        shippingRestoreInitializedRef.current = true;
        prevShippingMethodRef.current = databaseShippingMethodRef.current;
      }, 0);
    }
  }, [editMode, copyMode, editData, originalVariants, originalPrices, form, name]);

  // ✅ ฟังก์ชันสำหรับเปิด password form
  const handleOpenPasswordForm = () => {
    setShowPasswordForm(true);
    setPassword("");
    setPasswordError("");
    setTimeout(() => {
      passwordInputRef.current?.focus();
    }, 100);
  };

  // ✅ ฟังก์ชันสำหรับปิด password form
  const handleClosePasswordForm = () => {
    setShowPasswordForm(false);
    setPassword("");
    setPasswordError("");
  };

  // ✅ ฟังก์ชันสำหรับจัดการเมื่อ total_commission เปลี่ยน
  // ✅ Refactored: ใช้ calculateCommissionLevels จาก helpers.ts แทน inline calculation
  const handleTotalCommissionChange = (value: number | null) => {
    const currentValue = value || 0;
    const dbValue = databaseTotalCommissionRef.current ?? 0;

    // ✅ edit/copy: ทุกครั้งที่มี event แก้ไข total_commission (onChange/onBlur ฯลฯ) ให้ถือว่าต้องคำนวณระดับใหม่ทันที — ตั้งก่อน branch ยอด 0
    if (editMode || copyMode) {
      userAdjustedTotalCommissionAwayFromDbRef.current = true;
    }

    // ถ้า total_commission เป็น 0 → ให้ c1-c5 = 0
    if (!currentValue || currentValue === 0) {
      if (!editMode && !copyMode && Math.abs(dbValue) > 0.01) {
        userAdjustedTotalCommissionAwayFromDbRef.current = true;
      }
      form.setFieldValue(['sku_items', name, 'commistion_lv1'], 0);
      form.setFieldValue(['sku_items', name, 'commistion_lv2'], 0);
      form.setFieldValue(['sku_items', name, 'commistion_lv3'], 0);
      form.setFieldValue(['sku_items', name, 'commistion_lv4'], 0);
      form.setFieldValue(['sku_items', name, 'commistion_lv5'], 0);
    } else {
      // ✅ เปรียบเทียบกับค่าจากฐานข้อมูลด้วย tolerance 0.01 (ให้สอดคล้องกับ validator เดิม) — ใช้เฉพาะ create mode สำหรับ restore จาก ref
      const isSameAsDb = Math.abs(currentValue - dbValue) <= 0.01;

      if (isSameAsDb && !userAdjustedTotalCommissionAwayFromDbRef.current) {
        // create mode เท่านั้น: ตรงกับ snapshot ref และยังไม่เคยแก้ช่องนี้ → restore c1–c5 จาก ref (edit/copy ตั้ง flag ไว้แล้วต้นฟังก์ชัน → ไม่เข้า path นี้)
        form.setFieldValue(['sku_items', name, 'commistion_lv1'], databaseC1Ref.current ?? 0);
        form.setFieldValue(['sku_items', name, 'commistion_lv2'], databaseC2Ref.current ?? 0);
        form.setFieldValue(['sku_items', name, 'commistion_lv3'], databaseC3Ref.current ?? 0);
        form.setFieldValue(['sku_items', name, 'commistion_lv4'], databaseC4Ref.current ?? 0);
        form.setFieldValue(['sku_items', name, 'commistion_lv5'], databaseC5Ref.current ?? 0);
      } else {
        // edit/copy: ทุกครั้งที่แก้ total_commission → คำนวณสัดส่วนมาตรฐานทันที | create: ยอดต่างจาก ref หรือเคยแก้แล้ว
        const { c1, c2, c3, c4, c5 } = calculateCommissionLevels(currentValue);
        form.setFieldValue(['sku_items', name, 'commistion_lv1'], c1);
        form.setFieldValue(['sku_items', name, 'commistion_lv2'], c2);
        form.setFieldValue(['sku_items', name, 'commistion_lv3'], c3);
        form.setFieldValue(['sku_items', name, 'commistion_lv4'], c4);
        form.setFieldValue(['sku_items', name, 'commistion_lv5'], c5);
      }
    }

    // เรียก handleFormChange เพื่อคำนวณราคาใหม่ (ส่งค่าใหม่เข้าไปเพื่อคำนวณทันที)
    handleFormChange("total_commission", currentValue);

    // ✅ validate หลังอัปเดต c1–c5 แล้ว (รวม edit/copy ที่ debounce/onInput) — ให้ rule ผลรวม c = total อ่านค่าสอดคล้อง
    setTimeout(() => {
      form.validateFields([["sku_items", name, "total_commission"]]).catch(() => { });
    }, 0);
  };

  /** edit/copy: อ่าน total_commission จากฟอร์มแล้วคำนวณแยกระดับทันที (ใช้หลัง blur / Enter) */
  const flushTotalCommissionRecalcFromForm = () => {
    if (totalCommissionRecalcTimerRef.current) {
      clearTimeout(totalCommissionRecalcTimerRef.current);
      totalCommissionRecalcTimerRef.current = null;
    }
    const v = toFiniteNumberOr0(form.getFieldValue(["sku_items", name, "total_commission"]));
    handleTotalCommissionChange(v);
  };

  /** edit/copy: หลังพิมพ์ — debounce แล้วอ่านจากฟอร์ม (แก้กรณีค่าตัวเลขเท่าเดิมแต่ต้องการคำนวณใหม่ โดยไม่ต้องรอ blur อย่างเดียว) */
  const scheduleTotalCommissionRecalcFromForm = () => {
    if (!(editMode || copyMode)) return;
    if (totalCommissionRecalcTimerRef.current) {
      clearTimeout(totalCommissionRecalcTimerRef.current);
    }
    totalCommissionRecalcTimerRef.current = setTimeout(() => {
      totalCommissionRecalcTimerRef.current = null;
      const v = toFiniteNumberOr0(form.getFieldValue(["sku_items", name, "total_commission"]));
      handleTotalCommissionChange(v);
    }, 80);
  };

  // ✅ ฟังก์ชันสำหรับยืนยันรหัสผ่าน
  const handleConfirmPassword = () => {
    if (password === "414428") {
      // รหัสเต็ม: แสดงทุก field รวม การตลาด + ผู้แนะนำร้าน
      setShowCommissionFields(true);
      setShowAllCommissionFields(true);
      setManualBangbowTransportUnlocked(true);
      handleClosePasswordForm();
    } else if (password === "1111") {
      // รหัสจำกัด: แสดงเฉพาะ ดูแลผู้แนะนำ + ผู้แนะนำ + ผู้ซื้อ
      setShowCommissionFields(true);
      setShowAllCommissionFields(false);
      handleClosePasswordForm();
    } else {
      setPasswordError("รหัสผ่านไม่ถูกต้อง");
      setPassword("");
    }
  };

  // ✅ ฟังก์ชันสำหรับลบ error เมื่อ focus ที่ password field
  const handlePasswordFocus = () => {
    setPasswordError("");
  };

  /**
   * ฟังก์ชันสำหรับลบเลข 0 เมื่อ focus ที่ช่องราคา
   * เพื่อให้ผู้ใช้พิมพ์ได้ง่ายขึ้น
   * 
   * หมายเหตุ: สำหรับ total_commission ใน editMode หรือ copyMode
   * ถ้าค่าจาก db ไม่ใช่ 0 จะไม่ clear ค่า
   */
  const handleFocus = (fieldName: string) => {
    const currentValue = form.getFieldValue(["sku_items", name, fieldName]);

    // ✅ factory_price เป็น readonly (ระบบคำนวณให้) ไม่ควร clear ค่าเมื่อ focus
    if (fieldName === "factory_price") {
      return;
    }

    // ✅ สำหรับ total_commission ใน editMode หรือ copyMode
    // ถ้าค่าจาก db ไม่ใช่ 0 จะไม่ clear ค่า
    if (fieldName === "total_commission" && (editMode || copyMode)) {
      const dbValue = databaseTotalCommissionRef.current ?? 0;
      // ถ้าค่าปัจจุบันเป็น 0 และค่าจาก db ก็เป็น 0 → clear
      // ถ้าค่าปัจจุบันเป็น 0 แต่ค่าจาก db ไม่ใช่ 0 → ไม่ clear (แสดงค่าจาก db)
      if (currentValue === 0 && dbValue === 0) {
        form.setFieldValue(["sku_items", name, fieldName], undefined);
      }
      // ถ้าค่าปัจจุบันเป็น 0 แต่ค่าจาก db ไม่ใช่ 0 → ตั้งค่าเป็นค่าจาก db
      if (currentValue === 0 && dbValue !== 0) {
        form.setFieldValue(["sku_items", name, fieldName], dbValue);
      }
    } else {
      // สำหรับ field อื่นๆ: clear เมื่อค่าเป็น 0
      // ✅ รองรับทั้ง number 0 และ string "0"/"0.00" (กรณี formatter แสดง 0.00)
      const num =
        typeof currentValue === "string"
          ? Number(currentValue.replace(/,/g, "").trim())
          : Number(currentValue);
      if (Number.isFinite(num) && num === 0) {
        form.setFieldValue(["sku_items", name, fieldName], undefined);
      }
    }
  };

  /**
   * ✅ UX requirement (ล่าสุด): ไม่ต้องลบค่า 0.00 ตอน focus แล้ว
   * ให้ "ไฮไลท์เลือกทั้งหมด" เพื่อให้ผู้ใช้พิมพ์ทับได้ทันที (ใช้ได้ทั้ง create/edit/copy)
   *
   * หมายเหตุ: AntD InputNumber + formatter อาจ commit ค่าหลัง focus
   * จึงใช้ requestAnimationFrame เพื่อ select หลัง DOM update
   */
  const handleFocusHighlightAllText =
    (_fieldName: string) => (e: React.FocusEvent<HTMLInputElement>) => {
      const el = e.target;
      requestAnimationFrame(() => {
        try {
          el.setSelectionRange(0, el.value.length);
        } catch {
          // ignore
        }
      });
    };

  /**
   * ✅ [requirement 2026-10-16] ใช้กับ input_market_price / base_factory_price แทน highlight ทั้งหมด:
   * - ถ้าค่าปัจจุบันเป็น 0 (แสดง "0.00") → ตอน focus ลบออกให้ว่าง พิมพ์เลขใหม่ได้ทันที
   *   (onBlur เดิม handleBlurRestoreZeroIfEmpty จะ restore กลับเป็น 0 เองถ้าออกจากช่องโดยไม่พิมพ์)
   * - ถ้ามีค่าอื่นที่ไม่ใช่ 0 → ไม่ไฮไลท์ ปล่อย cursor ไปอยู่ตำแหน่งแรกสุด (เหมือน self_transport_price)
   */
  const handleFocusClearZeroOrCursorStart =
    (fieldName: string) => (e: React.FocusEvent<HTMLInputElement>) => {
      const el = e.target;
      // ✅ [บั๊กฟิกซ์] ต้องเคลียร์เป็น "" (empty string) ห้ามใช้ undefined/null —
      //    react-number-format (useInternalValues) ถือว่า value เป็น null/undefined คือ
      //    "สลับไปโหมด uncontrolled" แล้วดึงค่าที่จำไว้ภายใน (internal state เดิม "0"/"0.00")
      //    กลับมาแสดงแทนทันที ทำให้เคลียร์ไม่ได้เลยไม่ว่าจะหน่วงเวลาแค่ไหนก็ตาม ส่วน "" ไม่ถูกนับเป็น
      //    nil จึงไม่โดน fallback นี้ และ format("") ของ library คืนค่าว่างตรงๆ ตามที่ต้องการ
      requestAnimationFrame(() => {
        const currentValue = form.getFieldValue(["sku_items", name, fieldName]);
        const numValue = toFiniteNumberOr0(currentValue);
        if (numValue === 0) {
          form.setFieldValue(["sku_items", name, fieldName], "");
          return;
        }
        try {
          el.setSelectionRange(0, 0);
        } catch {
          // ignore
        }
      });
    };

  /**
   * ✅ UX requirement: ถ้าผู้ใช้ focus แล้วลบจนว่าง หรือไม่ได้กรอกอะไร
   * ให้ blur แล้ว restore เป็น 0 (เพื่อแสดง 0.00 ผ่าน formatter)
   * ใช้เฉพาะ field ที่ต้องการ behavior นี้ เช่น base_factory_price
   */
  const handleBlurRestoreZeroIfEmpty = (fieldName: string, afterRestore?: (restoredValue: number) => void) => {
    const currentValue = form.getFieldValue(["sku_items", name, fieldName]);
    const isEmpty =
      currentValue === undefined ||
      currentValue === null ||
      currentValue === "" ||
      (typeof currentValue === "number" && Number.isNaN(currentValue));
    if (isEmpty) {
      form.setFieldValue(["sku_items", name, fieldName], 0);
      afterRestore?.(0);
    }
  };

  /**
   * ✅ Handler สำหรับช่อง "ราคาตลาดที่แสดง" ด้านล่าง (Card ราคาสุดท้าย)
   * เมื่อ blur: ถ้า retail_price < factory_price → auto-correct เป็น factory_price + 5% (ปัดขึ้น)
   * retail_price (ด้านล่าง) แยกจาก input_market_price (ด้านบน) — ไม่ sync กลับ
   */
  const handleDisplayRetailPriceBlur = () => {
    const formValues = form.getFieldValue(["sku_items", name]);
    if (!formValues) return;
    const retailPrice = toFiniteNumberOr0(formValues.retail_price);
    const factoryPrice = toFiniteNumberOr0(formValues.factory_price);
    if (retailPrice < factoryPrice && factoryPrice > 0) {
      const corrected = Math.ceil(factoryPrice * 1.05);
      form.setFieldValue(["sku_items", name, "retail_price"], corrected);
    }
  };

  /**
   * ฟังก์ชันสำหรับลบเลข 0 เมื่อ focus ที่ช่อง unit price
   * เพื่อให้ผู้ใช้พิมพ์ได้ง่ายขึ้น (รองรับทั้ง Input string และ InputNumber)
   */
  const handleFocusUnitPrice = (fieldName: string) => {
    const currentValue = form.getFieldValue(["sku_items", name, fieldName]);
    // ✅ รองรับทั้ง string "0" และ number 0
    if (currentValue === "0" || currentValue === 0) {
      form.setFieldValue(["sku_items", name, fieldName], undefined);
    }
  };

  /**
   * ✅ Handler สำหรับตรวจสอบราคาตั้งต้นเมื่อ Blur
   * - ถ้า ราคาตั้งต้น < คอมมิชชั่นรวม -> ให้แสดง Modal ยืนยันเพื่อรีเซ็ตคอมมิชชั่นเป็น 0
   
  const handleBaseFactoryPriceBlur = () => {
    handleBlurRestoreZeroIfEmpty("base_factory_price");

    // ใช้ setTimeout เพื่อรอให้ค่าถูก update จาก handleBlurRestoreZeroIfEmpty ก่อน
    setTimeout(() => {
      const formValues = form.getFieldValue(["sku_items", name]);
      if (!formValues) return;

      const basePrice = Number(String(formValues.base_factory_price ?? 0).replace(/,/g, ""));
      const totalCommission = Number(String(formValues.total_commission ?? 0).replace(/,/g, ""));

      // ✅ เงื่อนไข: ถ้ามีคอมมิชชั่น และ ราคาตั้งต้นต่ำกว่าคอมมิชชั่น
      if (totalCommission > 0 && basePrice < totalCommission) {
        // ✅ เปลี่ยนจาก Modal confirm เป็นแค่แจ้งเตือน (Warning) รอให้ user แก้เอง หรือไปบล็อกตอน save
        message.warning(`ราคาตั้งต้น (${formatWithCommas(basePrice)}) ต่ำกว่าค่าคอมมิชชั่น (${formatWithCommas(totalCommission)}) กรุณาตรวจสอบและแก้ไขก่อนบันทึก`);
      }
    }, 100);
  };
*/
  // ✅ removeLeadingZeros ย้ายไปอยู่ใน helpers.ts แล้ว

  // ✅ isBasePriceOrTransportField ย้ายไปอยู่ใน helpers.ts แล้ว

  /**
   * ฟังก์ชันสำหรับตรวจสอบและอัปเดต state เมื่อมีการเปลี่ยนแปลงค่า
   * ฟังก์ชันนี้จะถูกเรียกทุกครั้งที่มีการเปลี่ยนแปลงค่าในฟิลด์
   *
   * @param changedField - ชื่อฟิลด์ที่เปลี่ยนแปลง (optional)
   * @param nextValue - ค่าใหม่ (optional)
   * @param userTriggered - true = ผู้ใช้กระทำเอง (override retail_price ได้), false = ระบบ/auto-trigger (ไม่ override retail_price → คง DB)
   *                       default = true เพื่อ backward-compat สำหรับ onChange handlers ของผู้ใช้
   */
  const handleFormChange = (changedField?: string, nextValue?: any, userTriggered: boolean = true) => {
    // if (changedField === "base_factory_price") {
    //   nextValue = parseFloat(nextValue.toString().replace(/,/g, ''));
    // }

    let formValues = form.getFieldValue(["sku_items", name]);
    if (!formValues) return;

    // ✅ ตรวจสอบ base_factory_price ก่อนเรียก toString()
    if (formValues.base_factory_price !== undefined && formValues.base_factory_price !== null) {
      formValues.base_factory_price = parseFloat(String(formValues.base_factory_price).replace(/,/g, ''));
    }
    // ✅ AntD Form อาจยังไม่ commit ค่าใหม่ทันทีในบางจังหวะ
    // จึง override ค่า field ที่เปลี่ยนด้วย nextValue เพื่อให้คำนวณได้ทันที
    if (changedField && nextValue !== undefined) {
      formValues = { ...formValues, [changedField]: nextValue };
    }

    /**
     * Re-validate ฟิลด์ที่เกี่ยวข้องเมื่อมีการเปลี่ยนแปลงราคา
     * เช่น: เมื่อแก้ไข base_factory_price จาก 100 → 150
     *       ระบบจะตรวจสอบ base_vip_price ว่ายัง >= 150 หรือไม่
     *
     * ✅ อัปเดต: เพิ่มการ validate field ทางซ้ายด้วย (เพื่อแสดง error ที่ field ทางซ้าย)
     *
     * ใช้ setTimeout(0) เพื่อให้ค่าใหม่ถูกบันทึกใน form ก่อนทำการ validate
     * เพื่อให้ validator ได้ค่าล่าสุดจาก form
     */
    // setTimeout(() => {
    //   form
    //     .validateFields([
    //       // ✅ เพิ่ม: validate field ทางซ้าย (แสดง error ที่ field ทางซ้าย)
    //       ["sku_items", name, "base_factory_price"], // ตรวจสอบว่า <= base_retail_price
    //       ["sku_items", name, "base_retail_price"], // ตรวจสอบว่า > base_factory_price
    //       ["sku_items", name, "factory_price"], // ตรวจสอบว่า <= retail_price
    //       ["sku_items", name, "retail_price"], // ตรวจสอบว่า > factory_price
    //     ])
    //     .catch(() => {
    //       // ไม่ต้องทำอะไร เพียงแค่ trigger validation เพื่อแสดง error message
    //     });
    // }, 0);

    /**
     * สำหรับ editMode และ copyMode:
     * - ไม่ต้องตรวจสอบการเปิด-ปิดฟิลด์ (เปิดทุกฟิลด์อยู่แล้ว)
     * - คำนวณราคาสุดท้ายเฉพาะเมื่อเปลี่ยนแปลงราคาตั้งต้นหรือค่าขนส่ง
     */
    if (editMode || copyMode) {
      // ✅ คำนวณราคาขายใหม่ทุกครั้งเมื่อเปลี่ยน:
      // 1) base_factory_price 2) transport_price 3) คอมมิชชั่น (c1-c3 / total_commission)
      // เงื่อนไขขั้นต่ำ: ต้องมี base_factory_price (ใช้ค่า 0 ได้แต่ส่วนใหญ่คำนวณเมื่อ > 0)

      // ✅ [requirement ใหม่ 2026-10-15] Edit/Copy mode: แก้ input_market_price → ไม่ recalc
      //    factory_price/commission/base_factory_price (คงค่าจาก DB ไว้เหมือนเดิม ตาม requirement
      //    ก่อนหน้า) แต่ sync retail_price ตามสูตรเดียวกับ create mode (calculateFinalPrices) โดย
      //    เทียบกับ factory_price "ปัจจุบัน" ที่มีอยู่แล้วในฟอร์ม (ไม่คำนวณ factory_price ใหม่):
      //      - ถ้า input_market_price < factory_price ปัจจุบัน → retail_price = ceil(factory_price × 1.05)
      //      - ไม่งั้น → retail_price = input_market_price ตรงๆ
      //    คง logic เดิมไว้ถ้า input_market_price ถูกลบจนว่าง/เป็น 0 → ไม่แตะ retail_price เลย
      //    (ข้อมูลอาจเป็นของจริงที่เคยบันทึกไว้แล้ว ไม่อยากให้หายง่ายๆ แค่เพราะลบ market price ชั่วคราว)
      // หมายเหตุ (เก็บไว้เป็นประวัติ — logic เดิมที่ถูกแทนที่):
      //   if (changedField === "input_market_price" && formValues.input_market_price > 0) {
      //     if (percentCommission > 0) {
      //       const commission = round2(formValues.input_market_price * percentCommission / 100);
      //       form.setFieldValue(["sku_items", name, "total_commission"], commission);
      //       formValues = { ...formValues, total_commission: commission };
      //       handleTotalCommissionChange(commission);
      //     }
      //     if (formValues.base_factory_price > 0) {
      //       calculateFinalPrices(formValues, userTriggered);
      //     }
      //   }
      if (changedField === "input_market_price") {
        const inputMarketPriceForSync = toFiniteNumberOr0(formValues.input_market_price);
        if (inputMarketPriceForSync > 0) {
          const currentFactoryPrice = toFiniteNumberOr0(formValues.factory_price);
          if (currentFactoryPrice > 0) {
            if (inputMarketPriceForSync < currentFactoryPrice) {
              form.setFieldValue(["sku_items", name, "retail_price"], Math.ceil(currentFactoryPrice * 1.05));
            } else {
              form.setFieldValue(["sku_items", name, "retail_price"], inputMarketPriceForSync);
            }
          }
        }
        return;
      }

      if (
        formValues.base_factory_price !== undefined &&
        changedField &&
        isBasePriceOrTransportField(changedField)
      ) {
        // ใช้ฟังก์ชันคำนวณที่เหมาะสมตาม mode (✅ แก้ไข: ใช้ standard calculateFinalPrices เสมอ เพื่อลดความซับซ้อนและ prevents drift)
        // ✅ userTriggered = false เมื่อถูกเรียกจาก system/auto-trigger (เช่น auto-search speed shipping) → ไม่ override retail_price
        calculateFinalPrices(formValues, userTriggered);
      }

      return; // จบการทำงานสำหรับ edit/copy mode
    } else {
      // console.log('create mode---------');
      if (changedField === "base_vip_price") {
        form.setFieldValue(["sku_items", name, "vip_price"], formValues.base_vip_price ?? 0)
      }
      if (changedField === "base_wholesale_price") {
        form.setFieldValue(["sku_items", name, "wholesale_price"], formValues.base_wholesale_price ?? 0)
      }
    }

    /**
     * สำหรับ create mode (progressive form):
     * ตรวจสอบและปลดล็อกฟิลด์ทีละขั้นตอน
     */

    /**
     * ✅ ขั้นตอนที่ 1: ตรวจสอบราคาตั้งต้นครบหรือไม่
     * - ถ้า sell_through_line = true: ต้องกรอกทั้ง 4 ฟิลด์ (base_factory_price, base_vip_price, base_wholesale_price, base_retail_price)
     * - ถ้า sell_through_line = false: ต้องกรอกเฉพาะ 2 ฟิลด์ (base_factory_price, base_retail_price)
     * ถ้าครบ → เปิดฟิลด์ขนาดและน้ำหนัก + เปิดฟิลด์ราคาสุดท้าย + คำนวณราคาสุดท้ายทันที
     */
    const basePricesComplete = sellThroughLine
      ? (formValues.base_factory_price > 0 &&
        formValues.base_vip_price > 0 &&
        formValues.base_wholesale_price > 0 &&
        formValues.base_retail_price > 0)
      : (formValues.base_factory_price > 0 &&
        formValues.base_retail_price > 0);

    setEnableDimensions(basePricesComplete);

    /**
     * ✅ เปิดฟิลด์ราคาสุดท้ายทันทีเมื่อกรอก base price ครบ (ไม่ต้องรอค่าขนส่ง)
     */
    setEnableFinalPrices(basePricesComplete);

    /**
     * ขั้นตอนที่ 2: ตรวจสอบขนาด & น้ำหนักครบหรือไม่ (ทั้ง 4 ฟิลด์)
     * ถ้าครบ → เปิดฟิลด์ค่าขนส่ง
     */
    const dimensionsComplete =
      basePricesComplete &&
      formValues.width > 0 &&
      formValues.length > 0 &&
      formValues.height > 0 &&
      formValues.weight > 0;

    setEnableTransport(dimensionsComplete);

    /**
     * ✅ ขั้นตอนที่ 3: คำนวณราคาสุดท้ายอัตโนมัติทันทีเมื่อกรอก base_factory_price
     * ไม่ต้องรอให้กรอก base_retail_price ครบ (ถ้ายังไม่กรอก ใช้ค่า 0 ในการคำนวณ)
     * ไม่ต้องรอค่าขนส่ง (ถ้ายังไม่กรอกค่าขนส่ง ใช้ค่า 0 ในการคำนวณ)
     * ✅ คำนวณใหม่เฉพาะเมื่อเปลี่ยนแปลงราคาตั้งต้นหรือค่าขนส่ง
     * ไม่คำนวณถ้าเปลี่ยนแปลงราคาสุดท้าย (เพื่อให้ผู้ใช้กรอกเองได้)
     */
    if (
      changedField &&
      isBasePriceOrTransportField(changedField)
    ) {
      // ✅ [บั๊กฟิกซ์] create mode: ถ้าผู้ใช้ลบ input_market_price จนว่าง/เป็น 0 (หรือ NaN)
      //    → รีเซ็ตฟิลด์ที่ auto-fill จาก market price ทั้งหมดให้เป็น 0
      //    (total_commission, c1-c5, base_factory_price, factory_price, retail_price, payment_getway)
      //    เหตุผลที่ setFieldValue ตรงๆ แทนการเรียก handleTotalCommissionChange(0) + calculateFinalPrices():
      //      - handleTotalCommissionChange(0) จะเรียก handleFormChange("total_commission", 0) ต่อ → เกิด side effect ซ้อน
      //      - calculateFinalPrices() จะไม่เซ็ต retail_price=0 เพราะเงื่อนไข calculatedFactoryPrice > 0 (~บรรทัด 691)
      if (
        changedField === "input_market_price" &&
        !(toFiniteNumberOr0(formValues.input_market_price) > 0)
      ) {
        form.setFieldValue(["sku_items", name, "total_commission"], 0);
        form.setFieldValue(["sku_items", name, "commistion_lv1"], 0);
        form.setFieldValue(["sku_items", name, "commistion_lv2"], 0);
        form.setFieldValue(["sku_items", name, "commistion_lv3"], 0);
        form.setFieldValue(["sku_items", name, "commistion_lv4"], 0);
        form.setFieldValue(["sku_items", name, "commistion_lv5"], 0);
        form.setFieldValue(["sku_items", name, "base_factory_price"], 0);
        form.setFieldValue(["sku_items", name, "factory_price"], 0);
        form.setFieldValue(["sku_items", name, "retail_price"], 0);
        form.setFieldValue(["sku_items", name, "payment_getway"], 0);
        return;
      }
      // ✅ เมื่อ input_market_price เปลี่ยน (create mode): auto-calculate commission + reverse-calc base_factory_price + factory_price
      if (changedField === "input_market_price" && formValues.input_market_price > 0) {
        // 1. คำนวณ commission จาก input_market_price (ไม่ใช่ base_factory_price)
        if (percentCommission > 0) {
          // ✅ [requirement 2026-10-16] ค่าคอมมิชชั่นที่มาจากการคำนวณ (% × ราคาตลาด) ถ้ามีเศษทศนิยม
          //    ให้ปัดขึ้นเป็นจำนวนเต็มบาททุกกรณี (ไม่ใช้ round2/ปัดเข้าใกล้เหมือนเดิม)
          const commission = Math.ceil(formValues.input_market_price * percentCommission / 100);
          form.setFieldValue(["sku_items", name, "total_commission"], commission);
          formValues = { ...formValues, total_commission: commission };
          handleTotalCommissionChange(commission);
        }
        // 2. คำนวณ base_factory_price = retail × 0.80 (ตรงๆ — สูตรใหม่ "ราคาที่โอนให้ผู้ประกอบการ = แนะนำขาย 80%")
        // หมายเหตุ: เดิมเคยเป็น Reverse-calculate base_factory_price จากสูตร 75% (หัก S/C/G) — คงคอมเมนต์เก่าไว้เป็นประวัติ
        const shippingMethod = (formValues.shipping_method ?? watchedShippingMethod ?? "self") as string;
        const transport = shippingMethod === "self" ? 0 : toFiniteNumberOr0(formValues.transport_price);
        const base = reverseCalculateBasePrice(
          formValues.input_market_price,
          percentCommission,
          paymentGatewayPercent,
          transport
        );
        form.setFieldValue(["sku_items", name, "base_factory_price"], base);
        formValues = { ...formValues, base_factory_price: base };
        // 3. คำนวณ factory_price
        // ✅ userTriggered = true (ผู้ใช้แก้ input_market_price) → override retail_price ตามเงื่อนไข
        calculateFinalPrices(formValues, userTriggered);
      }
      // ✅ คำนวณทันทีเมื่อกรอก base_factory_price (ไม่ต้องรอ base_retail_price)
      // ✅ เมื่อ base_factory_price เปลี่ยนด้วยมือ → แค่ recalculate factory_price (commission ยังคิดจาก retail_price)
      else if (changedField === "base_factory_price" && formValues.base_factory_price > 0) {
        calculateFinalPrices(formValues, userTriggered);
      }
      // ✅ คำนวณทันทีเมื่อเปลี่ยนค่าขนส่ง (เพื่อให้ factory_price อัปเดต แม้ basePricesComplete ยังไม่ครบ)
      // หมายเหตุ: เงื่อนไขนี้เจาะจงเฉพาะ transport_price เพื่อลดผลกระทบกับ field อื่น
      else if (changedField === "transport_price" && formValues.base_factory_price > 0) {
        calculateFinalPrices(formValues, userTriggered);
      }
      // ✅ คำนวณทันทีเมื่อเปลี่ยนค่าส่งเอง (self_transport_price) — เหมือน transport_price
      else if (changedField === "self_transport_price" && formValues.base_factory_price > 0) {
        calculateFinalPrices(formValues, userTriggered);
      }
      // ✅ คำนวณทันทีเมื่อเปลี่ยน include_transport_price (ส่งฟรี / แยกค่าขนส่ง)
      else if (changedField === "include_transport_price" && formValues.base_factory_price > 0) {
        calculateFinalPrices(formValues, userTriggered);
      }
      // ✅ คำนวณทันทีเมื่อเปลี่ยนคอมมิชชั่น (ใช้เฉพาะ total_commission)
      else if (
        (changedField === "total_commission") &&
        formValues.base_factory_price > 0
      ) {
        calculateFinalPrices(formValues, userTriggered);
      }
      // ✅ สำหรับ field อื่นๆ ยังคงคำนวณเมื่อกรอกครบตามเดิม
      else if (basePricesComplete) {
        calculateFinalPrices(formValues, userTriggered);
      }
    }
  };

  // ---------------------------
  // ✅ Speed Shipping: check-price (north + south) → merge by carrier_code (max total) → sort desc
  // ---------------------------
  // ✅ parseTotal, parseDeliveryMinDays ย้ายไปอยู่ใน helpers.ts แล้ว

  const mergeSpeedItems = (a: CheckPriceCarrierItem[], b: CheckPriceCarrierItem[]) => {
    const map = new Map<string, CheckPriceCarrierItem>();
    const put = (item: CheckPriceCarrierItem) => {
      const code = (item?.carrier_code ?? "").toString();
      if (!code) return;
      const prev = map.get(code);
      if (!prev) {
        map.set(code, item);
        return;
      }
      const prevTotal = parseTotal(prev.total ?? prev.price);
      const nextTotal = parseTotal(item.total ?? item.price);
      if (nextTotal >= prevTotal) map.set(code, item);
    };
    a.forEach(put);
    b.forEach(put);
    return Array.from(map.values());
  };

  const hasMerchantSpeed = (factoryData?.factory?.merchants?.speed?.length ?? 0) > 0;
  const hasMerchantThaipost = (factoryData?.factory?.merchants?.thaipost?.length ?? 0) > 0;
  // ✅ เลือก "ส่งกับแบ่งเบา" แต่ยังไม่มีที่อยู่รับสินค้า (merchant) เลย → ซ่อนรายละเอียดขนส่งทั้งหมด
  //    แล้วโชว์แค่ข้อความ + ปุ่มไปตั้งค่าที่อยู่รับสินค้าแทน (ดูจุดใช้งานใน Card "กำหนด dimension สินค้า")
  const bangbowMissingMerchant = watchedShippingMethod === "bangbow" && !hasMerchantSpeed && !hasMerchantThaipost;
  // ✅ ยังไม่ได้เลือกวิธีจัดส่งเลย (dropdown โชว์ placeholder อยู่) → ซ่อนรายละเอียดด้านล่างทั้งหมดไปก่อน
  const hasShippingMethodSelected = Boolean(watchedShippingMethod);

  // ✅ brand/partner แถวนี้ "ใช้งานได้จริง" (ไม่ถูกปิด/ลบทั้งที่ตัว brand หรือที่ partner เจ้าของ)
  const isBrandActiveForCod = (b: any) =>
    b?.is_delete !== true &&
    b?.brand_status !== false &&
    b?.partner_is_active !== false &&
    b?.partner_is_delete !== true;

  // ✅ ใช้เช็คว่ามี partner_transport_brand ที่รองรับ COD (is_cod=true) อย่างน้อย 1 ตัว
  //    และ brand/partner นั้นยังเปิดใช้งานอยู่จริง (ไม่นับ brand/partner ที่ปิด/ลบไปแล้ว แม้ is_cod=true)
  //    แทนที่เงื่อนไขเดิม hasMerchantThaipost ซึ่งเช็คแค่ merchant ไปรษณีย์ไทย ไม่เกี่ยวกับ COD เลย
  const hasCodCapableBrand = partnerTransportBrands.some((b) => b.is_cod === true && isBrandActiveForCod(b));

  // ✅ ใช้ตอนกรองผลค้นหาค่าขนส่ง (check-price) เมื่อเลือก COD — เทียบ carrier_code ของผลลัพธ์กับ
  //    brand_code ใน partner_transport_brand ว่าตัวนั้นเปิด is_cod ไว้หรือไม่ (ไปรษณีย์ไทยมีทั้ง
  //    "THAIPOST" (brand_code ปัจจุบัน) และ "THAILAND_POST" (legacy/partner code เก่าที่ SKU เก่าอาจเก็บไว้)
  const normalizeCarrierCode = (code?: string | null) => (code === "THAILAND_POST" ? "THAIPOST" : code);
  const isCarrierCodCapable = (carrierCode?: string | null) => {
    const normalized = normalizeCarrierCode(carrierCode);
    return partnerTransportBrands.some(
      (b) => normalizeCarrierCode(b.brand_code) === normalized && b.is_cod === true && isBrandActiveForCod(b)
    );
  };

  const getDefaultMerchantCodes = (): string[] => {
    const defaultMerchant = factoryData?.factory?.merchants?.speed?.find((m: { is_default: boolean }) => m.is_default);
    return defaultMerchant ? [defaultMerchant.merchant_id] : [];
  };

  const normalizeFactoryOrigin = () => {
    // Priority 1: ใช้ Speed merchants ที่ is_default เป็น true (จาก factoryData)
    const defaultMerchant = factoryData?.factory?.merchants?.speed?.find((m: { is_default: boolean }) => m.is_default);
    // console.log("defaultMerchant", defaultMerchant)
    if (defaultMerchant) {
      return {
        county: defaultMerchant.county ? String(defaultMerchant.county) : "",
        city: defaultMerchant.city ? String(defaultMerchant.city) : "",
        state: defaultMerchant.state ? String(defaultMerchant.state) : "",
        postcode: defaultMerchant.postcode !== undefined && defaultMerchant.postcode !== null
          ? String(defaultMerchant.postcode) : "",
      };
    }

    // Priority 2: Fallback ใช้ address_fac (logic เดิม)
    let fac = factoryData?.factory?.address_fac || factoryData?.factory?.address_fac;
    if (!fac) {
      fac = userContact?.address_fac;
    }
    const postcode = fac?.zip_code !== undefined && fac?.zip_code !== null ? String(fac.zip_code) : "";
    return {
      county: fac?.sub_district_name ? String(fac.sub_district_name) : "",
      city: fac?.district_name ? String(fac.district_name) : "",
      state: fac?.province_name ? String(fac.province_name) : "",
      postcode,
    };
  };

  const buildDestination = (area: "north" | "south") => {
    // ✅ Use speedShippingConfig (API) instead of session
    const s = speedShippingConfig?.data?.destination;
    const d = area === "north" ? s?.north : s?.south;
    return {
      county: d?.county ? String(d.county) : "",
      city: d?.city ? String(d.city) : "",
      state: d?.state ? String(d.state) : "",
      postcode: d?.postcode ? String(d.postcode) : "",
    };
  };

  const isAddressComplete = (addr: { county: string; city: string; state: string; postcode: string }) =>
    !!(addr.county && addr.city && addr.state && addr.postcode);

  // ✅ ปุ่มค้นหาบริษัทขนส่ง: แสดงตลอด แต่ disabled ถ้าไม่มี merchant / เป็น self / มิติไม่ครบ / ที่อยู่ไม่ครบ / กำลังโหลด
  const canSearchSpeedShipping = useMemo(() => {
    const shippingMethod = (watchedShippingMethod ?? "self") as string;
    if (shippingMethod !== "bangbow") return false;
    if (!hasMerchantSpeed && !hasMerchantThaipost) return false;
    if (speedLoading || speedConfigLoading) return false;
    // ✅ ต้องเลือกบรรจุภัณฑ์ครบก่อนค้นหา (สอดคล้องกับ guidance + submit)
    if (packagingSelectionIncomplete) return false;

    const w = Number(watchedWidth ?? 0);
    const l = Number(watchedLength ?? 0);
    const h = Number(watchedHeight ?? 0);
    const wt = Number(watchedWeight ?? 0);
    if (!(w > 0 && l > 0 && h > 0 && wt > 0)) return false;
    if (w + l + h > 1000) return false;

    // ✅ thaipostOnly = ไม่มี merchant Speed เลย (ไม่เกี่ยวกับ COD อีกต่อไป) — ตอน COD ถ้ามี merchant Speed
    //    และมี brand ที่เปิด is_cod จะยังค้นหาผ่าน Speed ได้ (ต้องมี origin address เหมือนปกติ)
    const thaipostOnly = !hasMerchantSpeed && hasMerchantThaipost;
    const north = buildDestination("north");
    const south = buildDestination("south");
    if (!thaipostOnly) {
      // Speed ต้องการ origin address ของโรงงาน
      const origin = normalizeFactoryOrigin();
      if (!isAddressComplete(origin)) return false;
    }
    if (!isAddressComplete(north) || !isAddressComplete(south)) return false;

    return true;
  }, [
    watchedShippingMethod,
    watchedIsCod,
    watchedWidth,
    watchedLength,
    watchedHeight,
    watchedWeight,
    speedLoading,
    speedConfigLoading,
    userContact,
    masterProvinceData,
    factoryData,
    speedShippingConfig,
    packagingSelectionIncomplete,
    hasMerchantSpeed,
    hasMerchantThaipost,
  ]);

  // ✅ toggle ไป COD → auto re-search เมื่อพร้อมเท่านั้น (ยิงครั้งเดียวต่อ toggle)
  useEffect(() => {
    if (!codAutoSearchPendingRef.current) return;
    if (!watchedIsCod) {
      codAutoSearchPendingRef.current = false;
      return;
    }
    if (!canSearchSpeedShipping) return;
    if (speedLoading) return;
    codAutoSearchPendingRef.current = false;
    setSpeedHasSearched(true);
    setSpeedSearchTrigger((x) => x + 1);
  }, [watchedIsCod, canSearchSpeedShipping, speedLoading]);

  /**
   * คำแนะใต้ปุ่ม "ค้นหาบริษัทขนส่ง" (เฉพาะ bangbow) — ลำดับ: เลือกบรรจุภัณฑ์ → ย่อย (ถ้ามี) → มิติ/น้ำหนัก → ที่อยู่/โหลด config → คลิกค้นหา → เลือกขนส่ง
   * - ซ่อนกว้าง/ยาว/สูง (มีขนาดจาก master แล้ว): ถ้ายังไม่มีน้ำหนัก → "โปรดระบุน้ำหนักบรรจุภัณฑ์"
   * - แสดงกว้าง/ยาว/สูง: ถ้ายังไม่ครบ → "โปรดระบุขนาดและน้ำหนักบรรจุภัณฑ์"
   * Parent/child ที่เป็น is_other จะอยู่ในกลุ่มที่แสดงมิติ (hideDimensionFields=false)
   * ถ้ามี speedStaleHint ไม่แสดงข้อความนี้ (ให้ Alert แดงด้านบนเป็นหลัก)
   */
  const partnerShippingGuidanceBelowSearch = useMemo(() => {
    if (isSelfShipping) return null;
    if (speedStaleHint) return null;

    const parentId = selectedParentPackagingTypeId;
    if (!parentId) {
      return { type: "warning" as const, message: "โปรดเลือกประเภทบรรจุภัณฑ์" };
    }

    if (showChildDropdown && !watchedPackagingTypeId) {
      return { type: "warning" as const, message: "โปรดเลือกบรรจุภัณฑ์ย่อย" };
    }

    const w = Number(watchedWidth ?? 0);
    const l = Number(watchedLength ?? 0);
    const h = Number(watchedHeight ?? 0);
    const wt = Number(watchedWeight ?? 0);
    // ซ่อนกว้าง/ยาว/สูง (เลือกบรรจุที่มีขนาดมาแล้ว) → เหลือน้ำหนักที่ต้องกรอกเอง
    if (hideDimensionFields) {
      if (wt <= 0) {
        return { type: "warning" as const, message: "โปรดระบุน้ำหนักบรรจุภัณฑ์" };
      }
    } else if (!(w > 0 && l > 0 && h > 0 && wt > 0)) {
      return { type: "warning" as const, message: "โปรดระบุขนาดและน้ำหนักบรรจุภัณฑ์" };
    }

    const sumDim = w + l + h;
    if (sumDim > 1000) {
      return { type: "warning" as const, message: "กว้างยาวสูงรวมกันต้องไม่เกิน 1,000 ซ.ม." };
    }

    // ✅ thaipostOnly = ไม่มี merchant Speed เลย (ไม่เกี่ยวกับ COD อีกต่อไป, สอดคล้อง canSearchSpeedShipping)
    const thaipostOnly = !hasMerchantSpeed && hasMerchantThaipost;
    if (!thaipostOnly) {
      const origin = normalizeFactoryOrigin();
      if (!isAddressComplete(origin)) {
        return {
          type: "warning" as const,
          message:
            "โปรดให้ที่อยู่โรงงานครบถ้วน (ตำบล อำเภอ จังหวัด รหัสไปรษณีย์) จากข้อมูลร้านค้า/Merchant เริ่มต้น จึงจะกดค้นหาขนส่งได้",
        };
      }
    }

    if (speedConfigLoading) {
      return {
        type: "info" as const,
        message: "กำลังโหลดข้อมูลที่อยู่ปลายทางและการตั้งค่าขนส่ง...",
      };
    }

    const north = buildDestination("north");
    const south = buildDestination("south");

    if (!isAddressComplete(north) || !isAddressComplete(south)) {
      return {
        type: "warning" as const,
        message:
          "โปรดให้ที่อยู่ปลายทางเหนือและใต้ครบในระบบ (ตั้งค่าขนส่ง) จึงจะกดค้นหาขนส่งได้ — หากไม่มีข้อมูล ติดต่อผู้ดูแลระบบ",
      };
    }

    if (speedLoading) return null;

    if (!speedHasSearched && canSearchSpeedShipping) {
      return {
        type: "info" as const,
        message: "คลิกปุ่มค้นหาบริษัทขนส่ง เพื่อแสดงค่าขนส่งที่แนะนำ",
      };
    }

    if (
      speedHasSearched &&
      !speedLoading &&
      !speedError &&
      !speedTimeout &&
      speedItems.length > 0 &&
      !selectedSpeedCarrierCode
    ) {
      return { type: "info" as const, message: "เลือกบริษัทขนส่ง" };
    }

    return null;
  }, [
    isSelfShipping,
    speedStaleHint,
    selectedParentPackagingTypeId,
    showChildDropdown,
    watchedPackagingTypeId,
    hideDimensionFields,
    watchedWidth,
    watchedLength,
    watchedHeight,
    watchedWeight,
    watchedIsCod,
    hasMerchantSpeed,
    hasMerchantThaipost,
    speedLoading,
    speedConfigLoading,
    speedHasSearched,
    canSearchSpeedShipping,
    speedItems.length,
    selectedSpeedCarrierCode,
    speedError,
    speedTimeout,
    factoryData,
    userContact,
    speedShippingConfig,
  ]);

  // ✅ Strict UX (no message): ไฮไลท์ "ช่องที่เปลี่ยน" เป็นสีแดง และบล็อกการบันทึกจนกว่าจะค้นหาใหม่
  const dimErrorFlags = useMemo(() => {
    const shippingMethod = (watchedShippingMethod ?? "self") as string;
    if (shippingMethod !== "bangbow") {
      return { width: false, length: false, height: false, weight: false };
    }

    // ✅ T1: ถ้า timeout ของระบบขนส่ง → ไม่ highlight มิติ และไม่ block submit
    if (speedTimeout) {
      return { width: false, length: false, height: false, weight: false };
    }

    const w = Number(watchedWidth ?? 0);
    const l = Number(watchedLength ?? 0);
    const h = Number(watchedHeight ?? 0);
    const wt = Number(watchedWeight ?? 0);

    const flags = {
      width: w <= 0,
      length: l <= 0,
      height: h <= 0,
      weight: wt <= 0,
    };

    const dimensionsOk = w > 0 && l > 0 && h > 0 && wt > 0;
    if (!dimensionsOk) return flags;

    // ระหว่างโหลด: กันบันทึก (ไม่โชว์ข้อความ)
    if (speedLoading) {
      return { width: true, length: true, height: true, weight: true };
    }

    // ยังไม่เคยกดค้นหาเลย: ต้องกดค้นหาก่อนบันทึก → ไฮไลท์มิติทั้งหมด
    if (!speedHasSearched) {
      return { width: true, length: true, height: true, weight: true };
    }

    // ค้นหาแล้วแต่ไม่พบรายการ: ยังบันทึกไม่ได้
    if (speedHasSearched && speedItems.length === 0) {
      return { width: true, length: true, height: true, weight: true };
    }

    // มีการเปลี่ยนมิติหลังค้นหาแล้ว: ไฮไลท์เฉพาะช่องที่เปลี่ยนจริง
    if (speedStaleHint) {
      const last = lastSearchedDimsRef.current;
      if (!last) {
        return { width: true, length: true, height: true, weight: true };
      }
      return {
        width: w !== last.w,
        length: l !== last.l,
        height: h !== last.h,
        weight: wt !== last.wt,
      };
    }

    return { width: false, length: false, height: false, weight: false };
  }, [
    watchedShippingMethod,
    watchedWidth,
    watchedLength,
    watchedHeight,
    watchedWeight,
    speedLoading,
    speedHasSearched,
    speedStaleHint,
    speedItems.length,
    speedTimeout,
  ]);

  // ✅ helper: กรอบแดงเฉพาะ field ที่ผิด/เปลี่ยน โดยไม่ใช้ validateStatus (กันช่องว่างของ Form.Item)
  const withDimErrorStyle = (base: React.CSSProperties, hasError: boolean): React.CSSProperties => {
    if (!hasError) return base;
    return {
      ...base,
      borderColor: "#ff4d4f",
      boxShadow: "0 0 0 2px rgba(255, 77, 79, 0.2)",
    };
  };

  // ✅ สีปุ่มตาม theme ที่ต้องการ (เหลือง)
  const speedSearchButtonStyle: React.CSSProperties = {
    background: "#faad14",
    borderColor: "#faad14",
    color: "#1f1f1f",
    fontWeight: 500,
    fontSize: 12,
  };

  // ✅ sync selected card จากค่าที่มีอยู่ใน form (edit/copy จะไม่ override อัตโนมัติ)
  useEffect(() => {
    // ✅ [requirement 2026-10-15] ถ้าเพิ่งกดปุ่ม "ตรวจสอบราคาและขนส่ง" เองแล้วเคลียร์ค่าทิ้ง
    //    ห้าม rehydrate กลับจาก databaseTransportCarrierCodeRef (ค่าขนส่งเดิมก่อนเคลียร์) เด็ดขาด
    //    ไม่งั้น selectedSpeedCarrierCode (และ logo ที่ผูกกับมัน) จะโผล่กลับมาเองทันทีหลังเคลียร์
    if (manualTransportClearedRef.current) return;
    const existing =
      (form.getFieldValue(["sku_items", name, "api_transport_carrier_code"]) as string | undefined) ??
      databaseTransportCarrierCodeRef.current ??
      null;
    if (existing && !selectedSpeedCarrierCode) {
      // ✅ normalize legacy partner code → brand code
      setSelectedSpeedCarrierCode(existing === "THAILAND_POST" ? "THAIPOST" : existing);
    }
  }, [form, name, selectedSpeedCarrierCode]);

  // ✅ requirement: edit mode ถ้ามี api_transport_* (carrier_code) ให้ยิง check-price เพื่อดึงราคาล่าสุดด้วย
  // - ยิง 1 ครั้งอัตโนมัติเมื่อพร้อม (bangbow + มิติครบ + ที่อยู่ครบ)
  // - ไม่ override transport_price อัตโนมัติ (policy เดิมยังคงอยู่)
  // (editAutoSearchOnceRef ประกาศไว้ด้านบนกับ refs อื่นแล้ว)
  useEffect(() => {
    if (!editMode && !copyMode) return;

    const shippingMethod = (watchedShippingMethod ?? "self") as string;
    if (shippingMethod !== "bangbow") {
      // ถ้าสลับออกจาก bangbow แล้วกลับมาใหม่ ให้สามารถ auto-search ได้อีกครั้ง
      editAutoSearchOnceRef.current = false;
      return;
    }

    const existingCarrier =
      (form.getFieldValue(["sku_items", name, "api_transport_carrier_code"]) as string | undefined) ??
      (codUserToggledFromDbRef.current ? null : databaseTransportCarrierCodeRef.current) ??
      null;

    if (!existingCarrier) return;
    if (!canSearchSpeedShipping) return;
    if (speedLoading) return;
    if (speedItems.length) return;
    if (editAutoSearchOnceRef.current) return;

    editAutoSearchOnceRef.current = true;
    setSpeedHasSearched(true);
    setSpeedSearchTrigger((x) => x + 1);
  }, [
    editMode,
    copyMode,
    watchedShippingMethod,
    canSearchSpeedShipping,
    speedLoading,
    speedItems.length,
    form,
    name,
  ]);

  // ✅ Trigger call เมื่อกรอก width/length/height/weight ครบ + shipping_method = bangbow
  useEffect(() => {
    const shippingMethod = (watchedShippingMethod ?? "self") as string;
    if (shippingMethod !== "bangbow") {
      setSpeedItems([]);
      setSpeedError(null);
      setSpeedLoading(false);
      setSpeedStaleHint(null);
      setSpeedHasSearched(false);
      return;
    }

    // ✅ requirement ใหม่: ยิง check-price "เฉพาะเมื่อกดปุ่มค้นหา"
    if (speedSearchTrigger === lastProcessedSpeedSearchTriggerRef.current) return;
    lastProcessedSpeedSearchTriggerRef.current = speedSearchTrigger;
    setSpeedStaleHint(null);

    // ✅ capture ว่า trigger รอบนี้มาจากการกดปุ่มเองหรือไม่ แล้ว reset ทันทีให้มีผลแค่รอบนี้รอบเดียว
    //    (auto-search ตอนเปิดหน้า edit mode ไม่เคยตั้ง flag นี้ → isManualSearch จะเป็น false เสมอ
    //    สำหรับ trigger นั้น และยังคง auto-match/auto-fill carrier ที่เคยบันทึกไว้เหมือนเดิม)
    const isManualSearch = manualSearchTriggeredRef.current;
    manualSearchTriggeredRef.current = false;

    const w = Number(watchedWidth ?? 0);
    const l = Number(watchedLength ?? 0);
    const h = Number(watchedHeight ?? 0);
    const wt = Number(watchedWeight ?? 0);
    const dimensionsOk = w > 0 && l > 0 && h > 0 && wt > 0;
    if (!dimensionsOk) {
      setSpeedError("กรุณากรอก กว้าง/ยาว/สูง/น้ำหนัก ให้ครบก่อนค้นหาบริษัทขนส่ง");
      return;
    }

    const isCod = watchedIsCod === true;
    const thaipostOnly = !hasMerchantSpeed && hasMerchantThaipost;
    // ไม่มี merchant เลย → ปุ่มถูก disabled อยู่แล้ว ไม่ควรถึงจุดนี้
    if (!hasMerchantSpeed && !hasMerchantThaipost) return;

    const origin = normalizeFactoryOrigin();
    const north = buildDestination("north");
    const south = buildDestination("south");

    // ✅ ไม่ force ไปรษณีย์ไทยอย่างเดียวเมื่อ isCod=true อีกต่อไป (เดิม: isCod || thaipostOnly)
    //    ถ้ามี merchant Speed อยู่ ให้ค้นหาผ่าน Speed ด้วย แล้วค่อยกรองเหลือเฉพาะ carrier ที่เปิด is_cod
    //    ไว้ตอน merge ผลลัพธ์ (ดูจุด isCod filter ด้านล่าง) — thaipostOnly ยังคงไว้สำหรับเคสไม่มี merchant Speed เลย
    const useThaiPostBranch = thaipostOnly;
    if (!useThaiPostBranch) {
      if (!isAddressComplete(origin)) {
        setSpeedError("ยังไม่มีข้อมูลที่อยู่โรงงาน (ตำบล/อำเภอ/จังหวัด/ไปรษณีย์) สำหรับคำนวณค่าขนส่ง");
        return;
      }
      if (!isAddressComplete(north) || !isAddressComplete(south)) {
        setSpeedError("ยังไม่ได้ตั้งค่า destination (north/south) ใน master_bangbow สำหรับคำนวณค่าขนส่ง");
        return;
      }
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setSpeedLoading(true);
      setSpeedError(null);
      try {
        if (useThaiPostBranch) {
          // ThaiPost branch: COD mode หรือ ThaiPost-only (ไม่มี Speed merchant)
          const tpResult = await checkThailandPostPrice({
              factory_id: targetFactoryId || "",
              weight: wt,
              destination_postcode_north: north.postcode,
              destination_postcode_south: south.postcode,
            });
          if (cancelled) return;

          const tpBrandCode = tpResult.brand_code ?? "THAIPOST";
          const tpBrandId = tpResult.brand_id ?? "";
          // backward compat — SKU เก่าใน DB อาจเก็บ "THAILAND_POST" (partner code)
          const isThaiPostCarrier = (code: string | null | undefined) =>
            code === tpBrandCode || code === "THAILAND_POST";

          // เลือก zone ที่แพงกว่า (north vs south) — ใช้ชุดราคาชื่อตรง DB จาก BE
          const northBd = pickTransportBreakdown(tpResult.north);
          const southBd = pickTransportBreakdown(tpResult.south);
          const useSouth =
            (southBd.transport_price || 0) > (northBd.transport_price || 0);
          const chosenBd = useSouth ? southBd : northBd;
          const tpItem = {
            carrier: tpBrandCode,
            carrier_code: tpBrandCode,
            logo: tpResult.brand_logo ?? undefined,
            delivery_time: "",
            total: chosenBd.api_transport_price,
            api_transport_price: chosenBd.api_transport_price,
            add_on_api_transport_price: chosenBd.add_on_api_transport_price,
            transport_price: chosenBd.transport_price,
            // alias
            add_on_price: chosenBd.add_on_api_transport_price,
            total_with_add_on: chosenBd.transport_price,
            name: "ไปรษณีย์ไทย",
            minimum_time: 0,
            maximum_time: 0,
            is_pickup: 0,
            raw: { partner_id: tpResult.partner_transport_id ?? "", brand_id: tpBrandId, carrier_code: tpBrandCode },
          };

          setSpeedItems([tpItem]);
          setSpeedTimeout(false);
          lastSearchedDimsRef.current = { w, l, h, wt };

          const existingCarrier =
            (form.getFieldValue(["sku_items", name, "api_transport_carrier_code"]) as string | undefined) ??
            (codUserToggledFromDbRef.current ? null : databaseTransportCarrierCodeRef.current) ??
            null;

          // ✅ โลโก้ไปรษณีย์ไทยจาก API — ต้องเขียนลง form เพื่อให้บันทึกเข้า product_sku_price.api_transport_logo
          //    (เดิม set เป็น null ทั้งที่ UI แสดง logo จาก selectedSpeedItem ทำให้ create/COD บันทึกไม่ติด)
          const tpLogo = normalizeSpeedLogo(tpResult.brand_logo) ?? tpResult.brand_logo ?? null;

          const applyTpBreakdownToForm = (includeTransportAndRecalc: boolean) => {
            form.setFieldValue(["sku_items", name, "api_transport_price"], chosenBd.api_transport_price);
            form.setFieldValue(
              ["sku_items", name, "add_on_api_transport_price"],
              chosenBd.add_on_api_transport_price,
            );
            if (includeTransportAndRecalc) {
              form.setFieldValue(["sku_items", name, "transport_price"], chosenBd.transport_price);
              handleFormChange("transport_price", chosenBd.transport_price);
            }
          };

          if (editMode || copyMode) {
            if (isManualSearch) {
              // ✅ กดปุ่ม "ตรวจสอบราคาและขนส่ง" เอง → เคลียร์ค่าขนส่งเดิมทิ้งหมด (ราคา+แบรนด์)
              //    ไม่ auto-match/auto-fill ให้ บังคับให้เลือกจากรายการเองก่อนถึงจะบันทึกได้
              setSelectedSpeedCarrierCode(null);
              form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], null);
              form.setFieldValue(["sku_items", name, "api_transport_logo"], null);
              form.setFieldValue(["sku_items", name, "api_transport_price"], 0);
              form.setFieldValue(["sku_items", name, "add_on_api_transport_price"], 0);
              form.setFieldValue(["sku_items", name, "transport_price"], 0);
              form.setFieldValue(["sku_items", name, "partner_id"], "");
              form.setFieldValue(["sku_items", name, "brand_id"], "");
              setSpeedNotice("กรุณาเลือกขนส่งจากรายการด้านล่างอีกครั้ง");
            } else if (isThaiPostCarrier(existingCarrier)) {
              // มีขนส่งเดิมอยู่แล้ว → update ราคา API + normalize carrier code + sync logo จาก API
              setSelectedSpeedCarrierCode(tpBrandCode);
              applyTpBreakdownToForm(false);
              // ✅ เขียน brand code ใหม่ทับ legacy THAILAND_POST ใน form
              form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], tpBrandCode);
              form.setFieldValue(["sku_items", name, "partner_id"], tpResult.partner_transport_id ?? "");
              form.setFieldValue(["sku_items", name, "brand_id"], tpBrandId);
              if (tpLogo) {
                form.setFieldValue(["sku_items", name, "api_transport_logo"], tpLogo);
              }
              setSpeedNotice(null);
            } else if (!existingCarrier) {
              // ไม่มีขนส่งเดิม (เช่น หลัง toggle COD) → auto-select เหมือน create mode
              setSelectedSpeedCarrierCode(tpBrandCode);
              form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], tpBrandCode);
              form.setFieldValue(["sku_items", name, "api_transport_logo"], tpLogo);
              form.setFieldValue(["sku_items", name, "partner_id"], tpResult.partner_transport_id ?? "");
              form.setFieldValue(["sku_items", name, "brand_id"], tpBrandId);
              applyTpBreakdownToForm(true);
              setSpeedNotice(null);
            } else {
              // มีขนส่งเดิมที่ไม่ใช่ไปรษณีย์ไทย (edge case) → แจ้งให้เลือกใหม่
              setSelectedSpeedCarrierCode(null);
              setSpeedNotice("โหมด COD ต้องใช้ไปรษณีย์ไทยเท่านั้น (กรุณาเลือกไปรษณีย์ไทย)");
            }
          } else if (isManualSearch) {
            // ✅ create mode + กดปุ่มเอง → ไม่ auto-fill ไปรษณีย์ไทยให้อีกต่อไป บังคับให้กดเลือกเอง
            setSelectedSpeedCarrierCode(null);
            form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], null);
            form.setFieldValue(["sku_items", name, "api_transport_logo"], null);
            form.setFieldValue(["sku_items", name, "api_transport_price"], 0);
            form.setFieldValue(["sku_items", name, "add_on_api_transport_price"], 0);
            form.setFieldValue(["sku_items", name, "transport_price"], 0);
            form.setFieldValue(["sku_items", name, "partner_id"], "");
            form.setFieldValue(["sku_items", name, "brand_id"], "");
            setSpeedNotice("กรุณาเลือกขนส่งจากรายการด้านล่าง");
          } else {
            setSelectedSpeedCarrierCode(tpBrandCode);
            form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], tpBrandCode);
            form.setFieldValue(["sku_items", name, "api_transport_logo"], tpLogo);
            form.setFieldValue(["sku_items", name, "partner_id"], tpResult.partner_transport_id ?? "");
            form.setFieldValue(["sku_items", name, "brand_id"], tpBrandId);
            applyTpBreakdownToForm(true);
            setSpeedNotice(null);
          }
        } else {
          // Non-COD mode: Speed API + Thailand Post
          const payloadBase = {
            box_width: w,
            box_height: h,
            box_length: l,
            box_weight: wt,
            carrier_code: "ALL",
            origin,
            cod_amount: 0,
          };

          const [r1, r2, tpResult] = await Promise.all([
            checkSpeedShippingPrice({ ...payloadBase, destination: north }),
            checkSpeedShippingPrice({ ...payloadBase, destination: south }),
            // ยิง ThaiPost เฉพาะเมื่อมี merchant ไปรษณีย์ไทย
            hasMerchantThaipost
              ? checkThailandPostPrice({
                  factory_id: targetFactoryId || "",
                  weight: wt,
                  destination_postcode_north: north.postcode,
                  destination_postcode_south: south.postcode,
                }).catch(() => null)
              : Promise.resolve(null),
          ]);

          if (cancelled) return;
          const list1: CheckPriceCarrierItem[] = Array.isArray((r1 as any)?.data) ? (r1 as any).data : [];
          const list2: CheckPriceCarrierItem[] = Array.isArray((r2 as any)?.data) ? (r2 as any).data : [];

          let merged: any[] = mergeSpeedItems(list1, list2)
            .map((raw) => {
              const bd = pickTransportBreakdown(raw);
              return {
                carrier: String(raw?.carrier ?? raw?.carrier_code ?? ""),
                carrier_code: String(raw?.carrier_code ?? ""),
                logo: normalizeSpeedLogo(raw?.logo),
                delivery_time: raw?.delivery_time ? String(raw.delivery_time) : "",
                total: bd.api_transport_price,
                api_transport_price: bd.api_transport_price,
                add_on_api_transport_price: bd.add_on_api_transport_price,
                transport_price: bd.transport_price,
                // alias
                add_on_price: bd.add_on_api_transport_price,
                total_with_add_on: bd.transport_price,
                name: raw?.name ?? "",
                minimum_time: raw?.minimum_time ?? 0,
                maximum_time: raw?.maximum_time ?? 0,
                is_pickup: raw?.is_pickup ?? 0,
                raw,
              };
            })
            .filter((x) => x.carrier_code);

          // Append Thailand Post item alongside Speed carriers
          if (tpResult) {
            const tpBrandCode = tpResult.brand_code ?? "THAIPOST";
            const tpBrandId = tpResult.brand_id ?? "";
            const northBd = pickTransportBreakdown(tpResult.north);
            const southBd = pickTransportBreakdown(tpResult.south);
            const useSouth =
              (southBd.transport_price || 0) > (northBd.transport_price || 0);
            const chosenBd = useSouth ? southBd : northBd;
            merged.push({
              carrier: tpBrandCode,
              carrier_code: tpBrandCode,
              logo: normalizeSpeedLogo(tpResult.brand_logo) ?? tpResult.brand_logo ?? undefined,
              delivery_time: "",
              total: chosenBd.api_transport_price,
              api_transport_price: chosenBd.api_transport_price,
              add_on_api_transport_price: chosenBd.add_on_api_transport_price,
              transport_price: chosenBd.transport_price,
              add_on_price: chosenBd.add_on_api_transport_price,
              total_with_add_on: chosenBd.transport_price,
              name: "ไปรษณีย์ไทย",
              minimum_time: 0,
              maximum_time: 0,
              is_pickup: 0,
              raw: { partner_id: tpResult.partner_transport_id ?? "", brand_id: tpBrandId, carrier_code: tpBrandCode },
            });
          }

          // ✅ 2.2: เมื่อเลือก COD ให้เหลือเฉพาะ carrier (Speed หรือไปรษณีย์ไทย) ที่เปิด is_cod ไว้จริง
          //    แทนที่การ force ไปรษณีย์ไทยอย่างเดียวแบบเดิม — ไม่กรองถ้าไม่ใช่โหมด COD
          if (isCod) {
            merged = merged.filter((item) => isCarrierCodCapable(item.carrier_code));
          }

          merged.sort((x, y) => {
            const xTotal = x.transport_price ?? x.total_with_add_on ?? x.total;
            const yTotal = y.transport_price ?? y.total_with_add_on ?? y.total;
            if (xTotal !== yTotal) return xTotal - yTotal;
            return parseDeliveryMinDays(x.delivery_time) - parseDeliveryMinDays(y.delivery_time);
          });

          setSpeedItems(merged);
          setSpeedTimeout(false);
          lastSearchedDimsRef.current = { w, l, h, wt };

          const existingCarrier =
            (form.getFieldValue(["sku_items", name, "api_transport_carrier_code"]) as string | undefined) ??
            (codUserToggledFromDbRef.current ? null : databaseTransportCarrierCodeRef.current) ??
            null;

          const applySpeedItemBreakdown = (
            item: (typeof merged)[number],
            includeTransportAndRecalc: boolean,
          ) => {
            const bd = pickTransportBreakdown(item);
            form.setFieldValue(["sku_items", name, "api_transport_price"], bd.api_transport_price);
            form.setFieldValue(
              ["sku_items", name, "add_on_api_transport_price"],
              bd.add_on_api_transport_price,
            );
            if (includeTransportAndRecalc) {
              form.setFieldValue(["sku_items", name, "transport_price"], bd.transport_price);
              handleFormChange("transport_price", bd.transport_price);
            }
            return bd;
          };

          if (editMode || copyMode) {
            if (isManualSearch) {
              // ✅ กดปุ่ม "ตรวจสอบราคาและขนส่ง" เอง → เคลียร์ค่าขนส่งเดิมทิ้งหมด (ราคา+แบรนด์)
              //    ไม่ auto-match/auto-fill ให้ บังคับให้เลือกจากรายการเองก่อนถึงจะบันทึกได้
              setSelectedSpeedCarrierCode(null);
              form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], null);
              form.setFieldValue(["sku_items", name, "api_transport_logo"], null);
              form.setFieldValue(["sku_items", name, "api_transport_price"], 0);
              form.setFieldValue(["sku_items", name, "add_on_api_transport_price"], 0);
              form.setFieldValue(["sku_items", name, "transport_price"], 0);
              form.setFieldValue(["sku_items", name, "partner_id"], "");
              form.setFieldValue(["sku_items", name, "brand_id"], "");
              setSpeedNotice("กรุณาเลือกขนส่งจากรายการด้านล่างอีกครั้ง");
            } else if (existingCarrier) {
              const currentSaved = Number(form.getFieldValue(["sku_items", name, "transport_price"]) ?? 0);
              // backward compat — SKU เก่าอาจเก็บ "THAILAND_POST" แต่ item ใหม่ใช้ brand_code ("THAIPOST")
              const found = merged.find((x) =>
                x.carrier_code === existingCarrier ||
                (existingCarrier === "THAILAND_POST" && (x.carrier_code === "THAIPOST" || x.name === "ไปรษณีย์ไทย")),
              );
              if (!found) {
                setSpeedNotice("ไม่พบขนส่งที่เคยเลือกในรายการล่าสุดจากระบบขนส่ง (กรุณาเลือกใหม่)");
                setSelectedSpeedCarrierCode(null);
              } else {
                // ✅ ใช้ carrier_code จากผลค้นหา (normalize THAILAND_POST → THAIPOST) เพื่อให้การ์ดไฮไลต์ตรง
                setSelectedSpeedCarrierCode(found.carrier_code);
                form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], found.carrier_code);
                const bd = applySpeedItemBreakdown(found, false);
                form.setFieldValue(["sku_items", name, "api_transport_logo"], normalizeSpeedLogo(found.logo) ?? null);
                form.setFieldValue(["sku_items", name, "partner_id"], found.raw?.partner_id ?? "");
                form.setFieldValue(["sku_items", name, "brand_id"], found.raw?.brand_id ?? "");
                if (bd.transport_price !== currentSaved) {
                  setSpeedNotice(
                    `ราคาขนส่งจากระบบล่าสุด (${formatWithCommas(bd.transport_price)}) ไม่ตรงกับค่าที่บันทึกไว้ (${formatWithCommas(currentSaved)})`,
                  );
                } else {
                  setSpeedNotice(null);
                }
              }
            } else {
              // ไม่มีขนส่งที่เคยบันทึกไว้ (เช่น หลัง toggle COD) → เลือกอันดับ 1 อัตโนมัติเหมือน create mode
              setSpeedNotice(null);
              const top = merged[0];
              if (top) {
                setSelectedSpeedCarrierCode(top.carrier_code);
                form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], top.carrier_code);
                form.setFieldValue(["sku_items", name, "api_transport_logo"], normalizeSpeedLogo(top.logo) ?? null);
                form.setFieldValue(["sku_items", name, "partner_id"], top.raw?.partner_id ?? "");
                form.setFieldValue(["sku_items", name, "brand_id"], top.raw?.brand_id ?? "");
                applySpeedItemBreakdown(top, true);
              }
            }
          } else if (isManualSearch) {
            // ✅ create mode + กดปุ่มเอง → ไม่ auto-select ตัวที่ถูกที่สุดให้อีกต่อไป บังคับให้เลือกเอง
            setSelectedSpeedCarrierCode(null);
            form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], null);
            form.setFieldValue(["sku_items", name, "api_transport_logo"], null);
            form.setFieldValue(["sku_items", name, "api_transport_price"], 0);
            form.setFieldValue(["sku_items", name, "add_on_api_transport_price"], 0);
            form.setFieldValue(["sku_items", name, "transport_price"], 0);
            form.setFieldValue(["sku_items", name, "partner_id"], "");
            form.setFieldValue(["sku_items", name, "brand_id"], "");
            setSpeedNotice("กรุณาเลือกขนส่งจากรายการด้านล่าง");
          } else {
            setSpeedNotice(null);
            const top = merged[0];
            if (top) {
              setSelectedSpeedCarrierCode(top.carrier_code);
              form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], top.carrier_code);
              form.setFieldValue(["sku_items", name, "api_transport_logo"], normalizeSpeedLogo(top.logo) ?? null);
              form.setFieldValue(["sku_items", name, "partner_id"], top.raw?.partner_id ?? "");
              form.setFieldValue(["sku_items", name, "brand_id"], top.raw?.brand_id ?? "");
              applySpeedItemBreakdown(top, true);
            }
          }
        }
      } catch (e: any) {
        if (cancelled) return;
        const rawMsg = e?.message ? String(e.message) : "";
        const isTimeout =
          e?.code === "ECONNABORTED" ||
          /\btimeout\b/i.test(rawMsg) ||
          /\bexceeded\b/i.test(rawMsg);

        // ✅ เฉพาะฟีเจอร์ค่าขนส่ง: ปรับข้อความ timeout ให้เป็นข้อความที่ต้องการ
        if (isTimeout) {
          setSpeedTimeout(true);
          setSpeedError(
            "ขออภัยในความไม่สะดวก เนื่องจากการเชื่อมต่อกับระบบการขนส่งปลายทางเกิดการขัดข้องชั่วคราว",
          );
        } else {
          setSpeedTimeout(false);
          setSpeedError(rawMsg || "โหลดค่าขนส่งจากระบบขนส่งไม่สำเร็จ");
        }
        setSpeedItems([]);
        setSpeedNotice(null);
      } finally {
        if (!cancelled) setSpeedLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [watchedShippingMethod, watchedIsCod, watchedWidth, watchedLength, watchedHeight, watchedWeight, form, name, editMode, speedSearchTrigger]);

  // ✅ G1: ถ้าผลลัพธ์เคยถูกค้นหาแล้ว แต่ผู้ใช้เปลี่ยนมิติ ให้เตือนให้กดค้นหาใหม่ (ไม่ล้างรายการเดิม)
  useEffect(() => {
    const shippingMethod = (watchedShippingMethod ?? "self") as string;
    if (shippingMethod !== "bangbow") {
      setSpeedStaleHint(null);
      return;
    }
    const last = lastSearchedDimsRef.current;
    if (!last) return;
    if (!speedItems.length) return;
    if (speedLoading) return;

    const w = Number(watchedWidth ?? 0);
    const l = Number(watchedLength ?? 0);
    const h = Number(watchedHeight ?? 0);
    const wt = Number(watchedWeight ?? 0);
    const changed = w !== last.w || l !== last.l || h !== last.h || wt !== last.wt;
    if (changed) {
      setSpeedStaleHint("มีการเปลี่ยนขนาดพัสดุ กรุณากดค้นหาบริษัทขนส่งอีกครั้ง");
    } else {
      setSpeedStaleHint(null);
    }
  }, [watchedShippingMethod, watchedWidth, watchedLength, watchedHeight, watchedWeight, speedItems.length, speedLoading]);

  const isShippingFirstRender = useRef(true);

  /**
   * เมื่อเปลี่ยน shipping_method: ถ้าสลับ bangbow → self ให้ล้างฟิลด์ Partner ใน Form;
   * ถ้า self → bangbow ให้ restore baseline ตามเดิม; จากนั้น trigger คำนวณราคาใหม่ (นับ/ไม่นับ S)
   */
  useEffect(() => {
    const nextShippingMethod = (watchedShippingMethod ?? "self") as string;
    const prevShippingMethod = (prevShippingMethodRef.current ?? null) as string | null;

    // ✅ ป้องกันไม่ให้รัน clear/restore ในตอน component เพิ่งถูก mount (edit/copy mode)
    if (isShippingFirstRender.current) {
      isShippingFirstRender.current = false;
      prevShippingMethodRef.current = nextShippingMethod;
      return;
    }

    // ✅ สลับจาก bangbow → self: ล้างฟิลด์/สถานะที่ผูกกับ Partner แบ่งเบาใน Form (DB ให้ payload + backend ช่วย normalize)
    if (nextShippingMethod === "self" && prevShippingMethod === "bangbow") {
      // ✅ snapshot บรรจุภัณฑ์ก่อนล้าง — เพื่อ restore ตอนกลับ bangbow (รวม create mode)
      const currentPackaging = form.getFieldValue(["sku_items", name, "packaging_type_id"]);
      const currentParentPackaging = form.getFieldValue(["sku_items", name, "_parent_packaging_type_id"]);
      if (currentPackaging) {
        databasePackagingTypeIdRef.current = currentPackaging;
      }
      if (currentParentPackaging) {
        databaseParentPackagingTypeIdRef.current = currentParentPackaging;
      }
      form.setFieldValue(["sku_items", name, "transport_price"], 0);
      form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], null);
      form.setFieldValue(["sku_items", name, "api_transport_logo"], null);
      form.setFieldValue(["sku_items", name, "api_transport_price"], 0);
      form.setFieldValue(["sku_items", name, "add_on_api_transport_price"], 0);
      form.setFieldValue(["sku_items", name, "partner_id"], "");
      form.setFieldValue(["sku_items", name, "brand_id"], "");
      form.setFieldValue(["sku_items", name, "packaging_type_id"], null);
      form.setFieldValue(["sku_items", name, "_parent_packaging_type_id"], null);
      // ✅ ส่งเอง → ปิด COD (ใช้ได้เฉพาะ Partner แบ่งเบา)
      form.setFieldValue(["sku_items", name, "is_cod"], false);
      setSpeedItems([]);
      setSpeedError(null);
      setSpeedTimeout(false);
      setSpeedStaleHint(null);
      setSpeedNotice(null);
      setSpeedHasSearched(false);
      setSelectedSpeedCarrierCode(null);
      setManualBangbowTransportUnlocked(false);
      lastSearchedDimsRef.current = null;
    } else if (nextShippingMethod === "bangbow" && prevShippingMethod === "self") {
      // ✅ set merchants และ merchant_id จาก default Speed merchant เมื่อสลับเป็น bangbow (จาก factoryData)
      const currentMerchants = form.getFieldValue(["sku_items", name, "merchants"]) as string[] | undefined;
      if (!currentMerchants || currentMerchants.length === 0) {
        const defaultCodes = getDefaultMerchantCodes();
        const defaultMerchant = factoryData?.factory?.merchants?.speed?.find((m: { is_default: boolean }) => m.is_default);
        form.setFieldValue(["sku_items", name, "merchants"], defaultCodes);
        form.setFieldValue(["sku_items", name, "merchant_id"], defaultMerchant?.merchant_id ?? "");
      }
      // ✅ restore ค่าขนส่งเดิม (baseline) เมื่อสลับกลับไป "ส่งกับแบ่งเบา"
      // หมายเหตุ: ใช้ baseline ที่ snapshot ไว้ (คล้าย c1-c3) เพื่อให้สอดคล้องกับ behavior เดิมที่เคยกำหนด
      const restoreTransport = databaseTransportPriceRef.current ?? 0;
      const latest = form.getFieldValue(["sku_items", name, "transport_price"]);
      if (latest === 0 || latest === null || latest === undefined) {
        form.setFieldValue(["sku_items", name, "transport_price"], restoreTransport);
      }
      const restoreCarrier = databaseTransportCarrierCodeRef.current ?? null;
      const restoreLogo = databaseTransportLogoRef.current ?? null;
      const restoreApiPrice = databaseApiTransportPriceRef.current ?? 0;
      const restoreAddOnApiPrice = databaseAddOnApiTransportPriceRef.current ?? 0;
      const restorePartnerId = databasePartnerIdRef.current ?? "";
      const restoreBrandId = databaseBrandIdRef.current ?? "";
      const latestCarrier = form.getFieldValue(["sku_items", name, "api_transport_carrier_code"]);
      if (!latestCarrier && restoreCarrier) {
        // ✅ normalize legacy THAILAND_POST → THAIPOST เพื่อให้การ์ดไฮไลต์ตรง
        const restoreSelectCode =
          restoreCarrier === "THAILAND_POST" ? "THAIPOST" : restoreCarrier;
        form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], restoreSelectCode);
        form.setFieldValue(["sku_items", name, "api_transport_logo"], restoreLogo);
        form.setFieldValue(["sku_items", name, "api_transport_price"], restoreApiPrice);
        form.setFieldValue(["sku_items", name, "add_on_api_transport_price"], restoreAddOnApiPrice);
        form.setFieldValue(["sku_items", name, "partner_id"], restorePartnerId);
        form.setFieldValue(["sku_items", name, "brand_id"], restoreBrandId);
        setSelectedSpeedCarrierCode(restoreSelectCode);
      }
      // ✅ restore บรรจุภัณฑ์ที่เคยล้างตอนสลับไป self
      const latestPackaging = form.getFieldValue(["sku_items", name, "packaging_type_id"]);
      if (!latestPackaging && databasePackagingTypeIdRef.current) {
        form.setFieldValue(["sku_items", name, "packaging_type_id"], databasePackagingTypeIdRef.current);
        if (databaseParentPackagingTypeIdRef.current) {
          form.setFieldValue(
            ["sku_items", name, "_parent_packaging_type_id"],
            databaseParentPackagingTypeIdRef.current,
          );
        }
      }
    }

    // ✅ Guard: ถ้า shipping method ไม่เปลี่ยน (เช่น useEffect fire จาก factoryData เปลี่ยน)
    //         → ไม่ต้อง trigger recalculation (กัน override retail_price ตอน initial mount หลัง factoryData load)
    const shippingMethodChanged = prevShippingMethod !== nextShippingMethod;

    prevShippingMethodRef.current = nextShippingMethod;

    if (!shippingMethodChanged) {
      return;
    }

    // ใช้ setTimeout เพื่อรอให้ค่า shipping_method/transport_price commit ก่อน แล้วค่อย trigger recalculation
    setTimeout(() => {
      const committedTransport = form.getFieldValue(["sku_items", name, "transport_price"]) ?? 0;
      handleFormChange("transport_price", committedTransport);
    }, 0);
  }, [watchedShippingMethod, form, name, factoryData]);

  useEffect(() => {
    if (isSelfShipping) {
      setControlSpeedNotice(false);
    } else {
      setControlSpeedNotice(true);
    }
  }, [isSelfShipping]);

  useEffect(() => {
    if (userProfile) {
      setUserRole(userProfile.user.role.ref_type);
    }
  }, [userProfile]);

  return (
    <FormWrapper>
      <Card className="sku-section-card" style={{ width: "95%", padding: 15 }}>
        <Row gutter={16}>
          {/*รูปภาพ*/}
          <Col className="gutter-row" span={6}>
            {/* // list upload */}
            <Form.Item
              name={[name, "image_id"]}
              rules={[
                {
                  required: !isDraftSave, // ถ้าบันทึกร่าง ไม่ต้อง validate รูปภาพ
                  message: "กรุณาอัพโหลดรูปภาพ",
                },
                {
                  // มีไฟล์ในรายการแต่ยังไม่ได้ id (กำลังอัปโหลด/อัปโหลดไม่สำเร็จ) = ยังไม่มีรูปจริง
                  validator: (_, value) => {
                    const list: any[] = Array.isArray(value) ? value : [];
                    if (isDraftSave || list.length === 0) return Promise.resolve();
                    if (list.some((f) => f?.status === "uploading")) {
                      return Promise.reject(new Error("กรุณารอให้อัปโหลดรูปภาพเสร็จก่อน"));
                    }
                    return list.some((f) => f?.id || f?.response?.id)
                      ? Promise.resolve()
                      : Promise.reject(new Error("อัปโหลดรูปภาพไม่สำเร็จ กรุณาอัปโหลดใหม่"));
                  },
                },
              ]}
            >
              <UploadImages
                maximumUploadItems={1}
                allowFileExtensions={allowFileExtensionsImage}
                skuImage={skuImage}
                setSkuImage={setSkuImage}
              />
            </Form.Item>
            {/* ซ่อนการแสดงชื่อสินค้าเดิมด้านล่างรูปภาพ */}
            {/* <TextWrapper>
              <Typography.Text style={{ textAlign: "center", marginTop: 10 }}>
                {imgaeName[0]}
              </Typography.Text>
            </TextWrapper> */}
          </Col>
          {/*รหัสสินค้า*/}
          <Col className="gutter-row" span={18}>
            <Form.Item
              label="รหัสสินค้า (SKU)"
              name={[name, "product_code"]}
              className="form-varation-name"
              // rules={[
              //   {
              //     required: true,
              //     message: "กรุณากรอกกำลังการผลิตต่อวัน",
              //   },
              // ]}
              style={{ width: 450, maxWidth: "100%" }}
            >
              <Input
                style={{ marginLeft: 5 }}
                className="form-varation-name__input"
                size="small"
                autoComplete="off"
                showCount
                maxLength={30}
              />
            </Form.Item>

            <Form.Item
              label="ชื่อตัวเลือกสินค้า"
              name={[name, "name"]}
              className="form-varation-name"
              rules={[
                {
                  required: true,
                  message: "กรุณากรอกชื่อสินค้า",
                },
              ]}
              style={{ width: 550, maxWidth: "100%" }}
            >
              <Input
                style={{ marginLeft: 5 }}
                className="form-varation-name__input"
                size="small"
                showCount
                maxLength={75}
              />
            </Form.Item>

            {/* ✅ แสดงชื่อประเภทสินค้าใต้ input ชื่อ SKU (ไม่แสดง %) */}
            {categoryName ? (
              <Typography.Text
                type="secondary"
                style={{ marginLeft: 5, marginTop: -8, marginBottom: 8, display: "block", fontSize: 13 }}
              >
                หมวดหมู่สินค้า : {categoryName}
              </Typography.Text>
            ) : null}

            {/*  ระดับ SKU — is_active=false จะถูกซ่อนจากช่องทางขายและล้างออกจากตะกร้า (ฝั่ง API) */}
            <Form.Item
              label="การวางขาย"
              name={[name, "is_active"]}
              className="form-varation-name"
              initialValue={true}
              style={{ width: 550, marginTop: 10, maxWidth: "100%" }}
            >
              <Radio.Group style={{ marginLeft: 5, whiteSpace: "nowrap" }}>
                <Radio value={true}>วางขาย</Radio>
                <Radio value={false}>หยุดขายชั่วคราว</Radio>
              </Radio.Group>
            </Form.Item>

            {/* ✅ ประเภทการขาย — Exclusive group "sale_channel" จาก master_sku_tag (ขายส่ง/ขายปลีก)
                 - เก็บ tag_id เดียวใน form field "sku_channel_tag_id" (optional — ไม่บังคับเลือก)
                 - payload ส่งเป็น sku_tag_ids: [id] (หรือ [] ถ้าไม่เลือก) → backend sync product_sku_tag_relation */}
            <Form.Item
              label="รูปแบบการขาย"
              name={[name, "sku_channel_tag_id"]}
              className="form-varation-name"
              style={{ width: 550, marginTop: 10, maxWidth: "100%" }}
            >
              <Radio.Group style={{ marginLeft: 5 }}>
                {saleChannelTags.map((tag) => (
                  <Radio key={tag.id} value={tag.id}>
                    {tag.name}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      </Card>
      {/* ✅ Card 0: ราคาตลาด - แสดงเสมอ */}
      <Card className="sku-section-card" style={{ width: "95%", padding: 15, marginTop: 15 }}>
        <Row gutter={16}>
          <Col className="gutter-row" span={16}>
            <Row gutter={[16, 16]}>
              <Col className="gutter-row" span={20}>
                <h4><span style={{ color: "red" }}>* </span>ราคาที่แนะนำให้ผู้ซื้อนำไปขายต่อ:</h4>
              </Col>
            </Row>
            
            {/* ✅ ช่อง input_market_price (ราคาตลาด — ใช้คำนวณ แยกจาก retail_price ด้านล่าง) */}
            <Row gutter={16} style={{ marginTop: 5, marginBottom: 5 }} align="middle">
             {/*  <Col span={8}>
                <Typography.Text>
                  <span style={{ color: "red" }}>* </span>ราคาขายแนะนำ :
                </Typography.Text>
              </Col>*/}
              <Col span={10}>
                {/* ✅ ครอบด้วย shouldUpdate เพื่อสลับสี input ตาม isOverMarket
                     (retail_price "ราคาตลาดที่แสดง" ฝั่งขวา > input_market_price "ราคาตลาด" ฝั่งนี้)
                     - isOverMarket=false (default) → input_market_price ติดสีส้ม (เดิม)
                     - isOverMarket=true           → ย้ายสีส้มไปที่ base_factory_price (ราคาที่โอนให้ผู้ประกอบการ Inc Vat)
                       ฝั่งนี้จึงเป็นสี default (neutral) */}
                <Form.Item
                  shouldUpdate={(prevValues: any, currentValues: any) => {
                    const prevItem = prevValues.sku_items?.[name];
                    const curItem = currentValues.sku_items?.[name];
                    return (
                      prevItem?.retail_price !== curItem?.retail_price ||
                      prevItem?.input_market_price !== curItem?.input_market_price
                    );
                  }}
                  noStyle
                >
                  {({ getFieldValue }: any) => {
                    const retailNum = toFiniteNumberOr0(
                      getFieldValue(["sku_items", name, "retail_price"])
                    );
                    const marketNum = toFiniteNumberOr0(
                      getFieldValue(["sku_items", name, "input_market_price"])
                    );
                    // ✅ เงื่อนไขเดียวกับ block คำเตือน (lines ~4019, ~4155)
                    const isOverMarket = marketNum > 0 && retailNum > marketNum;
                    return (
                      <Form.Item
                        name={[name, "input_market_price"]}
                        style={{ marginBottom: 0 }}
                      >
                        <NumericFormat
                          customInput={Input}
                          style={{
                            width: "100%",
                            padding: "4px 8px",
                            textAlign: "right",
                            // ✅ สลับสี: ถ้า isOverMarket=true ย้ายสีส้มไปที่ base_factory_price
                            //    ฝั่งนี้ (input_market_price) ใช้สี default (neutral)
                            ...(isOverMarket
                              ? {}
                              : {
                                borderColor: "#fa8c16",
                                backgroundColor: "#fff7e6",
                                color: "#fa8c16",
                                fontWeight: 600,
                              }),
                          }}
                          size="small"
                          thousandSeparator=","
                          decimalSeparator="."
                          decimalScale={2}
                          fixedDecimalScale={true}
                          allowNegative={false}
                          min={0}
                          max={999999.99}
                          onFocus={handleFocusClearZeroOrCursorStart("input_market_price")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = event.target?.value;
                            if (value !== undefined && value !== null) {
                              const raw = String(value).replace(/,/g, '');
                              // ✅ [บั๊กฟิกซ์] ถ้าลบจนว่าง ต้อง set เป็น "" (empty string) ห้ามใช้ undefined —
                              //    react-number-format ถือว่า undefined/null คือ "ไม่ controlled แล้ว" แล้วจะ
                              //    ดึงค่าที่จำไว้ภายใน (เดิม) กลับมาแสดงแทน ทำให้เคลียร์ไม่ได้เลย ส่วน "" ใช้ได้ปกติ
                              //    handleFormChange ยังส่ง 0 ไปเหมือนเดิม เพื่อให้ยัง reset ฟิลด์ที่ auto-fill
                              //    จาก market price ได้ตามเดิม (total_commission, base_factory_price,
                              //    factory_price, retail_price, payment_getway, c1-c5)
                              if (raw === "") {
                                form.setFieldValue(["sku_items", name, "input_market_price"], "");
                                handleFormChange("input_market_price", 0);
                                return;
                              }
                              const numValue = parseFloat(raw);
                              const safeValue = Number.isFinite(numValue) ? numValue : 0;
                              form.setFieldValue(["sku_items", name, "input_market_price"], safeValue);
                              handleFormChange("input_market_price", safeValue);
                            }
                          }}
                          onBlur={() => handleBlurRestoreZeroIfEmpty("input_market_price")}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Typography.Text style={{ fontSize: 13 }}>
                  หมายเหตุ : ไม่มีผลต่อการกำหนดราคาขาย
                </Typography.Text>
              </Col>
            </Row>

          </Col>
        </Row>
      </Card>
      {/*✅ Card 1: ราคาตั้งต้น - แสดงเสมอ*/}
      <Card className="sku-section-card" style={{ width: "95%", padding: 15, marginTop: 15 }}>
        <Row gutter={16}>
          <Col className="gutter-row" span={16}>
            <Row gutter={[16, 16]}>
              <Col className="gutter-row" span={20}>
                <h4>1. ราคาส่งสินค้าให้แบ่งเบา รวม VAT (โอนให้ผู้ประกอบการ)</h4>
              </Col>
            </Row>
            {/* ✅ ปรับ layout: label / input อยู่ในแนวเดียวกัน */}
            <Row gutter={16} style={{ marginTop: 5, marginBottom: 5 }} align="middle">
              {/*
              <Col span={14}>
                <Typography.Text>
                  <span style={{ color: "red" }}>*</span>จำนวนเงินที่ผู้ขายได้รับ (รวม VAT) :
                </Typography.Text>
              </Col>
              */}
              <Col span={10}>
                <Form.Item shouldUpdate={(prevValues, currentValues) => {
                  const prevItem = prevValues.sku_items?.[name];
                  const curItem = currentValues.sku_items?.[name];
                  // ✅ เพิ่ม retail_price + input_market_price เป็น dependency
                  //    เพื่อให้ re-render เมื่อเงื่อนไข isOverMarket เปลี่ยน
                  //    แล้วสลับสีส้มเข้า/ออก ช่อง base_factory_price ได้ทันที
                  return prevItem?.base_factory_price !== curItem?.base_factory_price ||
                    prevItem?.total_commission !== curItem?.total_commission ||
                    prevItem?.retail_price !== curItem?.retail_price ||
                    prevItem?.input_market_price !== curItem?.input_market_price;
                }} noStyle>
                  {({ getFieldValue }) => {
                    const formValues = getFieldValue(["sku_items", name]) || {};
                    const basePrice = Number(String(formValues.base_factory_price ?? 0).replace(/,/g, ""));
                    const totalCommission = Number(String(formValues.total_commission ?? 0).replace(/,/g, ""));

                    // ✅ คำนวณ isOverMarket (retail_price "ราคาตลาดที่แสดง" > input_market_price "ราคาตลาด")
                    //    เพื่อย้ายสีส้มมาที่ช่อง base_factory_price (ราคาที่โอนให้ผู้ประกอบการ Inc Vat) เมื่อเงื่อนไขเป็นจริง
                    const retailNum = toFiniteNumberOr0(formValues.retail_price);
                    const marketNum = toFiniteNumberOr0(formValues.input_market_price);
                    const isOverMarket = marketNum > 0 && retailNum > marketNum;

                    return (
                      <Form.Item
                        name={[name, "base_factory_price"]}
                        rules={[
                          {
                            required: !isDraftSave,
                            message: "",
                          },
                          {
                            validator: (_, value) => {
                              if (isDraftSave) {
                                return Promise.resolve();
                              }
                              const formValues = getFieldValue(["sku_items", name]) || {};
                              const basePrice = Number(String(value ?? 0).replace(/,/g, ""));
                              const totalCommission = Number(String(formValues.total_commission ?? 0).replace(/,/g, ""));

                              if (basePrice === 0 && !value) {
                                return Promise.reject("");
                              }

                              // ✅ Condition > Commission: Validation Removed per user request
                              // if (totalCommission > 0 && basePrice < totalCommission) {
                              //    return Promise.reject(`ราคาตั้งต้น (${formatWithCommas(basePrice)}) ต่ำกว่าค่าคอมมิชชั่น (${formatWithCommas(totalCommission)})`);
                              // }

                              return Promise.resolve();
                            },
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <NumericFormat
                          customInput={Input}
                          style={{
                            width: "100%",
                            padding: "4px 8px",
                            textAlign: "right",
                            backgroundColor: "#fff0f6",
                            // ✅ สลับสี: ถ้า isOverMarket=true ให้ช่องนี้ติดสีส้มแทน input_market_price
                            //    (isOverMarket=false → สีชมพูอ่อนโทนเดียวกับราคาขายแนะนำ)
                            ...(isOverMarket
                              ? {
                                borderColor: "#fa8c16",
                                backgroundColor: "#fff7e6",
                                color: "#fa8c16",
                                fontWeight: 600,
                              }
                              : {}),
                          }}
                          size="small"
                          thousandSeparator=","
                          decimalSeparator="."
                          decimalScale={2}
                          fixedDecimalScale={true}
                          allowNegative={false}
                          min={0}
                          max={999999.99}
                          onFocus={handleFocusClearZeroOrCursorStart("base_factory_price")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = event.target?.value;
                            if (value !== undefined && value !== null) {
                              const raw = String(value).replace(/,/g, '');
                              // ✅ [บั๊กฟิกซ์] ลบจนว่าง → ต้อง set เป็น "" (empty string) ห้ามใช้ undefined/NaN —
                              //    react-number-format ถือว่า undefined/null คือ "ไม่ controlled แล้ว" แล้วจะ
                              //    ดึงค่าที่จำไว้ภายใน (เดิม "0.00") กลับมาแสดงแทน ทำให้เคลียร์ไม่ได้เลย
                              if (raw === "") {
                                form.setFieldValue(["sku_items", name, "base_factory_price"], "");
                                handleFormChange("base_factory_price", 0);
                                return;
                              }
                              const numValue = parseFloat(raw);
                              const safeValue = Number.isFinite(numValue) ? numValue : 0;
                              form.setFieldValue(["sku_items", name, "base_factory_price"], safeValue);
                              handleFormChange("base_factory_price", safeValue);
                            }
                          }}
                          onBlur={() => handleBlurRestoreZeroIfEmpty("base_factory_price")}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>

              </Col>

            </Row>
            <Row>
              <Col span={24}>
                {/* ✅ แนะนำขาย (80%) — readonly, ไม่มีผลต่อการคำนวณ เป็นแค่ข้อมูลแนะนำ แสดงเมื่อกรอกราคาตลาดแล้ว
                    ค่าที่แสดง (retail × 0.80) จะตรงกับ base_factory_price พอดี ตามสูตรใหม่ */}
                <Form.Item
                  shouldUpdate={(prevValues: any, currentValues: any) => {
                    const prevItem = prevValues.sku_items?.[name];
                    const curItem = currentValues.sku_items?.[name];
                    return prevItem?.input_market_price !== curItem?.input_market_price;
                  }}
                  noStyle
                >
                  {({ getFieldValue }: any) => {
                    // ✅ Requirement ใหม่: ไม่แสดง "ราคาแนะนำขาย (ปรับได้)" ใน edit mode
                    //    (ใน edit mode ไม่มีการ recalc จาก input_market_price แล้ว ข้อความนี้จึงไม่มีประโยชน์)
                    if (editMode) return null;
                    const retailPriceVal = getFieldValue(["sku_items", name, "input_market_price"]);
                    const numVal = Number(String(retailPriceVal ?? 0).replace(/,/g, ""));
                    if (numVal <= 0) return null;
                    const recommended = round2(numVal * 0.80);
                    return (
                      <Row style={{ marginTop: 4 }} align="middle">
                        <Col span={20} style={{ textAlign: "left" }}>
                          <Typography.Text type="secondary">
                            {/* ✅ ถ้าเลือก "ส่งกับ Partner แบ่งเบา" (bangbow) ให้ตัดคำว่า "พร้อมส่ง" ออก */}
                            {watchedShippingMethod === "bangbow"
                              ? "ราคาแนะนำขาย (ปรับได้)"
                              : "ราคาแนะนำขายพร้อมส่ง (ปรับได้)"}
                            {" : "}{formatWithCommasFixed2(recommended)}
                          </Typography.Text>
                        </Col>
                        <Col span={4} style={{ textAlign: "right" }}></Col>
                      </Row>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Typography.Text style={{ fontSize: 13 }}>
                  หมายเหตุ : ไม่รวมค่าขนส่ง
                </Typography.Text>
              </Col>
            </Row>
            {/* ✅ Task 1.2: นำราคาตลาดออกจาก Card ราคาตั้งต้น (แต่ยังเก็บ base_retail_price ไว้ใน form) */}
          </Col>
          <Col className="gutter-row" span={12}>

          </Col>
        </Row>
      </Card>

      {/*✅ Card 2: ราคาที่ขายใน Line - แสดงเมื่อ sell_through_line = true*/}
      {sellThroughLine && (
        <Card className="sku-section-card" style={{ width: "95%", padding: 15, marginTop: 15 }}>
          <Row gutter={16}>
            <Col className="gutter-row" span={12}>
              <Row gutter={[16, 16]}>
                <Col className="gutter-row" span={16}>
                  <h4>ราคาที่ขายใน Line OA (บาท)</h4>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 15 }} align="middle">
                <Col span={8}>
                  <Typography.Text>
                    ราคา VIP:
                  </Typography.Text>
                </Col>
                <Col span={10}>
                  <Form.Item
                    name={[name, "base_vip_price"]}
                    rules={[
                      {
                        required: false, // ✅ Task 3.1: ไม่ต้อง required
                        message: "กรุณากรอกราคา VIP",
                      },
                      {
                        type: "number",
                        min: 1,
                        message: "ราคาต้องไม่ต่ำกว่า 0",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{
                        width: "100%",
                        padding: "4px 8px",
                        textAlign: "right",
                      }}
                      formatter={formatWithCommas}
                      size="small"
                      min={0}
                      max={999999}
                      controls={false}
                      maxLength={6}
                      onKeyDown={integerGuards.onKeyDown}
                      onPaste={integerGuards.onPaste}
                      parser={(value) => {
                        const parsed = value?.replace(/\D/g, "").slice(0, 6);
                        return parsed ? parseInt(parsed) : 0;
                      }}
                      placeholder="กรุณากรอกราคา VIP"
                      onChange={() => handleFormChange("base_vip_price")}
                      onFocus={() => handleFocus("base_vip_price")}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 12 }} align="middle">
                <Col span={8}>
                  <Typography.Text>
                    ราคาที่ขายใน Line :
                  </Typography.Text>
                </Col>
                <Col span={10}>
                  <Form.Item
                    name={[name, "base_wholesale_price"]}
                    rules={[
                      {
                        required: false, // ✅ Task 3.1: ไม่ต้อง required
                        message: "กรุณากรอกราคาที่ขายใน Line",
                      },
                      {
                        type: "number",
                        min: 1,
                        message: "ราคาต้องไม่ต่ำกว่า 0",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      style={{
                        width: "100%",
                        marginLeft: "auto",
                        display: "block",
                        padding: "4px 8px",
                        textAlign: "right",
                      }}
                      formatter={formatWithCommas}
                      size="small"
                      min={0}
                      max={999999}
                      controls={false}
                      maxLength={6}
                      onKeyDown={integerGuards.onKeyDown}
                      onPaste={integerGuards.onPaste}
                      parser={(value) => {
                        const parsed = value?.replace(/\D/g, "").slice(0, 6);
                        return parsed ? parseInt(parsed) : 0;
                      }}
                      placeholder="กรุณากรอกราคาที่ขายใน Line OA"
                      onChange={() => handleFormChange("base_wholesale_price")}
                      onFocus={() => handleFocus("base_wholesale_price")}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col className="gutter-row" span={12}>
              <Card
                style={{
                  border: "1px dashed rgb(200, 199, 199)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <div style={{ padding: "16px" }}>
                  <Typography.Title
                    level={5}
                    style={{ color: "#1890ff", marginTop: 0 }}
                  >
                    💡 คำแนะนำราคาที่ขายใน Line
                  </Typography.Title>
                  <Typography.Paragraph style={{ marginBottom: 8 }}>
                    <strong>เงื่อนไขราคา:</strong>
                  </Typography.Paragraph>
                  <Typography.Paragraph
                    style={{ marginBottom: 4, paddingLeft: 16 }}
                  >
                    • ไม่รวมค่าขนส่งและ PaymentGateway
                  </Typography.Paragraph>
                </div>
              </Card>
            </Col>
          </Row>
        </Card>
      )}
      {/*กำหนด dimemsion สินค้า*/}
      <Card className="sku-section-card" style={{ width: "95%", padding: 15, marginTop: 15 }}>
        <Row gutter={16}>
          <Col className="gutter-row" span={12}>
            {/*กำหนดขนาดพัสดุสำหรับจัดส่ง*/}
            <Row gutter={16}>
              <Col className="gutter-row" span={16}>
                <h4>2. เลือกวิธีจัดส่ง</h4>
              </Col>
            </Row>
            {/* ✅ เปลี่ยนจาก Radio เป็น dropdown เลือกวิธีการจัดส่ง — เงื่อนไข/ค่า "self"|"bangbow" เดิมคงไว้ทั้งหมด */}
            <Row gutter={16} style={{ marginTop: 15 }} align="middle">
              <Col span={24}>
                <Form.Item
                  name={[name, "shipping_method"]}
                  style={{ marginBottom: 0 }}
                  rules={[{ required: true, message: "กรุณาเลือกวิธีการจัดส่ง" }]}
                >
                  <PinkBgSelect style={{ width: "100%", maxWidth: 380 }} placeholder="กรุณาเลือกวิธีการจัดส่ง">
                    <Select.Option value="self">
                      จัดส่งสินค้าเอง <span style={{ fontWeight: "bold"}}>(โอนให้ผู้ประกอบการ)</span>
                    </Select.Option>
                    <Select.Option value="bangbow">
                      ใช้ขนส่งพาร์ตเนอร์ของแบ่งเบา
                    </Select.Option>
                  </PinkBgSelect>
                </Form.Item>
              </Col>
            </Row>
            {/* ✅ เลือก bangbow แต่ยังไม่มีที่อยู่รับสินค้า (merchant) → ซ่อนรายละเอียดด้านล่างทั้งหมด
                 แล้วโชว์แค่ข้อความแจ้ง + ปุ่มไปตั้งค่าที่อยู่รับสินค้าแทน */}
            {bangbowMissingMerchant && (
              <Row gutter={16} style={{ marginTop: 15 }}>
                <Col span={24}>
                  <Alert
                    type="error"
                    showIcon
                    message={
                      <div>
                        <div>ไม่สามารถใช้บริการขนส่งพาร์ตเนอร์แบ่งเบาได้ เนื่องจากไม่พบข้อมูลที่อยู่สำหรับให้ขนส่งเข้ารับสินค้า</div>
                        <Button
                          type="primary"
                          size="small"
                          style={{ marginTop: 8 }}
                          onClick={() => navigate("/manage-merchant")}
                        >
                          คลิกเพื่อตั้งค่าที่อยู่รับสินค้า
                        </Button>
                      </div>
                    }
                  />
                </Col>
              </Row>
            )}
            {!bangbowMissingMerchant && hasShippingMethodSelected && (
            <>
            {isSelfShipping && (
              <Row gutter={16} style={{ marginLeft:15,marginTop: 8 }} align="middle">
                <Col span={13}>
                  <Typography.Text>ค่าขนส่งเท่าใด :</Typography.Text>
                </Col>
                <Col span={11}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Form.Item
                      name={[name, "self_transport_price"]}
                      style={{ marginBottom: 0, flex: 1 }}
                      rules={[{ required: true, message: "กรุณากรอกค่าขนส่ง หากไม่มีให้กรอก 0" }]}
                    >
                      <NumericFormat
                        customInput={Input}
                        style={{ width: "100%", padding: "4px 8px", textAlign: "right", backgroundColor: "#fff0f6" }}
                        size="small"
                        thousandSeparator=","
                        decimalScale={0}
                        allowNegative={false}
                        min={0}
                        onFocus={handleFocusHighlightAllText("self_transport_price")}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const value = event.target?.value;
                          if (value !== undefined && value !== null) {
                            const raw = String(value).replace(/,/g, "");
                            // ✅ [requirement 2026-10-16] ค่าเริ่มต้นเป็นค่าว่าง + บังคับกรอก —
                            //    ถ้าลบจนว่าง ต้องปล่อยเป็น "" (ไม่ force เป็น 0) ให้ required rule ทำงาน
                            //    ส่วนถ้าผู้ใช้ตั้งใจกรอก 0 เอง ให้ผ่านได้ปกติ
                            //    ✅ [บั๊กฟิกซ์] ใช้ "" ไม่ใช่ undefined — react-number-format ถือว่า
                            //    undefined/null คือ "ไม่ controlled แล้ว" แล้วดึงค่าที่จำไว้ภายในกลับมาแสดงแทน
                            const numValue = raw === "" ? "" : parseInt(raw, 10);
                            form.setFieldValue(["sku_items", name, "self_transport_price"], numValue);
                            handleFormChange("self_transport_price", numValue === "" ? 0 : numValue);
                          }
                        }}
                      />
                    </Form.Item>
                    {selectedSelfCarrier?.image && (
                      <TransportLogo
                        src={selectedSelfCarrier.image}
                        alt={selectedSelfCarrier.description}
                        height={26}
                        width={52}
                      />
                    )}
                  </div>
                </Col>
              </Row>
            )}
            {/* ✅ [requirement 2026-10-27] dropdown เลือกขนส่งกรณีส่งเอง — ย้ายมาจาก card grid ฝั่งขวา
                 (ข้อ 3) มาไว้ใต้ช่องค่าขนส่งแทน แสดง logo + ชื่อขนส่งในแต่ละ option */}
            {isSelfShipping && (
              <Row gutter={16} style={{ marginLeft: 15, marginTop: 8 }} align="middle">
                <Col span={13}>
                  <Typography.Text>ท่านจะส่งสินค้ากับขนส่งใด :</Typography.Text>
                </Col>
                <Col span={11}>
                  <Form.Item
                    name={[name, "self_transport_carrier_id"]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      placeholder="เลือกขนส่ง"
                      style={{ width: "100%" }}
                      optionLabelProp="label"
                      allowClear
                      popupMatchSelectWidth={false}
                      dropdownStyle={{ minWidth: 280 }}
                    >
                      {selfCarriers.map((carrier) => (
                        <Select.Option
                          key={carrier.id}
                          value={carrier.id}
                          // ✅ [requirement 2026-10-27] label รองรับ ReactNode — ใส่ logo เข้าไปด้วย
                          //    เพื่อให้กล่อง Select ที่ปิดอยู่ (หลังเลือกแล้ว) โชว์ logo เหมือนตอนเปิด list
                          label={
                            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <TransportLogo src={carrier.image} alt={carrier.description} height={16} width={26} />
                              <span>{carrier.description}</span>
                            </span>
                          }
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <TransportLogo src={carrier.image} alt={carrier.description} height={20} width={32} />
                            <span>{carrier.description}</span>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )}
            {isSelfShipping && (
              <Row style={{ marginTop: 10 ,marginBottom: 10, border: "1px solid #d9d9d9", padding: "10px" }} align="middle">
                <Col span={24}>
                  <Typography.Text style={{ fontSize: 14 }}>
                    หมายเหตุ : แบ่งเบา แพลตฟอร์ม จะโอนค่าขนส่งนี้ให้ผู้ประกอบการพร้อมกับยอดขาย หากผู้ซื้อ เลือกรับสินค้า ณ จุดจำหน่าย ค่าขนส่งนี้จะถูกตัดออกเป็นส่วนลดจากราคาขายพร้อมส่ง
                  </Typography.Text>
                </Col>
              </Row>
            )}
            <Row
              gutter={16}
              style={{ marginTop: 8, display: watchedShippingMethod === "bangbow" && hasCodCapableBrand ? undefined : "none" }}
              align="middle"
            >
              <Col span={24} style={{ paddingLeft: 40 }}>
                <Form.Item
                  name={[name, "is_cod"]}
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group>
                    <Radio value={false}>ไม่เก็บเงินปลายทาง</Radio>
                    <Radio value={true}>เก็บเงินปลายทาง (COD)</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            {/* ✅ strict guard: บล็อกการบันทึกเมื่อยังไม่ค้นหาตามมิติล่าสุด (ซ่อนไว้ ไม่เพิ่มช่องว่าง/ไม่โชว์ข้อความ) */}
            <Form.Item
              name={[name, "__speed_strict_guard"]}
              style={{ display: "none" }}
              rules={[
                {
                  validator: async () => {
                    const shippingMethod = (watchedShippingMethod ?? "self") as string;
                    if (shippingMethod !== "bangbow") return Promise.resolve();
                    if (speedTimeout) return Promise.resolve();

                    // ถ้าเงื่อนไข strict ยังไม่พร้อม ให้บล็อก submit
                    if (dimErrorFlags.width || dimErrorFlags.length || dimErrorFlags.height || dimErrorFlags.weight) {
                      return Promise.reject();
                    }

                    const w = Number(watchedWidth ?? 0);
                    const l = Number(watchedLength ?? 0);
                    const h = Number(watchedHeight ?? 0);
                    if (w + l + h > 1000) {
                      return Promise.reject("กว้างยาวสูงรวมกันต้องไม่เกิน 1000 ซ.ม.");
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input />
            </Form.Item>

            {/* ✅ Dropdown สำหรับเลือกประเภทบรรจุภัณฑ์ (เฉพาะส่งกับแบ่งเบา) */}
            {!isSelfShipping && (
              <Row gutter={16} style={{ marginTop: 15 }} align="middle">
                <Col span={10}>
                  <Typography.Text>
                    ประเภทพัสดุ :
                  </Typography.Text>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={[name, "_parent_packaging_type_id"]} // ใช้ name ชั่วคราวที่ไม่ส่ง API เพื่อบังคับกรอก parent
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        required: !isSelfShipping,
                        message: "กรุณาเลือกประเภทบรรจุภัณฑ์",
                      }
                    ]}
                  >
                    <Select
                      placeholder="เลือกประเภทบรรจุภัณฑ์"
                      loading={packagingTypeLoading}
                      style={{ width: "100%" }}
                      allowClear
                      onChange={(value) => {
                        // ✅ เก็บ parent ที่ user เลือกไว้ใน _parent_packaging_type_id (stable แม้ packaging_type_id=null)
                        form.setFieldValue(["sku_items", name, "_parent_packaging_type_id"], value ?? null);
                        // 1. หาว่า parent ที่เลือกคืออะไร
                        const parentTarget = packagingTypeData?.data?.master_packaging_types?.find(p => p.id === value);
                        if (parentTarget) {
                          if (!parentTarget.has_child) {
                            form.setFieldValue(["sku_items", name, "packaging_type_id"], value);
                          } else {
                            // ต้องรอเลือก child, clear main field ให้หมด
                            // ใช้ undefined แทน null เพื่อให้ AntD Select reset ค่าที่แสดงด้วย
                            form.setFieldValue(["sku_items", name, "packaging_type_id"], undefined);
                          }
                        } else {
                          // Clear (user กด x)
                          form.setFieldValue(["sku_items", name, "packaging_type_id"], null);
                        }
                      }}
                      options={packagingTypeData?.data?.master_packaging_types?.map(p => ({ label: p.name, value: p.id }))}
                      disabled={!form.getFieldValue(['sku_items', 0, 'shipping_method']) || form.getFieldValue(['sku_items', 0, 'shipping_method']) !== 'bangbow'}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {/* ✅ Dropdown สำหรับเลือกบรรจุภัณฑ์ย่อย (ถ้า parent มี has_child=true) */}
            {!isSelfShipping && showChildDropdown && (
              <Row gutter={16} style={{ marginTop: 12 }} align="middle">
                <Col span={10}>
                  <Typography.Text>
                    รูปแบบบรรจุภัณฑ์:
                  </Typography.Text>
                </Col>
                <Col span={12} style={{ display: isSelfShipping ? 'none' : 'block' }}>
                  <Form.Item
                    name={[name, "packaging_type_id"]}
                    style={{ marginBottom: 0, width: '100%' }}
                    rules={[
                      {
                        required: !isSelfShipping,
                        message: "กรุณาเลือกบรรจุภัณฑ์ย่อย",
                      }
                    ]}
                  >
                    <Select
                      key={selectedParentPackagingTypeId ?? "no-parent"} // ✅ force re-mount เมื่อ parent เปลี่ยน (ล้าง internal state ทั้งหมด)
                      placeholder="เลือกขนาดกล่อง/ซอง"
                      loading={childPackagingTypeLoading}
                      allowClear
                      value={(() => {
                        // ✅ validate: ถ้า packaging_type_id ไม่อยู่ใน options ของ child ปัจจุบัน → แสดง placeholder
                        const v = watchedPackagingTypeId;
                        const exists = childPackagingTypeData?.data?.master_packaging_types?.some(c => c.id === v);
                        return exists ? v : undefined;
                      })()}
                      onChange={(value) => {
                        // 1. หาข้อมูลของ child บรรจุภัณฑ์ที่เพิ่งเลือก
                        form.setFieldValue(["sku_items", name, "packaging_type_id"], value ?? undefined);
                        const childTarget = childPackagingTypeData?.data?.master_packaging_types?.find(c => c.id === value);
                        if (childTarget) {
                          if (childTarget.width != null) form.setFieldValue(["sku_items", name, "width"], childTarget.width);
                          if (childTarget.length != null) form.setFieldValue(["sku_items", name, "length"], childTarget.length);
                          if (childTarget.height != null) form.setFieldValue(["sku_items", name, "height"], childTarget.height);
                          handleFormChange("width");
                          handleFormChange("length");
                          handleFormChange("height");
                        }
                      }}
                      options={childPackagingTypeData?.data?.master_packaging_types?.map(c => ({
                        label: `${c.name} ${c.width != null ? `(${c.width}x${c.length}x${c.height} ซม.)` : ''}`,
                        value: c.id
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={16} style={{ marginTop: 15, display: isSelfShipping || hideDimensionFields ? 'none' : 'flex' }} align="middle">
              <Col span={10}>
                <Typography.Text>
                  ความกว้าง (ซม.) :
                </Typography.Text>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[name, "width"]}
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (isDraftSave) {
                          return Promise.resolve();
                        }
                        // ✅ ตรวจสอบเฉพาะเมื่อ shipping_method เป็น "bangbow"
                        const currentShippingMethod = form.getFieldValue(["sku_items", name, "shipping_method"]);
                        if (currentShippingMethod !== "bangbow") {
                          return Promise.resolve();
                        }
                        if (!value || value === 0) {
                          return Promise.reject("กรุณากรอกความกว้าง");
                        }
                        if (value > 500) {
                          return Promise.reject("ความกว้างต้องไม่เกิน 500 ซม.");
                        }
                        // inline sum limit moved to global alert
                        // if (value && value % 1 !== 0) {
                        //   return Promise.reject(
                        //     "กรุณากรอกราคาเป็นจำนวนเต็ม ไม่อนุญาตให้กรอกทศนิยม"
                        //   );
                        // } 
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber<number>
                    style={{
                      width: "200px",
                      padding: "4px 8px",
                      textAlign: "right",
                    }}
                    formatter={formatWithCommas}
                    size="small"
                    min={0}
                    max={999999}
                    controls={false}
                    maxLength={9}
                    parser={(value) => {
                      // ✅ แก้ไข: อนุญาตให้ใส่จุดทศนิยมได้
                      const parsed = value?.replace(/[^\d.]/g, "").replace(/\.(?=.*\.)/g, "").slice(0, 9);
                      return parsed ? parseFloat(parsed) : 0;
                    }}
                    placeholder="กรุณากรอกความกว้าง"
                    onChange={() => handleFormChange("width")}
                    onFocus={() => handleFocus("width")}
                    disabled={isSelfShipping || packagingSelectionIncomplete}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12, display: isSelfShipping || hideDimensionFields ? 'none' : 'flex' }} align="middle">
              <Col span={10}>
                <Typography.Text>
                  ความยาว (ซม.) :
                </Typography.Text>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[name, "length"]}
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (isDraftSave) {
                          return Promise.resolve();
                        }
                        // ✅ ตรวจสอบเฉพาะเมื่อ shipping_method เป็น "bangbow"
                        const currentShippingMethod = form.getFieldValue(["sku_items", name, "shipping_method"]);
                        if (currentShippingMethod !== "bangbow") {
                          return Promise.resolve();
                        }
                        if (!value || value === 0) {
                          return Promise.reject("กรุณากรอกความยาว");
                        }
                        if (value > 500) {
                          return Promise.reject("ความยาวต้องไม่เกิน 500 ซม.");
                        }
                        // inline sum limit moved to global alert
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber<number>
                    style={{
                      width: "200px",
                      padding: "4px 8px",
                      textAlign: "right",
                    }}
                    formatter={formatWithCommas}
                    size="small"
                    min={0}
                    max={999999}
                    controls={false}
                    maxLength={9}
                    parser={(value) => {
                      // ✅ แก้ไข: อนุญาตให้ใส่จุดทศนิยมได้
                      const parsed = value?.replace(/[^\d.]/g, "").replace(/\.(?=.*\.)/g, "").slice(0, 9);
                      return parsed ? parseFloat(parsed) : 0;
                    }}
                    placeholder="กรุณากรอกความยาว"
                    onChange={() => handleFormChange("length")}
                    onFocus={() => handleFocus("length")}
                    disabled={isSelfShipping || packagingSelectionIncomplete}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12, display: isSelfShipping || hideDimensionFields ? 'none' : 'flex' }} align="middle">
              <Col span={10}>
                <Typography.Text>
                  ความสูง (ซม.) :
                </Typography.Text>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[name, "height"]}
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (isDraftSave) {
                          return Promise.resolve();
                        }
                        // ✅ ตรวจสอบเฉพาะเมื่อ shipping_method เป็น "bangbow"
                        const currentShippingMethod = form.getFieldValue(["sku_items", name, "shipping_method"]);
                        if (currentShippingMethod !== "bangbow") {
                          return Promise.resolve();
                        }
                        if (!value || value === 0) {
                          return Promise.reject("กรุณากรอกความสูง");
                        }
                        if (value > 500) {
                          return Promise.reject("ความสูงต้องไม่เกิน 500 ซม.");
                        }
                        // inline sum limit moved to global alert
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber<number>
                    style={{
                      width: "200px",
                      padding: "4px 8px",
                      textAlign: "right",
                    }}
                    formatter={formatWithCommas}
                    size="small"
                    min={0}
                    max={999999}
                    controls={false}
                    maxLength={9}
                    parser={(value) => {
                      // ✅ แก้ไข: อนุญาตให้ใส่จุดทศนิยมได้
                      const parsed = value?.replace(/[^\d.]/g, "").replace(/\.(?=.*\.)/g, "").slice(0, 9);
                      return parsed ? parseFloat(parsed) : 0;
                    }}
                    placeholder="กรุณากรอกความสูง"
                    onChange={() => handleFormChange("height")}
                    onFocus={() => handleFocus("height")}
                    disabled={isSelfShipping || packagingSelectionIncomplete}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12, display: isSelfShipping ? 'none' : 'flex' }} align="middle">
              <Col span={10}>
                <Typography.Text>
                  น้ำหนัก (กรัม) :
                </Typography.Text>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[name, "weight"]}
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (isDraftSave) {
                          return Promise.resolve();
                        }
                        // ✅ ตรวจสอบเฉพาะเมื่อ shipping_method เป็น "bangbow"
                        const currentShippingMethod = form.getFieldValue(["sku_items", name, "shipping_method"]);
                        if (currentShippingMethod !== "bangbow") {
                          return Promise.resolve();
                        }
                        if (!value || value === 0) {
                          return Promise.reject("กรุณากรอกน้ำหนัก");
                        }
                        if (value === 0) {
                          return Promise.reject("น้ำหนักต้องมากกว่า 0");
                        }
                        if (value > 100000) {
                          return Promise.reject("น้ำหนักต้องไม่เกิน 100 ก.ก. (100,000 กรัม)");
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber<number>
                    style={{
                      width: "200px",
                      padding: "4px 8px",
                      textAlign: "right",
                    }}
                    formatter={formatWithCommas}
                    size="small"
                    min={0 as number}
                    max={100000 as number}
                    controls={false}
                    maxLength={7}
                    parser={(value) => {
                      const parsed = value?.replace(/\D/g, "").slice(0, 6);
                      return parsed ? parseInt(parsed) : 0;
                    }}
                    placeholder="กรุณากรอกน้ำหนัก"
                    onChange={() => handleFormChange("weight")}
                    onFocus={() => handleFocus("weight")}
                    disabled={isSelfShipping || packagingSelectionIncomplete}
                  />
                </Form.Item>
              </Col>
            </Row>
            {/* ✅ แสดง Error เมื่อผลรวมมิติเกิน 1000 */}
            {!isSelfShipping && (Number(watchedWidth ?? 0) + Number(watchedLength ?? 0) + Number(watchedHeight ?? 0) > 1000) && (
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={24}>
                  <Alert
                    type="error"
                    showIcon
                    message="กว้างยาวสูงรวมกันต้องไม่เกิน 1,000 ซ.ม."
                  />
                </Col>
              </Row>
            )}
            {/* ✅ G1: ถ้ามีการเปลี่ยนมิติหลังเคยค้นหาแล้ว ให้เตือนก่อนปุ่มค้นหา */}
            {speedStaleHint ? (
              <Row gutter={16} style={{ marginTop: 10 }}>
                <Col span={24}>
                  <Alert type="error" showIcon message={speedStaleHint} />
                </Col>
              </Row>
            ) : null}
            {/* ✅ ไม่มี merchant เลย: แสดงข้อความแจ้งเตือนแทนปุ่ม */}
            {/*}
            {watchedShippingMethod === "bangbow" && !hasMerchantSpeed && !hasMerchantThaipost && (
              <Row gutter={16} style={{ marginTop: 10 }}>
                <Col span={24}>
                  <Alert
                    type="warning"
                    showIcon
                    message="ไม่สามารถใช้บริการขนส่งพาร์ตเนอร์แบ่งเบาได้ เนื่องจากไม่พบข้อมูลที่อยู่สำหรับจัดส่งสินค้า คลิกที่นี่เพื่อไปตั้งค่าที่อยู่สำหรับจัดส่งสินค้า"
                  />
                </Col>
              </Row>
            )}
            */}
            {/* ✅ requirement ใหม่: ปุ่มค้นหาบริษัทขนส่ง (ยิง check-price หลังคลิกปุ่ม) */}
            <Row gutter={16} style={{ marginTop: 10, display: isSelfShipping ? 'none' : 'flex' }} align="middle">
              <Col span={10} />
              <Col span={12}>
                <BlinkingButton
                  id="factory-price-field"
                  size="middle"
                  block
                  type="primary"
                  style={canSearchSpeedShipping ? speedSearchButtonStyle : undefined}
                  disabled={!canSearchSpeedShipping}
                  loading={speedLoading}
                  $shouldBlink={shouldBlinkButton}
                  onClick={(value) => {
                    if (value) {
                      setControlSpeedNotice(true);
                    } else {
                      setControlSpeedNotice(false);
                    }
                    setSpeedError(null);
                    setSpeedTimeout(false);
                    setSpeedStaleHint(null);
                    setSpeedHasSearched(true);
                    // ✅ [requirement 2026-10-15] กดปุ่มเอง → เคลียร์ค่าขนส่งเดิม (ราคา+แบรนด์+logo) ทันที
                    //    ไม่ต้องรอผลจาก API ก่อน (เอฟเฟกต์ค้นหาด้านล่างจะ clear ซ้ำอีกครั้งเมื่อ isManualSearch
                    //    เป็น true อยู่แล้ว แต่การเคลียร์ตรงนี้ทำให้ UI อัปเดตทันทีไม่ต้องรอ round-trip)
                    // ✅ กันไม่ให้ effect rehydrate (sync selected card) ดึงค่าขนส่งเดิมกลับมาใส่ทันทีหลังเคลียร์
                    manualTransportClearedRef.current = true;
                    setSelectedSpeedCarrierCode(null);
                    form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], null);
                    form.setFieldValue(["sku_items", name, "api_transport_logo"], null);
                    form.setFieldValue(["sku_items", name, "api_transport_price"], 0);
                    form.setFieldValue(["sku_items", name, "add_on_api_transport_price"], 0);
                    form.setFieldValue(["sku_items", name, "transport_price"], 0);
                    form.setFieldValue(["sku_items", name, "partner_id"], "");
                    form.setFieldValue(["sku_items", name, "brand_id"], "");
                    setSpeedNotice("กรุณาเลือกขนส่งจากรายการด้านล่างอีกครั้ง");
                    // ✅ กดปุ่มเอง → ตั้ง flag ให้ effect รู้ว่าต้องเคลียร์ค่าขนส่งเดิมแล้วบังคับเลือกใหม่
                    manualSearchTriggeredRef.current = true;
                    setSpeedSearchTrigger((x) => x + 1);
                    // ✅ หยุดการกระพริบเมื่อคลิกปุ่ม
                    setShouldBlinkButton(false);
                  }}
                >
                  ตรวจสอบราคาและขนส่ง
                </BlinkingButton>
              </Col>
            </Row>
            {/* ✅ คำแนะใต้ปุ่มค้นหา: ลำดับบรรจุภัณฑ์ → มิติ → คลิกค้นหา → เลือกขนส่ง (ไม่แสดงเมื่อมี speedStaleHint) */}
            <Row gutter={16} style={{ marginTop: 8, display: isSelfShipping ? 'none' : 'flex' }}>
              <Col span={24}>
                {partnerShippingGuidanceBelowSearch ? (
                  <Alert
                    type={partnerShippingGuidanceBelowSearch.type}
                    showIcon={partnerShippingGuidanceBelowSearch.type === "info"}
                    style={
                      partnerShippingGuidanceBelowSearch.type === "warning"
                        ? {
                            background: "#fffbe6",
                            border: "1px solid #ffe58f",
                            color: "#613400",
                            padding: "8px 12px",
                            borderRadius: 6,
                            marginBottom: 0,
                          }
                        : { marginBottom: 0 }
                    }
                    message={partnerShippingGuidanceBelowSearch.message}
                  />
                ) : null}
              </Col>
            </Row>
            </>
            )}
          </Col>
          <Col className="gutter-row" span={12}>
            {!bangbowMissingMerchant && hasShippingMethodSelected && (
            <>
            {isSelfShipping ? (
              // ✅ [requirement 2026-10-27] เอา card grid เลือกขนส่งฝั่งขวาออก — ย้ายไปเป็น dropdown
              //    "ท่านจะส่งสินค้ากับขนส่งใด" ใต้ช่องค่าขนส่งฝั่งซ้ายแทนแล้ว (ผูก field self_transport_carrier_id
              //    เดียวกัน ไม่ต้องมี hidden Form.Item ซ้ำที่นี่อีก)
              null
            ) : (
              <>
                {(hasMerchantSpeed || hasMerchantThaipost) && (
                  <Row gutter={16}>
                    <Col className="gutter-row" span={16}>
                      <h4>
                        เลือกบริษัทขนส่ง
                      </h4>
                    </Col>
                  </Row>
                )}

            <Row gutter={16}>
              <Col className="gutter-row" span={24}>
                {/* ✅ แสดง master brand list:
                  - ตอน Speed Shipping แต่ยังไม่กดค้นหา: คงไว้เป็น dummy
                  - ตอน Speed Shipping กดค้นหาแล้ว: ซ่อน (ให้โชว speed results แทน)
                */}
                {/* ✅ requirement ใหม่: ถ้ายังไม่กด "ค้นหาบริษัทขนส่ง" (speed shipping) → ยังแสดง logo/ชื่อขนส่งได้ แต่คลิกเลือกไม่ได้ (disabled) */}
                {!speedHasSearched && (
                  <div style={{ maxHeight: 310, overflowY: "auto", paddingRight: 4, marginTop: 10 }}>
                    <Row gutter={[10, 10]}>
                      {partnerTransportBrands.map((item) => {
                        const active = item.id === currentBrandId;
                        // ✅ disable การคลิกเลือก เมื่ออยู่โหมด speed shipping และยังไม่กดปุ่ม "ค้นหาบริษัทขนส่ง"
                        const isBrandClickDisabled = !isSelfShipping && !speedHasSearched;
                        return (
                          <Col key={item.id} xs={24} sm={24}>
                            <Card
                              size="small"
                              onClick={() => {
                                // ✅ ถ้ายังไม่กดค้นหาในโหมด speed shipping → ไม่อนุญาตให้เลือก
                                if (isBrandClickDisabled) {
                                  return;
                                }
                                // Toggle Off if already active
                                if (active) {
                                  form.setFieldValue(["sku_items", name, "brand_id"], "");
                                  form.setFieldValue(["sku_items", name, "api_transport_logo"], null);
                                  return;
                                }

                                form.setFieldValue(["sku_items", name, "brand_id"], item.id);
                                form.setFieldValue(["sku_items", name, "api_transport_logo"], item.brand_logo ?? null);
                              }}
                              style={{
                                border: active ? "1px solid #1890ff" : "1px solid #f0f0f0",
                                background: active ? "#e6f7ff" : "#fff",
                                height: "100%",
                                minHeight: 65,
                                padding: 10,
                                paddingTop: 10,
                                paddingBottom: 10,
                                // ✅ ให้ดูเป็น disabled state เมื่อคลิกไม่ได้ (ยังไม่กดค้นหา) — แต่คง opacity เต็ม 1 ตาม requirement
                                cursor: isBrandClickDisabled ? "not-allowed" : "pointer",
                                opacity: 1,
                                pointerEvents: isBrandClickDisabled ? "none" : "auto",
                              }}
                            >
                              <Row align="middle" gutter={16} wrap={false}>
                                <Col>
                                  <TransportLogo
                                    src={item.brand_logo.replace("_SPEED", '') ?? undefined}
                                    alt={item.brand_name}
                                    height={40}
                                    width={64}
                                  />
                                </Col>
                                <Col flex="auto">
                                  <Typography.Text strong>{item.brand_name}</Typography.Text>
                                </Col>
                              </Row>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                )}

                {!isSelfShipping && speedError && (
                  <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={speedError} />
                )}
                {!isSelfShipping && speedNotice && (() => {
                  const LEGACY_NOTICE =
                    "รหัส SKU นี้ยังไม่ได้ผูกค่าขนส่งกับระบบ (สามารถเลือกค่าขนส่งจากรายการด้านล่าง)";
                  // ✅ requirement: ข้อความ legacy นี้ต้องหายไปเมื่อผู้ใช้ "เลือกขนส่งแล้ว"
                  if (speedNotice === LEGACY_NOTICE && selectedSpeedCarrierCode) return null;
                  return <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={speedNotice} />;
                })()}
                {!isSelfShipping && speedLoading && (
                  <Alert type="info" showIcon style={{ marginBottom: 8 }} message="กำลังโหลดค่าขนส่ง..." />
                )}

                {!isSelfShipping && speedHasSearched ? (
                  <>
                    {/* ✅ policy: แสดง card/list ขนส่ง เฉพาะตอน "ไม่มี error จาก api speed" และไม่อยู่ระหว่าง loading */}
                    {!speedTimeout && !speedError && !speedLoading ? (
                      <>
                        {/* ✅ Card แรก: ขนส่งที่เลือก (เรนดเฉพาะตอนส่งโดยแบ่งเบาเท่านั้น) */}
                        {!isSelfShipping && (
                          <Card
                            size="small"
                            bodyStyle={{ padding: 16 }}
                            style={{
                              border: "1px solid #d9d9d9",
                              marginBottom: 10,
                              background: selectedSpeedItem ? "#fafafa" : "#fff",
                            }}
                          >
                            <Row align="middle" justify="space-between" gutter={8} wrap={false}>
                              <Col>
                                <TransportLogo
                                  src={selectedSpeedItem?.logo}
                                  alt={selectedSpeedItem?.carrier ?? "transport-logo"}
                                  height={36}
                                  width={64}
                                />
                              </Col>
                              <Col flex="auto">
                                <Typography.Text strong>ขนส่งที่เลือก</Typography.Text>
                                <div style={{ marginTop: 2, fontWeight: 600, paddingTop: 5 }}>
                                  {selectedSpeedItem ? selectedSpeedItem.name : "ยังไม่ได้เลือก"}
                                </div>
                                {/*{selectedSpeedItem?.delivery_time ? (
                                <div style={{ marginTop: 2, color: "#666", fontSize: 12 }}>
                                  ระยะเวลาจัดส่ง: {formatDeliveryTimeTh(selectedSpeedItem.delivery_time)}
                                </div>
                              ) : null}*/}
                              </Col>
                              <Col>
                                <div style={{ minWidth: 120, textAlign: "right", paddingRight: 10 }}>
                                  <Typography.Text strong>
                                    {selectedSpeedItem
                                      ? `${formatWithCommas(
                                          selectedSpeedItem.transport_price ??
                                            selectedSpeedItem.total_with_add_on ??
                                            selectedSpeedItem.total,
                                        )} บาท`
                                      : "-"}
                                  </Typography.Text>
                                </div>
                              </Col>
                            </Row>
                          </Card>
                        )}

                        {/* ✅ Card list: scroll แนวตั้ง */}
                        <div style={{ maxHeight: 270, overflowY: "auto", paddingRight: 4 }}>
                          <Row gutter={[10, 10]}>
                            {speedItems.map((item) => {
                              const active = item.carrier_code === selectedSpeedCarrierCode;
                              return (
                                <Col key={item.carrier_code} xs={24} sm={24}>
                                  <Card
                                    size="small"
                                    hoverable
                                    onClick={() => {
                                      shippingUserSelectedRef.current = true;
                                      // if (active) {
                                      //   // toggle off
                                      //   setSelectedSpeedCarrierCode(null);
                                      //   console.log('item---------4444---->')
                                      //   form.setFieldValue(["sku_items", name, "transport_price"], 0);
                                      //   form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], null);
                                      //   form.setFieldValue(["sku_items", name, "api_transport_logo"], null);
                                      //   form.setFieldValue(["sku_items", name, "api_transport_price"], 0);
                                      //   form.setFieldValue(["sku_items", name, "partner_id"], "");
                                      //   form.setFieldValue(["sku_items", name, "brand_id"], "");
                                      //   handleFormChange("transport_price", 0);
                                      //   return;
                                      // }

                                      // ✅ ผู้ใช้เลือกขนส่งเองแล้ว → ปลดล็อก ให้ rehydrate effect ทำงานได้ปกติอีกครั้ง
                                      manualTransportClearedRef.current = false;
                                      setSelectedSpeedCarrierCode(item.carrier_code);
                                      // ✅ ใช้ชุดราคาชื่อตรง DB จาก BE — ไม่ปัดซ้ำฝั่ง FE
                                      const bd = pickTransportBreakdown(item);
                                      form.setFieldValue(["sku_items", name, "transport_price"], bd.transport_price);
                                      form.setFieldValue(["sku_items", name, "api_transport_carrier_code"], item.carrier_code);
                                      form.setFieldValue(["sku_items", name, "api_transport_logo"], normalizeSpeedLogo(item.logo) ?? null);
                                      form.setFieldValue(["sku_items", name, "api_transport_price"], bd.api_transport_price);
                                      form.setFieldValue(
                                        ["sku_items", name, "add_on_api_transport_price"],
                                        bd.add_on_api_transport_price,
                                      );
                                      form.setFieldValue(["sku_items", name, "partner_id"], item.raw?.partner_id ?? "");
                                      form.setFieldValue(["sku_items", name, "brand_id"], item.raw?.brand_id ?? "");
                                      handleFormChange("transport_price", bd.transport_price);
                                    }}
                                    style={{
                                      border: active ? "1px solid #1890ff" : "1px solid #f0f0f0",
                                      background: active ? "#e6f7ff" : "#fff",
                                      height: "100%",
                                      minHeight: 65,
                                      padding: 10,
                                      paddingTop: 10,
                                      paddingBottom: 10,
                                    }}
                                  >
                                    <Row align="middle" justify="space-between" gutter={8} wrap={false}>
                                      <Col>
                                        <TransportLogo
                                          src={item.logo}
                                          alt={item.carrier}
                                          height={40}
                                          width={64}
                                        />
                                      </Col>
                                      <Col flex="auto">
                                        <Typography.Text strong>{item.name}</Typography.Text>
                                        {/*em.delivery_time ? (
                                          <div style={{ marginTop: 2, color: "#666", fontSize: 12 }}>
                                            ระยะเวลาจัดส่ง: {formatDeliveryTimeTh(item.delivery_time)}
                                          </div>
                                        ) : (
                                          <div style={{ height: 18 }} />
                                         )}*/}
                                      </Col>
                                      <Col>
                                        <div style={{ minWidth: 120, textAlign: "right", paddingRight: 10 }}>
                                          <Typography.Text strong>{`${formatWithCommas(
                                            item.transport_price ?? item.total_with_add_on ?? item.total,
                                          )} บาท`}</Typography.Text>
                                        </div>
                                      </Col>
                                    </Row>
                                  </Card>
                                </Col>
                              );
                            })}
                          </Row>
                          {!isSelfShipping && !speedLoading && !speedError && speedItems.length === 0 ? (
                            <Typography.Text style={{ color: "#999" }}>ยังไม่มีรายการขนส่ง</Typography.Text>
                          ) : null}
                        </div>
                      </>
                    ) : null}
                  </>
                ) : null}
              </Col>
            </Row>
            </>
          )}
            </>
            )}
          </Col>
        </Row>

        {/* ✅ ซ่อน Card ราคาค่าขนส่งที่เลือก เมื่อเลือก "ส่งเอง" (isSelfShipping = true) */}
        {!isSelfShipping && (
          <Row gutter={16} style={{ marginTop: 10 }}>
            <Col className="gutter-row" span={24}>
              <Row gutter={[16, 16]}>
                <Col className="gutter-row" span={24}>
                  <h4>สรุปค่าจัดส่ง</h4>
                </Col>
              </Row>
              <Row gutter={16} align="middle" style={{ width: '100%', }}>
                <Col span={5}>
                  <Typography.Text>
                    ค่าจัดส่ง : 
                  </Typography.Text>
                </Col>
                <Col span={17}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, width: '100%' }}>
                    <Form.Item
                      name={[name, "transport_price"]}
                      initialValue={0}
                      rules={[
                        {
                          validator: (_, value) => {
                            // ถ้าไม่กรอกอะไรเลย ให้ผ่าน (ไม่บังคับ)
                            if (!value || value === 0) {
                              return Promise.resolve();
                            }
                            // ถ้ากรอกแล้ว ต้องมากกว่า 0
                            if (value > 0) {
                              return Promise.resolve();
                            }
                            return Promise.reject("ราคาที่โอนให้กับขนส่งต้องมากกว่า 0");
                          },
                        },
                      ]}
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <InputNumber<number>
                        // ✅ requirement:
                        // - ส่งเอง (self): disabled
                        // - ส่งกับแบ่งเบา (bangbow): เลือกจาก card (readOnly) ยกเว้นกรณี timeout ให้กรอกเองได้
                        disabled={isSelfShipping}
                        readOnly={!isSelfShipping && !speedTimeout && !manualBangbowTransportUnlocked}
                        style={{
                          width: "200px",
                          padding: "4px 8px",
                          textAlign: "right",
                          backgroundColor: "#fff0f6",                          
                        }}
                        formatter={formatWithCommas}
                        size="small"
                        min={0 as number}
                        max={999999 as number}
                        controls={false}
                        maxLength={6}
                        onKeyDown={integerGuards.onKeyDown}
                        onPaste={integerGuards.onPaste}
                        parser={(value) => {
                          const parsed = value?.replace(/\D/g, "").slice(0, 6);
                          return parsed ? parseInt(parsed) : 0;
                        }}
                        placeholder=""
                        onChange={(v) => handleFormChange("transport_price", v)}
                        // ✅ ถ้าเป็น 0 แล้ว focus ให้ 0 หายไปเพื่อพร้อมกรอกข้อมูล (เหมือน base_factory_price)
                        onFocus={() => handleFocus("transport_price")}
                      // ✅ หมายเหตุ: ไม่ควรลบค่าที่ไม่ใช่ 0 เมื่อคลิก (ให้ behavior เหมือน payment_getway)
                      />
                    </Form.Item>

                    {!isSelfShipping && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: 'row', width: '100%', }}>
                        <TransportLogo src={displayTransportLogo} alt="transport-logo" height={26} width={64} />
                        <Typography.Text strong style={{ width: '100%' }}>
                          {/* ✅ ใช้ชื่อจาก displayTransportName แทน if-else แบบเดิม */}
                          {displayTransportName}
                        </Typography.Text>
                      </div>
                    )}
                  </div>

                  {/* ✅ hidden fields: persist selection to API */}
                  <Form.Item name={[name, "api_transport_carrier_code"]} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, "api_transport_logo"]} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, "api_transport_price"]} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, "add_on_api_transport_price"]} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, "partner_id"]} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, "brand_id"]} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, "merchants"]} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[name, "merchant_id"]} hidden>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row style={{ marginTop: 10 }}>
                <Col span={24}>
                  <Typography.Text style={{ fontSize: 13 }}>
                    หมายเหตุ : หากผู้ซื้อ เลือกรับสินค้า ณ จุดจำหน่าย ค่าขนส่งนี้จะถูกตัดออกเป็นส่วนลดจากราคาขายพร้อมส่ง
                  </Typography.Text>
                </Col>
              </Row>
            </Col>

          </Row>
        )}
        {hasShippingMethodSelected && (
        <>
        <Row gutter={16} style={{ marginTop: 10 }}>
          <Col span={24}>
            <h4>การแสดงค่าจัดส่งหน้าร้าน</h4>
          </Col>
        </Row>
        <Form.Item
          name={[name, "include_transport_price"]}
          initialValue={true}
          style={{ marginBottom: 0 }}
        >
          <Radio.Group
            style={{ width: "100%" }}
            onChange={(e) => handleFormChange("include_transport_price", e.target.value)}
          >
            {/* ✅ [requirement 2026-10-16] แยกตัวเลือกเป็น 2 column ซ้าย-ขวา แต่ละฝั่งมีรูปตัวอย่าง
                 อยู่ด้านล่างตลอด — ฝั่งที่ไม่ได้เลือกให้รูปจางลง (ไม่ซ่อน/สลับเหมือนเดิม) */}
            <Row gutter={16}>
              <Col span={12}>
                <Tooltip title="แสดงราคาขายสินค้าและราคาขนส่งรวมกัน">
                <Radio value={true}>ค่าขนส่งรวมในราคาสินค้า</Radio>
                </Tooltip>
                <Typography.Text
                  style={{ display: "block", marginTop: 6, fontSize: 13 }}
                >
                  ค่าจัดส่งจะถูกรวมไว้ในราคาสินค้า ระบบจะไม่แสดงค่าขนส่ง
                </Typography.Text>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues: any, currentValues: any) =>
                    prevValues.sku_items?.[name]?.include_transport_price !==
                    currentValues.sku_items?.[name]?.include_transport_price
                  }
                >
                  {({ getFieldValue }) => {
                    const isSelected =
                      getFieldValue(["sku_items", name, "include_transport_price"]) !== false;
                    return (
                      <Card
                        size="small"
                        bodyStyle={{ padding: 8, textAlign: "center" }}
                        style={{ marginTop: 8 }}
                      >
                        <img
                          src="/in-tp.png"
                          alt="ค่าขนส่งรวมในราคาสินค้า"
                          style={{
                            maxWidth: "70%",
                            height: "auto",
                            opacity: isSelected ? 1 : 0.35,
                            transition: "opacity 0.2s ease",
                          }}
                        />
                      </Card>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Tooltip title="แสดงราคาขายสินค้าและราคาขนส่งแยกกัน">
                  <Radio value={false}>ค่าขนส่งไม่รวมในราคาสินค้า</Radio>
                </Tooltip>
                <Typography.Text
                  style={{ display: "block", marginTop: 6, fontSize: 13 }}
                >
                  ค่าจัดส่งจะไม่ถูกรวมไว้ในราคาสินค้า ระบบจะแสดงค่าขนส่งแยกจากราคาสินค้า
                </Typography.Text>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues: any, currentValues: any) =>
                    prevValues.sku_items?.[name]?.include_transport_price !==
                    currentValues.sku_items?.[name]?.include_transport_price
                  }
                >
                  {({ getFieldValue }) => {
                    const isSelected =
                      getFieldValue(["sku_items", name, "include_transport_price"]) === false;
                    return (
                      <Card
                        size="small"
                        bodyStyle={{ padding: 8, textAlign: "center" }}
                        style={{ marginTop: 8 }}
                      >
                        <img
                          src="/ex-tp.png"
                          alt="ค่าขนส่งไม่รวมในราคาสินค้า"
                          style={{
                            maxWidth: "70%",
                            height: "auto",
                            opacity: isSelected ? 1 : 0.35,
                            transition: "opacity 0.2s ease",
                          }}
                        />
                      </Card>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
          </Radio.Group>
        </Form.Item>
        </>
        )}
      </Card>
      {/* <Card className="sku-section-card" style={{ width: "95%", padding: 15, marginTop: 15 }}>
  
      </Card> */}

      {/* Card สำหรับคอมมิชชั่น 4 ระดับ */}
      <Card className="sku-section-card" style={{ width: "95%", padding: 15, marginTop: 15 }}>
        <Row gutter={16}>
          <Col className="gutter-row" span={24}>
            <Row gutter={[16, 16]}>
              <Col className="gutter-row" span={16}>
                <h4>3. กำหนดค่าตอบแทนรวมในรูปแบบของพอยต์และคอมมิชชั่น</h4>
              </Col>
            </Row>
            {/* ✅ Field ใหม่: ค่าคอมมิชชั่นและค่าดำเนินการ */}
            <Row gutter={16} style={{ marginTop: 15 }} align="middle">
              <Col span={8} >
                <div style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Typography.Text>
                    ค่าตอบแทนรวม :
                  </Typography.Text>
                </div>
              </Col>
              <Col span={10}>
                <Form.Item
                  name={[name, "total_commission"]}
                  initialValue={
                    editMode && editData
                      ? editData.price?.total_commission || 0
                      : copyMode
                        ? undefined // ให้ form ใช้ค่าจาก form.setFieldsValue แทน
                        : 0
                  }
                  dependencies={[
                    ['sku_items', name, 'commistion_lv1'],
                    ['sku_items', name, 'commistion_lv2'],
                    ['sku_items', name, 'commistion_lv3'],
                    ['sku_items', name, 'commistion_lv4'],
                    ['sku_items', name, 'commistion_lv5'],
                    ['sku_items', name, 'input_market_price'] // ✅ เพิ่ม dependency เพื่อ revalidate เมื่อราคาตลาดเปลี่ยน (ใช้คำนวณราคาแนะนำ)
                  ]}
                  rules={[
                    {
                      // ✅ message สั้นเป็น fallback กรณี UI ไม่ดึงข้อความจาก Promise.reject (locale Ant Design)
                      // ✅ Requirement ใหม่ (2026-08-13): อนุญาตยอดรวมไม่ถึง (คงเหลือ >= 0) แต่ห้ามคงเหลือติดลบ
                      message:
                        "คงเหลือติดลบไม่ได้ — ผลรวมแยกระดับห้ามเกินค่าแนะนำและคอมมิชชั่น",
                      validator: (_, value) => {
                        // ✅ ถ้าบันทึกร่าง skip validation
                        if (isDraftSave) {
                          return Promise.resolve();
                        }
                        // ✅ อ่านยอดรวมจากฟอร์มเป็นหลัก + normalize (string/คอมม่า/ค่าว่าง) ให้ตรงกับ rule อื่นในไฟล์
                        const rawTotal =
                          form.getFieldValue(["sku_items", name, "total_commission"]) ?? value;
                        const total = round2(toFiniteNumberOr0(rawTotal));
                        // ✅ ถ้า total_commission = 0 หรือไม่กรอก ให้ผ่าน (ไม่บังคับ)
                        if (!total || total === 0) {
                          return Promise.resolve();
                        }
                        // ✅ ตรวจสอบว่าผลรวม c1+c2+c3+c4+c5 ไม่เกิน total_commission (parse ทุกระดับแบบเดียวกับยอดรวม)
                        const formValues = form.getFieldValue(["sku_items", name]) || {};
                        const c1 = round2(toFiniteNumberOr0(formValues?.commistion_lv1));
                        const c2 = round2(toFiniteNumberOr0(formValues?.commistion_lv2));
                        const c3 = round2(toFiniteNumberOr0(formValues?.commistion_lv3));
                        const c4 = round2(toFiniteNumberOr0((formValues as any)?.commistion_lv4));
                        const c5 = round2(toFiniteNumberOr0((formValues as any)?.commistion_lv5));
                        const sum = round2(c1 + c2 + c3 + c4 + c5);
                        const remaining = round2(total - sum);

                        // ✅ ถ้า showCommissionFields = false (ยังไม่เปิด field c1-c5) ให้ผ่าน
                        if (!showCommissionFields) {
                          return Promise.resolve();
                        }

                        // ✅ เดิม (ก่อน 2026-08-13): บังคับผลรวม c1–c5 ต้องเท่ากับ total_commission เมื่อเปิดแสดงระดับแล้ว
                        // if (Math.abs(sum - total) > 0.01) {
                        //   // ✅ สาเหตุ + แนวทางแก้สั้นๆ (ไม่พึ่งข้อความทั่วไปของฟอร์มอย่างเดียว)
                        //   return Promise.reject(
                        //     `ยอดรวมด้านบน ${total.toFixed(
                        //       2
                        //     )} บาท ไม่ตรงกับผลรวมแยกระดับ ${sum.toFixed(
                        //       2
                        //     )} บาท (ผู้แนะนำร้าน+ดูแลผู้แนะนำ+ผู้แนะนำ+ผู้ซื้อ+การตลาด) — ทำไม: ระบบบังคับให้ผลรวมระดับเท่ายอดรวมเมื่อเปิดแสดงระดับแล้ว — แก้โดยปรับตัวเลขแยกระดับให้รวมเท่ายอดรวม หรือแก้ยอดรวมแล้วพิมพ์/เลิก focus ช่องยอดรวมเพื่อให้ระบบคำนวณแยกระดับใหม่`
                        //   );
                        // }
                        // ✅ Requirement ใหม่ (2026-08-13): อนุญาตบันทึกถ้ายอดรวมไม่ถึง (คงเหลือ >= 0)
                        //    ห้ามเฉพาะกรณีคงเหลือติดลบ (ผลรวมแยกระดับเกินยอดรวม)
                        if (remaining < -0.01) {
                          return Promise.reject(
                            `คงเหลือติดลบไม่ได้ — ผลรวมแยกระดับ ${sum.toFixed(
                              2
                            )} บาท เกินยอดรวม ${total.toFixed(
                              2
                            )} บาท (ผู้แนะนำร้าน+ดูแลผู้แนะนำ+ผู้แนะนำ+ผู้ซื้อ+การตลาด) — แก้โดยลดค่าแยกระดับให้ไม่เกินยอดรวม`
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                    {
                      // ✅ Rule ใหม่: C ต้องไม่น้อยกว่าราคาแนะนำ (เฉพาะ create mode)
                      // สูตร: recommended = round2(input_market_price × percentCommission / 100)
                      // ⚠️ Requirement ใหม่ (2026-07-14): create mode ไม่ต้องบังคับ rule นี้แล้ว
                      //    ให้เหมือน edit/copy mode → validator นี้ไม่ reject อีกต่อไป
                      //    (คง logic/คอมเมนต์เดิมไว้เป็นประวัติ ตามข้อตกลง ห้ามลบ)
                      validator: (_, value) => {
                        // ✅ ถ้าบันทึกร่าง skip validation
                        if (isDraftSave) {
                          return Promise.resolve();
                        }
                        // ✅ Requirement ใหม่ (2026-07-14): create mode ไม่บังคับ rule นี้แล้ว
                        //    เดิมบังคับเฉพาะ create (edit/copy skip); ตอนนี้ให้ทุกโหมด skip เหมือนกัน
                        //    ใช้ flag เพื่อคง logic เดิมด้านล่างไว้เป็นประวัติ (ตามข้อตกลง ห้ามลบ)
                        const enforceMinCommissionRule: boolean = false;
                        if (!enforceMinCommissionRule || editMode || copyMode) {
                          return Promise.resolve();
                        }
                        // ✅ ถ้ายังไม่มีราคาตลาด หรือไม่มี % commission ของหมวดหมู่ → ยังไม่บังคับ
                        const retail = toFiniteNumberOr0(
                          form.getFieldValue(["sku_items", name, "input_market_price"])
                        );
                        if (retail <= 0 || percentCommission <= 0) {
                          return Promise.resolve();
                        }
                        const recommended = round2((retail * percentCommission) / 100);
                        // ✅ อ่านค่า total_commission ตรงจาก form แทน param value
                        // เหตุผล: NumericFormat ไม่ได้เป็น controlled child ของ AntD Form.Item
                        //        → param value อาจเป็นค่าเก่าตอน validator รัน (จาก dependency trigger)
                        //        → ใช้ form.getFieldValue เพื่อให้ได้ค่าล่าสุดเสมอ (pattern เดียวกับ validator c1-c5)
                        const currentRaw = form.getFieldValue(["sku_items", name, "total_commission"]);
                        const current = round2(toFiniteNumberOr0(currentRaw));
                        // ✅ ใช้ tolerance 0.01 กัน floating-point precision (เช่น 10.00 vs 9.9999999)
                        // เทียบแนวทางเดียวกับ validator c1+..+c5 = total_commission ที่ใช้ Math.abs > 0.01
                        if (recommended - current > 0.01) {
                          return Promise.reject(
                            `ค่าคอมมิชชั่นและค่าดำเนินการต้องมากกว่าหรือเท่ากับราคาแนะนำ (${recommended.toFixed(2)})`
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  {/* <InputNumber<number>
                    style={{
                      width: "100%",
                      marginLeft: "auto",
                      display: "block",
                      padding: "4px 8px",
                      textAlign: "right",
                    }}
                    formatter={formatWithCommas}
                    parser={parseNumberWithoutCommas}
                    size="small"
                    min={0}
                    max={999999.99}
                    controls={false}
                    precision={2}
                    step={0.01}
                    onKeyDown={decimal2Guards.onKeyDown}
                    onPaste={decimal2Guards.onPaste}
                    placeholder="0.00"
                    onChange={(value) => {
                      // ✅ ถ้ากำลังลบจนว่าง ให้คงค่าไว้ (จะคืน 0.00 ตอน blur ตาม logic เดิม)
                      if (value === null || Number.isNaN(value)) return;
                      handleTotalCommissionChange(value);
                    }}
                    onFocus={() => handleFocus("total_commission")}
                    onBlur={() => {
                      // ✅ ถ้า focus แล้วลบ 0.00 ออก แต่ผู้ใช้ไม่ได้กรอกอะไร → blur แล้วให้คืนค่า 0.00 กลับมา
                      // ทำเหมือน base_factory_price: คืนค่าใน field ให้เป็น 0 แล้วค่อย trigger logic
                      handleBlurRestoreZeroIfEmpty("total_commission", (restoredValue) => {
                        handleTotalCommissionChange(restoredValue);
                      });
                    }}
                  /> */}

                  <NumericFormat
                    customInput={Input}
                    style={{
                      width: "100%",
                      padding: "4px 8px",
                      textAlign: "right",
                      backgroundColor: "#fff0f6",
                    }}
                    size="small"
                    thousandSeparator=","
                    decimalSeparator="."
                    decimalScale={2}
                    fixedDecimalScale={true}
                    allowNegative={false}
                    // ✅ NumericFormat ไม่รองรับ prop min/max (จะกลายเป็น attribute ของ <input type="text"> ซึ่งไม่มีผล)
                    //    ต้องกันด้วย isAllowed เท่านั้น — เพดานต้องเท่ากับ max ของช่อง c1-c5 (999,999.99)
                    //    ไม่งั้นยอดรวมจะเกินจนแยกระดับแล้ว c1-c5 โดน InputNumber clamp → ผลรวมไม่ตรงยอดรวม
                    isAllowed={({ floatValue }) =>
                      floatValue === undefined || floatValue <= 999999.99
                    }
                    onValueChange={(values) => {
                      const baseFactoryPrice = form.getFieldValue(["sku_items", name, "base_factory_price"]);
                      const floatValue = values.floatValue;

                      // ✅ Update Form State manually
                      if (floatValue !== undefined && floatValue !== null) {
                        form.setFieldValue(["sku_items", name, "total_commission"], floatValue); // ✅ เพิ่มบรรทัดนี้
                        // ✅ validateFields อยู่ท้าย handleTotalCommissionChange เท่านั้น (หลังอัปเดต c1–c5)
                        //    กัน race: ถ้า validate ทันทีตรงนี้ ผลรวม c1–c5 อาจยังเป็นค่าเก่า → error ฟิลด์ total_commission
                      }

                      // ✅ ถ้า baseFactoryPrice <= 0 และ floatValue เป็น 0 อยู่แล้ว (จากการ setFieldValue ก่อนหน้า) → ไม่ต้องทำอะไร
                      if (baseFactoryPrice !== undefined && baseFactoryPrice !== null && baseFactoryPrice <= 0) {
                        // ✅ ถ้าค่าปัจจุบันใน field เป็น 0 อยู่แล้ว → ไม่ต้อง set อีก (ป้องกัน recursive call)
                        if (floatValue === 0 || floatValue === undefined || floatValue === null) {
                          notification.error({
                            message: 'ราคาฐานโรงงานต้องมากกว่า 0',
                          });
                          return;
                        }

                        // ✅ ถ้ายังไม่เป็น 0 → set เป็น 0 และแสดง error
                        notification.error({
                          message: 'ราคาฐานโรงงานต้องมากกว่า 0',
                        });
                        // ✅ ใช้ setTimeout เพื่อป้องกัน recursive call
                        setTimeout(() => {
                          form.setFieldValue(["sku_items", name, "total_commission"], 0);
                        }, 0);
                        return;
                      } else {
                        /* if (values.floatValue !== undefined && values.floatValue !== null && values.floatValue > baseFactoryPrice) {
                           notification.error({
                             message: 'ราคาค่าคอมต้องไม่มากกว่าราคาฐานโรงงาน (A)',
                           });
                           setTimeout(() => {
                             form.setFieldValue(["sku_items", name, "total_commission"], baseFactoryPrice);
                           }, 0);
                           return;
                         } else {*/
                        // ✅ ถ้ากำลังลบจนว่าง ให้คงค่าไว้ (จะคืน 0.00 ตอน blur ตาม logic เดิม)
                        if (floatValue === undefined || floatValue === null || Number.isNaN(floatValue)) return;
                        if (editMode || copyMode) {
                          scheduleTotalCommissionRecalcFromForm();
                        } else {
                          handleTotalCommissionChange(floatValue);
                        }
                        //}
                      }

                    }}
                    onFocus={() => handleFocus("total_commission")}
                    onInput={() => {
                      if (editMode || copyMode) {
                        scheduleTotalCommissionRecalcFromForm();
                      }
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      decimal2Guards.onKeyDown(e);
                      if ((editMode || copyMode) && e.key === "Enter") {
                        e.preventDefault();
                        flushTotalCommissionRecalcFromForm();
                      }
                    }}
                    onPaste={decimal2Guards.onPaste}
                    onBlur={() => {
                      // ✅ ถ้า focus แล้วลบ 0.00 ออก แต่ผู้ใช้ไม่ได้กรอกอะไร → blur แล้วให้คืนค่า 0.00 กลับมา
                      // ทำเหมือน base_factory_price: คืนค่าใน field ให้เป็น 0 แล้วค่อย trigger logic
                      handleBlurRestoreZeroIfEmpty("total_commission", (restoredValue) => {
                        handleTotalCommissionChange(restoredValue);
                      });
                      // ✅ edit/copy: flush debounce + คำนวณทันทีเมื่อออกจากช่อง (ไม่ต้องรอให้ยัง focus อยู่)
                      if (editMode || copyMode) {
                        flushTotalCommissionRecalcFromForm();
                      }
                    }}
                  />
                </Form.Item>
              </Col>

            </Row>
            <Row>
              <Col span={18} style={{ textAlign: "right" }}>
                {/* ✅ แสดงตัวเลขราคาแนะนำแบบ real-time ตาม input_market_price × percentCommission / 100
                    ใช้ Form.Item shouldUpdate เพื่อ re-render เฉพาะเมื่อ input_market_price ของ SKU นี้เปลี่ยน */}
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => {
                    const p = prev?.sku_items?.[name]?.input_market_price;
                    const c = cur?.sku_items?.[name]?.input_market_price;
                    return p !== c;
                  }}
                >
                  {({ getFieldValue }) => {
                    const retail = toFiniteNumberOr0(
                      getFieldValue(["sku_items", name, "input_market_price"])
                    );
                    const recommended = round2((retail * percentCommission) / 100);
                    return (
                      <Typography.Text style={{ fontSize: 14, color: "#666" }}>
                        แนะนำอย่างน้อย {recommended.toFixed(2)} บาท เพื่อช่วยจูงใจให้เกิดการแนะนำสินค้า
                      </Typography.Text>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col span={6} style={{ textAlign: "right" }}></Col>
            </Row>
            {/* ✅ Password form - แสดงเมื่อคลิกปุ่ม */}
            {showPasswordForm && (
              <Row gutter={16} style={{ marginTop: 12 }} align="middle">
                <Col span={8}>
                  <Typography.Text>รหัสผ่าน:</Typography.Text>
                </Col>
                <Col span={10}>
                  <Input.Password
                    ref={passwordInputRef}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError("");
                    }}
                    onFocus={handlePasswordFocus}
                    placeholder="กรุณากรอกรหัสผ่าน"
                    onPressEnter={handleConfirmPassword}
                    status={passwordError ? "error" : ""}
                    style={{ width: "100%" }}
                  />
                  {passwordError && (
                    <Typography.Text type="danger" style={{ fontSize: 12 }}>
                      {passwordError}
                    </Typography.Text>
                  )}
                </Col>
                <Col span={6}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      type="primary"
                      onClick={handleConfirmPassword}
                      style={{ flex: 1 }}
                    >
                      ยืนยัน
                    </Button>
                    <Button
                      onClick={handleClosePasswordForm}
                      style={{ flex: 1 }}
                    >
                      ยกเลิก
                    </Button>
                  </div>
                </Col>
              </Row>
            )}
            {/* ✅ Field ระดับคอมมิชชั่น - ซ่อนด้วย display: none */}
            <div style={{ display: showCommissionFields || userRole === 'admin' ? "block" : "none" }}>
              {/* คอมมิชชั่นคงเหลือ — แสดงเป็นตัวเลขอ่านอย่างเดียว ไม่ใช่ input (Requirement 2026-08-13) */}
              <Row gutter={16} style={{ marginTop: 15 }} align="middle">
                <Col span={8} />
                <Col span={16}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={commissionLvLabelBoxStyle}>
                      <Typography.Text style={commissionLvLabelStyle}>
                        คงเหลือ:
                      </Typography.Text>
                    </div>
                    <Typography.Text
                      style={{
                        ...commissionLvInputStyle,
                        color: commissionRemaining < -0.01 ? "#ff4d4f" : "#1677ff",
                        fontVariantNumeric: "tabular-nums",
                        cursor: "default",
                      }}
                    >
                      {commissionRemaining.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography.Text>
                  </div>
                </Col>
              </Row>
              {/* การตลาด (lv5) — แสดงเฉพาะ 414428 หรือ admin */}
              <Row gutter={16} style={{ marginTop: 12, display: showAllCommissionFields || userRole === 'admin' ? undefined : "none" }} align="middle">
                <Col span={8} />
                <Col span={16}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={commissionLvLabelBoxStyle}>
                      <Typography.Text style={commissionLvLabelStyle}>
                        การตลาด:
                      </Typography.Text>
                    </div>
                    <Form.Item
                      name={[name, "commistion_lv5"]}
                      initialValue={0}
                      dependencies={[
                        ['sku_items', name, 'total_commission'],
                        ['sku_items', name, 'commistion_lv1'],
                        ['sku_items', name, 'commistion_lv2'],
                        ['sku_items', name, 'commistion_lv3'],
                        ['sku_items', name, 'commistion_lv4']
                      ]}
                      rules={[
                        {
                          validator: (_, value) => {
                            // ✅ ถ้าบันทึกร่าง skip validation
                            if (isDraftSave) {
                              return Promise.resolve();
                            }
                            // ✅ ถ้า showCommissionFields = false ให้ผ่าน
                            if (!showCommissionFields) {
                              return Promise.resolve();
                            }
                            // ✅ ถ้า total_commission = 0 หรือไม่กรอก ให้ผ่าน
                            const formValues = form.getFieldValue(["sku_items", name]);
                            const totalCommission = formValues?.total_commission || 0;
                            if (!totalCommission || totalCommission === 0) {
                              return Promise.resolve();
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={commissionLvInputStyle}
                        formatter={formatWithCommas}
                        parser={parseNumberWithoutCommas}
                        size="small"
                        min={0}
                        max={999999.99}
                        controls={false}
                        precision={2}
                        step={0.01}
                        onKeyDown={decimal2Guards.onKeyDown}
                        onPaste={decimal2Guards.onPaste}
                        placeholder="0.00"
                        // ✅ ไม่ใช้ c1-c5 ในการคำนวณแล้ว → ไม่ต้อง trigger การคำนวณจาก field นี้
                        onChange={() => undefined}
                        onFocus={() => handleFocus("commistion_lv5")}
                      />
                    </Form.Item>
                  </div>
                  
                </Col>
              </Row>
              {/* ผู้แนะนำร้าน (lv1) — แสดงเฉพาะ 414428 หรือ admin */}
              <Row gutter={16} style={{ marginTop: 12, display: showAllCommissionFields || userRole === 'admin' ? undefined : "none" }} align="middle">
                <Col span={8} />
                <Col span={16}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={commissionLvLabelBoxStyle}>
                      <Typography.Text style={commissionLvLabelStyle}>
                        ผู้แนะนำร้าน:
                      </Typography.Text>
                    </div>
                    <Form.Item
                      name={[name, "commistion_lv1"]}
                      initialValue={0}
                      dependencies={[
                        ['sku_items', name, 'total_commission'],
                        ['sku_items', name, 'commistion_lv2'],
                        ['sku_items', name, 'commistion_lv3']
                      ]}
                      rules={[
                        {
                          validator: (_, value) => {
                            // ✅ ถ้าบันทึกร่าง skip validation
                            if (isDraftSave) {
                              return Promise.resolve();
                            }
                            // ✅ ถ้า showCommissionFields = false (ยังไม่เปิด field c1-c3) ให้ผ่าน
                            if (!showCommissionFields) {
                              return Promise.resolve();
                            }
                            // ✅ ตรวจสอบว่าผลรวม c1+c2+c3 = total_commission
                            const formValues = form.getFieldValue(["sku_items", name]);
                            const totalCommission = formValues?.total_commission || 0;
                            const c1 = value || 0;
                            const c2 = formValues?.commistion_lv2 || 0;
                            const c3 = formValues?.commistion_lv3 || 0;
                            const sum = c1 + c2 + c3;

                            // ✅ ถ้า total_commission = 0 หรือไม่กรอก ให้ผ่าน
                            if (!totalCommission || totalCommission === 0) {
                              return Promise.resolve();
                            }

                            // ✅ ถ้าเปิด field c1-c3 แล้ว ต้องตรวจสอบว่าผลรวมเท่ากับ total_commission
                            // if (Math.abs(sum - totalCommission) > 0.01) { // ใช้ tolerance 0.01 สำหรับทศนิยม
                            //   return Promise.reject(`ผลรวมของคอมมิชชั่น (${sum.toFixed(2)}) ต้องเท่ากับค่าคอมมิชชั่นและค่าดำเนินการ (${totalCommission.toFixed(2)})`);
                            // }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={commissionLvInputStyle}
                        formatter={formatWithCommas}
                        parser={parseNumberWithoutCommas}
                        size="small"
                        min={0}
                        max={999999.99}
                        controls={false}
                        precision={2}
                        step={0.01}
                        onKeyDown={decimal2Guards.onKeyDown}
                        onPaste={decimal2Guards.onPaste}
                        placeholder="0.00"
                        // ✅ ไม่ใช้ c1-c3 ในการคำนวณแล้ว → ไม่ต้อง trigger การคำนวณจาก field นี้
                        onChange={() => undefined}
                        onFocus={() => handleFocus("commistion_lv1")}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 12 }} align="middle">
                <Col span={8} />
                <Col span={16}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={commissionLvLabelBoxStyle}>
                      <Typography.Text style={commissionLvLabelStyle}>
                        ดูแลผู้แนะนำ:
                      </Typography.Text>
                    </div>
                    <Form.Item
                      name={[name, "commistion_lv2"]}
                      initialValue={0}
                      dependencies={[
                        ['sku_items', name, 'total_commission'],
                        ['sku_items', name, 'commistion_lv1'],
                        ['sku_items', name, 'commistion_lv3']
                      ]}
                      rules={[
                        {
                          validator: (_, value) => {
                            // ✅ ถ้าบันทึกร่าง skip validation
                            if (isDraftSave) {
                              return Promise.resolve();
                            }
                            // ✅ ถ้า showCommissionFields = false (ยังไม่เปิด field c1-c3) ให้ผ่าน
                            if (!showCommissionFields) {
                              return Promise.resolve();
                            }
                            // ✅ ตรวจสอบว่าผลรวม c1+c2+c3 = total_commission
                            const formValues = form.getFieldValue(["sku_items", name]);
                            const totalCommission = formValues?.total_commission || 0;
                            const c1 = formValues?.commistion_lv1 || 0;
                            const c2 = value || 0;
                            const c3 = formValues?.commistion_lv3 || 0;
                            const sum = c1 + c2 + c3;

                            // ✅ ถ้า total_commission = 0 หรือไม่กรอก ให้ผ่าน
                            if (!totalCommission || totalCommission === 0) {
                              return Promise.resolve();
                            }

                            // ✅ ถ้าเปิด field c1-c3 แล้ว ต้องตรวจสอบว่าผลรวมเท่ากับ total_commission
                            // if (Math.abs(sum - totalCommission) > 0.01) { // ใช้ tolerance 0.01 สำหรับทศนิยม
                            //   return Promise.reject(`ผลรวมของคอมมิชชั่น (${sum.toFixed(2)}) ต้องเท่ากับค่าคอมมิชชั่นและค่าดำเนินการ (${totalCommission.toFixed(2)})`);
                            // }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={commissionLvInputStyle}
                        formatter={formatWithCommas}
                        parser={parseNumberWithoutCommas}
                        size="small"
                        min={0}
                        max={999999.99}
                        controls={false}
                        precision={2}
                        step={0.01}
                        onKeyDown={decimal2Guards.onKeyDown}
                        onPaste={decimal2Guards.onPaste}
                        placeholder="0.00"
                        // ✅ ไม่ใช้ c1-c3 ในการคำนวณแล้ว → ไม่ต้อง trigger การคำนวณจาก field นี้
                        onChange={() => undefined}
                        onFocus={() => handleFocus("commistion_lv2")}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 12 }} align="middle">
                <Col span={8} />
                <Col span={16}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={commissionLvLabelBoxStyle}>
                      <Typography.Text style={commissionLvLabelStyle}>
                        ผู้แนะนำ:
                      </Typography.Text>
                    </div>
                    <Form.Item
                      name={[name, "commistion_lv3"]}
                      initialValue={0}
                      dependencies={[
                        ['sku_items', name, 'total_commission'],
                        ['sku_items', name, 'commistion_lv1'],
                        ['sku_items', name, 'commistion_lv2']
                      ]}
                      rules={[
                        {
                          validator: (_, value) => {
                            // ✅ ถ้าบันทึกร่าง skip validation
                            if (isDraftSave) {
                              return Promise.resolve();
                            }
                            // ✅ ถ้า showCommissionFields = false (ยังไม่เปิด field c1-c3) ให้ผ่าน
                            if (!showCommissionFields) {
                              return Promise.resolve();
                            }
                            // ✅ ตรวจสอบว่าผลรวม c1+c2+c3 = total_commission
                            const formValues = form.getFieldValue(["sku_items", name]);
                            const totalCommission = formValues?.total_commission || 0;
                            const c1 = formValues?.commistion_lv1 || 0;
                            const c2 = formValues?.commistion_lv2 || 0;
                            const c3 = value || 0;
                            const sum = c1 + c2 + c3;

                            // ✅ ถ้า total_commission = 0 หรือไม่กรอก ให้ผ่าน
                            if (!totalCommission || totalCommission === 0) {
                              return Promise.resolve();
                            }

                            // ✅ ถ้าเปิด field c1-c3 แล้ว ต้องตรวจสอบว่าผลรวมเท่ากับ total_commission
                            // if (Math.abs(sum - totalCommission) > 0.01) { // ใช้ tolerance 0.01 สำหรับทศนิยม
                            //   return Promise.reject(`ผลรวมของคอมมิชชั่น (${sum.toFixed(2)}) ต้องเท่ากับค่าคอมมิชชั่นและค่าดำเนินการ (${totalCommission.toFixed(2)})`);
                            // }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={commissionLvInputStyle}
                        formatter={formatWithCommas}
                        parser={parseNumberWithoutCommas}
                        size="small"
                        min={0}
                        max={999999.99}
                        controls={false}
                        precision={2}
                        step={0.01}
                        onKeyDown={decimal2Guards.onKeyDown}
                        onPaste={decimal2Guards.onPaste}
                        placeholder="0.00"
                        // ✅ ไม่ใช้ c1-c3 ในการคำนวณแล้ว → ไม่ต้อง trigger การคำนวณจาก field นี้
                        onChange={() => undefined}
                        onFocus={() => handleFocus("commistion_lv3")}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 12 }} align="middle">
                <Col span={8} />
                <Col span={16}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={commissionLvLabelBoxStyle}>
                      <Typography.Text style={commissionLvLabelStyle}>
                        ผู้ซื้อ:
                      </Typography.Text>
                    </div>
                    <Form.Item
                      name={[name, "commistion_lv4"]}
                      initialValue={0}
                      dependencies={[
                        ['sku_items', name, 'total_commission'],
                        ['sku_items', name, 'commistion_lv1'],
                        ['sku_items', name, 'commistion_lv2'],
                        ['sku_items', name, 'commistion_lv3']
                      ]}
                      rules={[
                        {
                          validator: (_, value) => {
                            // ✅ ถ้าบันทึกร่าง skip validation
                            if (isDraftSave) {
                              return Promise.resolve();
                            }
                            // ✅ ถ้า showCommissionFields = false ให้ผ่าน
                            if (!showCommissionFields) {
                              return Promise.resolve();
                            }
                            // ✅ ถ้า total_commission = 0 หรือไม่กรอก ให้ผ่าน
                            const formValues = form.getFieldValue(["sku_items", name]);
                            const totalCommission = formValues?.total_commission || 0;
                            if (!totalCommission || totalCommission === 0) {
                              return Promise.resolve();
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={commissionLvInputStyle}
                        formatter={formatWithCommas}
                        parser={parseNumberWithoutCommas}
                        size="small"
                        min={0}
                        max={999999.99}
                        controls={false}
                        precision={2}
                        step={0.01}
                        onKeyDown={decimal2Guards.onKeyDown}
                        onPaste={decimal2Guards.onPaste}
                        placeholder="0.00"
                        // ✅ ไม่ใช้ c1-c5 ในการคำนวณแล้ว → ไม่ต้อง trigger การคำนวณจาก field นี้
                        onChange={() => undefined}
                        onFocus={() => handleFocus("commistion_lv4")}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Card>

      {/* ✅ Card: Payment Gateway */}
      <Card className="sku-section-card" style={{ width: "95%", padding: 15, marginTop: 12 }}>
        <Row gutter={16} align="top">
          <Col className="gutter-row" span={12}>
            <Row gutter={[16, 16]}>
              <Col className="gutter-row" span={24}>
                <h4>4. ค่าธรรมเนียมการรับชำระเงิน</h4>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12 }} align="middle">
              <Col span={14}>
                <Typography.Text>ค่าธรรมเนียมที่ระบบคำนวณ :</Typography.Text>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[name, "payment_getway"]}
                  initialValue={0}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber<number>
                    readOnly
                    style={{
                      width: "100%",
                      padding: "4px 8px",
                      textAlign: "right",
                      backgroundColor: "#fff0f6",
                    }}
                    formatter={formatWithCommasFixed2}
                    parser={parseNumberWithoutCommas}
                    size="small"
                    min={0 as number}
                    max={999999999 as number}
                    controls={false}
                    precision={2}
                    step={0.01}
                    onKeyDown={decimal2Guards.onKeyDown}
                    onPaste={decimal2Guards.onPaste}
                    placeholder="0.00"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col className="gutter-row" span={12}>
            <Card
              style={{
                border: "1px dashed rgb(200, 199, 199)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <div style={{ padding: "16px" }}>
                <Typography.Title level={5} style={{ color: "#1890ff", marginTop: 0 }}>
                  💡 คำอธิบาย Payment Gateway
                </Typography.Title>
                <Typography.Paragraph style={{ marginBottom: 0, paddingLeft: 16 }}>
                  •ค่าบริการรับชำระเงินผ่านระบบ คิด 3.3% (รวม VAT แล้ว) ของยอดชำระ ระบบจะคำนวณให้อัตโนมัติและรวมไว้ในราคาที่ลูกค้าชำระแล้ว
                </Typography.Paragraph>
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      {/*การกำหนดราคา*/}

      {/*ราคาสุดท้าย & ราคาต่อหน่วย*/}
      <Card className="sku-section-card" style={{ width: "95%", padding: 15, marginTop: 15 }}>
        {/* ✅ อัปเดต: ใส่ align="stretch" ให้ชัดเจน (default อยู่แล้ว) เพื่อให้ Col ซ้าย/ขวา สูงเท่ากัน
             แล้วใช้ flex-column + marginTop:auto ฝั่งซ้าย ดัน block ราคาไปชิดล่างให้ตรงกับฝั่งขวา */}
        <Row gutter={16} align="stretch">
          {/* ✅ ฝั่งซ้าย (ราคาสุดท้าย) — ทำเป็น flex column เพื่อให้ดัน "block ราคา" ไปชิดล่าง
               เหตุผล: ฝั่งขวามี 3 แถว (จำนวนต่อหน่วย + ราคาขาย + ราคาท้องตลาด) ส่วนฝั่งซ้าย
               มีแค่ 2 แถว (ราคาขาย + ราคาตลาดที่แสดง) ทำให้ระดับแถวล่างสุดไม่ตรงกัน */}
          <Col
            className="gutter-row"
            span={12}
            style={{ display: "flex", flexDirection: "column" }}
          >
            <Row gutter={[16, 16]}>
              <Col className="gutter-row" span={16}>
                <h4>ราคาที่ลูกค้าเห็นบนแพลตฟอร์ม</h4>
              </Col>
            </Row>
            {/* ✅ wrapper สำหรับ "block ราคา" ทั้งหมดของฝั่งซ้าย — marginTop:auto ดันลงชิดล่าง
                 เพื่อให้แถวสุดท้าย (ราคาตลาดที่แสดง / wholesale / VIP) อยู่ระดับเดียวกับ
                 แถวสุดท้ายของฝั่งขวา (ราคาท้องตลาด) */}
            <div style={{ marginTop: "auto" }}>
              {/* ✅ ข้อความเตือนสีเทา — แสดงเมื่อ isOverMarket = true
                   (retail_price "ราคาตลาดที่แสดง" > input_market_price "ราคาตลาด")
                   ใช้ Form.Item shouldUpdate pattern เดียวกับด้านล่าง (บรรทัด ~4083-4104)
                   เพื่อ re-render เฉพาะเมื่อค่าที่เกี่ยวข้องของ SKU นี้เปลี่ยน */}
              <Form.Item
                shouldUpdate={(prevValues: any, currentValues: any) => {
                  const prevItem = prevValues.sku_items?.[name];
                  const curItem = currentValues.sku_items?.[name];
                  return (
                    prevItem?.retail_price !== curItem?.retail_price ||
                    prevItem?.input_market_price !== curItem?.input_market_price
                  );
                }}
                noStyle
              >
                {({ getFieldValue }: any) => {
                  const retailPriceVal = getFieldValue(["sku_items", name, "retail_price"]);
                  // ✅ ดึง "ราคาตลาด" (input_market_price) ด้านบน มาเปรียบเทียบ
                  const inputMarketPriceVal = getFieldValue(["sku_items", name, "input_market_price"]);
                  const retailNum = toFiniteNumberOr0(retailPriceVal);
                  const marketNum = toFiniteNumberOr0(inputMarketPriceVal);
                  // ✅ เงื่อนไขเดียวกับช่อง "ราคาตลาดที่แสดง" ด้านล่าง
                  //    (marketNum > 0 เพื่อกันกรณียังไม่ได้กรอกราคาตลาด)
                  const isOverMarket = marketNum > 0 && retailNum > marketNum;
                  if (!isOverMarket) return null;
                  return (
                    // ✅ ใช้ Row/Col โครงเดียวกับแถว "ราคาขาย" ด้านล่าง (span={8} + span={10})
                    //    เพื่อให้ข้อความเตือนอยู่ตรงกับ "ช่อง input" (ไม่ใช่ตรง label)
                    //    และแสดงเป็น 2 บรรทัดโดยใช้ <div> สองอัน
                    <Row gutter={16} style={{ marginBottom: 8 }}>
                      <Col span={22}>
                        <div
                          style={{
                            color: "#ff4d4f",
                            fontSize: 14,
                            textAlign: "left",
                            lineHeight: 1.4,
                          }}
                        >
                          <div>ราคาขายที่คำนวณได้สูงกว่าราคาตลาดที่ได้กำหนดไว้</div>
                        </div>
                      </Col>
                    </Row>
                  );
                }}
              </Form.Item>
              {/* ✅ อัปเดต: เปลี่ยน align จาก "middle" → "bottom" เพื่อให้เนื้อหาในแถว "ราคาขาย" ชิดขอบล่าง */}
              <Row gutter={16} style={{ marginTop: 15 }} align="bottom"  >
                <Col span={14}>
                  <Typography.Text>
                    ราคาขาย :
                  </Typography.Text>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={[name, "factory_price"]}
                    rules={[
                      {
                        required: !isDraftSave, // ✅ ถ้าบันทึกร่าง ไม่บังคับ
                        message: "กรุณากรอกราคาขาย",
                      },
                      {
                        validator: (_, value) => {
                          // ✅ ถ้าบันทึกร่าง skip validation
                          if (isDraftSave) {
                            return Promise.resolve();
                          }
                          if (value === 0 || !value) {
                            return Promise.reject("ราคาขายต้องมากกว่า 0");
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      disabled
                      // ✅ ทำให้ "ราคาขาย" (factory_price) แสดงเป็นสีฟ้า ถึงแม้จะ disabled
                      // ✅ อัปเดต: เปลี่ยนเป็น "พื้นหลังสีฟ้า + ตัวหนังสือสีดำ" ตามคำสั่งผู้ใช้
                      // ✅ อัปเดต (ล่าสุด): ใช้สีเดียวกับ "ราคาตลาดที่แสดง" แบบปกติ (ไม่มีเงื่อนไข)
                      //   borderColor: #1890ff, backgroundColor: #e6f7ff, color: #1890ff, fontWeight: 600
                      className="factory-price-blue"
                      style={{
                        width: "100%",
                        padding: "4px 8px",
                        textAlign: "right",
                        borderColor: "#1890ff",
                        backgroundColor: "#e6f7ff",
                        color: "#1890ff",
                        fontWeight: 600,
                      }}
                      formatter={formatWithCommas}
                      size="small"
                      min={0}
                      max={999999}
                      controls={false}
                      maxLength={6}
                      onKeyDown={integerGuards.onKeyDown}
                      onPaste={integerGuards.onPaste}
                      parser={(value) => {
                        const parsed = value?.replace(/\D/g, "").slice(0, 6);
                        return parsed ? parseInt(parsed) : 0;
                      }}
                      placeholder="กรอกเองหรือคำนวณอัตโนมัติ"
                    // ✅ disabled = กรอกไม่ได้จริง (ตาม requirement ล่าสุด)
                    // onChange={() => handleFormChange("factory_price")}
                    // ✅ factory_price เป็น readonly → ไม่ควร clear ค่าเมื่อ focus
                    />
                  </Form.Item>
                  {/* Comment: dependencies wrapper สำหรับ helpText 
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                */}
                </Col>
              </Row>
              {/* ✅ ราคาท้องตลาด — readonly display (ค่ามาจาก retail_price ที่กรอกใน Card ราคาตั้งต้น) */}
              <Row gutter={16} style={{ marginTop: 12 }} align="middle">
                <Col span={14}>
                  <Typography.Text>
                    ราคาที่แนะนำให้ผู้ซื้อนำไปขายต่อ :
                  </Typography.Text>
                </Col>
                <Col span={8}>
                  {/* Comment: dependencies wrapper สำหรับ helpText
                <Form.Item dependencies={[...]}>
                  {({ getFieldValue }) => {
                    const baseRetailPrice = getFieldValue([...]);
                    const transportPrice = getFieldValue([...]);
                    const currentPrice = getFieldValue([...]);
                    let helpText = ...;
                    return (
                */}
                  <Form.Item
                    shouldUpdate={(prevValues: any, currentValues: any) => {
                      const prevItem = prevValues.sku_items?.[name];
                      const curItem = currentValues.sku_items?.[name];
                      // ✅ เพิ่ม input_market_price เป็น dependency เพื่อให้ re-render
                      //    เมื่อ "ราคาตลาด" ด้านบนเปลี่ยน จะได้เปรียบเทียบกับ "ราคาตลาดที่แสดง" ด้านล่าง
                      //    แล้วสลับสีส้มตามเงื่อนไข retail_price > input_market_price
                      return prevItem?.retail_price !== curItem?.retail_price ||
                        prevItem?.factory_price !== curItem?.factory_price ||
                        prevItem?.input_market_price !== curItem?.input_market_price;
                    }}
                    noStyle
                  >
                    {({ getFieldValue }: any) => {
                      const retailPriceVal = getFieldValue(["sku_items", name, "retail_price"]);
                      // ✅ ดึง "ราคาตลาด" (input_market_price) ด้านบน มาเปรียบเทียบ
                      const inputMarketPriceVal = getFieldValue(["sku_items", name, "input_market_price"]);
                      const retailNum = toFiniteNumberOr0(retailPriceVal);
                      const marketNum = toFiniteNumberOr0(inputMarketPriceVal);
                      // ✅ ถ้า "ราคาตลาดที่แสดง" (retail_price) มากกว่า "ราคาตลาด" (input_market_price)
                      //    ให้ไฮไลท์ input เป็นสีส้ม (เตือนว่าราคาที่แสดงแพงกว่าตลาดจริง)
                      //    เงื่อนไข: ต้องมี marketNum > 0 ด้วย เพื่อกันกรณียังไม่ได้กรอกราคาตลาด
                      const isOverMarket = marketNum > 0 && retailNum > marketNum;
                      return (
                        <InputNumber
                          style={{
                            width: "100%",
                            padding: "4px 8px",
                            textAlign: "right",
                            // ✅ ไฮไลท์สีส้มเมื่อราคาที่แสดงมากกว่าราคาตลาด
                            //    ถ้าปกติ (ไม่เกินราคาตลาด) → แสดงเป็นสีฟ้า
                            ...(isOverMarket
                              ? {
                                borderColor: "#fa8c16",
                                backgroundColor: "#fff7e6",
                                color: "#fa8c16",
                                fontWeight: 600,
                              }
                              : {
                                borderColor: "#1890ff",
                                backgroundColor: "#e6f7ff",
                                color: "#1890ff",
                                fontWeight: 600,
                              }),
                          }}
                          formatter={formatWithCommas}
                          size="small"
                          min={0}
                          max={999999}
                          controls={false}
                          value={retailPriceVal}
                          placeholder="-"
                          onFocus={handleFocusHighlightAllText("retail_price")}
                          onChange={(newValue) => {
                            const numValue = toFiniteNumberOr0(newValue);
                            form.setFieldValue(["sku_items", name, "retail_price"], numValue);
                          }}
                          onBlur={handleDisplayRetailPriceBlur}
                        />
                      );
                    }}
                  </Form.Item>
                  {/* Comment: dependencies wrapper ปิด
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                */}
                </Col>
              </Row>
              {/*✅ ราคา VIP - แสดงเมื่อ sell_through_line = true*/}
              {sellThroughLine && (
                <Row gutter={16} style={{ marginTop: 12 }} align="middle">
                  <Col span={14}>
                    <Typography.Text>
                      ราคา VIP:
                    </Typography.Text>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name={[name, "vip_price"]}
                      rules={[
                        {
                          required: false,
                          message: "กรุณากรอกราคา VIP",
                        },
                        {
                          type: "number",
                          min: 0,
                          message: "ราคาต้องไม่ต่ำกว่า 0",
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        disabled
                        style={{
                          width: "100%",
                          padding: "4px 8px",
                          textAlign: "right",
                          backgroundColor: "#f5f5f5",
                        }}
                        formatter={formatWithCommas}
                        size="small"
                        min={0}
                        max={999999}
                        controls={false}
                        maxLength={6}
                        onKeyDown={integerGuards.onKeyDown}
                        onPaste={integerGuards.onPaste}
                        parser={(value) => {
                          const parsed = value?.replace(/\D/g, "").slice(0, 6);
                          return parsed ? parseInt(parsed) : 0;
                        }}
                        placeholder="กรุณากรอกราคา VIP"
                      // ✅ ต้องการให้กรอกไม่ได้จริง (disabled)
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
              {/*✅ ราคาที่ขายใน Line OA (wholesale_price) - แสดงเมื่อ sell_through_line = true*/}
              {sellThroughLine && (
                <Row gutter={16} style={{ marginTop: 12 }} align="middle">
                  <Col span={14}>
                    <Typography.Text>
                      ราคาที่ขายใน Line:
                    </Typography.Text>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name={[name, "wholesale_price"]}
                      rules={[
                        {
                          required: false,
                          message: "กรุณากรอกราคาที่ขายใน Line",
                        },
                        {
                          type: "number",
                          min: 0,
                          message: "ราคาต้องไม่ต่ำกว่า 0",
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        disabled
                        style={{
                          width: "100%",
                          padding: "4px 8px",
                          textAlign: "right",
                          backgroundColor: "#f5f5f5",
                        }}
                        formatter={formatWithCommas}
                        size="small"
                        min={0}
                        max={999999}
                        controls={false}
                        maxLength={6}
                        onKeyDown={integerGuards.onKeyDown}
                        onPaste={integerGuards.onPaste}
                        parser={(value) => {
                          const parsed = value?.replace(/\D/g, "").slice(0, 6);
                          return parsed ? parseInt(parsed) : 0;
                        }}
                        placeholder="กรุณากรอกราคาที่ขายใน Line"
                      // ✅ ต้องการให้กรอกไม่ได้จริง (disabled)
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
              {/* ✅ ปิด wrapper marginTop:auto ของฝั่งซ้าย (block ราคาชิดล่าง) */}
            </div>
          </Col>
          {/* ✅ ฝั่งขวา (ราคาต่อหน่วย) — ทำเป็น flex column เช่นกันเพื่อให้โครงสร้างสมมาตร
               (ฝั่งขวามีแถวเต็มอยู่แล้ว ไม่จำเป็นต้อง marginTop:auto แต่คง flex-column ไว้
               เพื่อป้องกันเคสอนาคตที่เนื้อหาฝั่งขวาสั้นกว่าฝั่งซ้าย) */}
          <Col
            className="gutter-row"
            span={12}
            style={{ display: "flex", flexDirection: "column" }}
          >
            <Row gutter={[16, 16]}>
              <Col className="gutter-row" span={16}>
                <h4>สรุปราคาต่อหน่วย</h4>
              </Col>
            </Row>

            {/* ✅ เพิ่ม: ช่อง "จำนวนต่อหน่วย" (form field quantity_per_unit, ส่งขึ้น API)
                — ใช้หาร factory_price / retail_price เพื่อเติม unit_factory_price / unit_retail_price อัตโนมัติ
                — ว่าง/0 จะไม่ override ค่าเดิม (คงค่า DB ในโหมด edit) */}
            <Row gutter={20} style={{ marginTop: 15 }} align="middle">
              <Col span={8}>
                <Typography.Text>จำนวนสินค้าใน 1 ชุด :</Typography.Text>
              </Col>
              <Col span={14}>
                <Row gutter={8} align="middle" wrap={false}>
                  <Col flex="none">
                    {/* ✅ noStyle — ไม่แสดง error ใต้ input กว้าง 72px (ย้ายไปแถวถัดไปเต็มความกว้าง) */}
                    <Form.Item
                      name={[name, "quantity_per_unit"]}
                      rules={[{ required: true, message: "กรุณากรอกจำนวนชิ้นต่อหน่วย" }]}
                      noStyle
                    >
                      <Input
                        size="small"
                        inputMode="numeric"
                        placeholder="เช่น 10"
                        maxLength={6}
                        style={{
                          width: 72,
                          padding: "4px 8px",
                          textAlign: "right",
                          height: "24px", // ✅ ให้สูงเท่า Input ราคาอื่นใน card นี้
                          backgroundColor: "#fff0f6",
                        }}
                        onChange={(e) => {
                          // ✅ รับเฉพาะตัวเลข (ไม่รับทศนิยม เพราะจำนวนชิ้นเป็นจำนวนเต็ม)
                          const v = e.target.value.replace(/\D/g, "");
                          form.setFieldValue(["sku_items", name, "quantity_per_unit"], v);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="none" style={{ width: 100 }}>
                    <Form.Item name={[name, "product_unit"]} style={{ marginBottom: 0 }}>
                      <PinkBgSelect
                        showSearch
                        allowClear
                        loading={productUnitLoading}
                        placeholder="หน่วย"
                        options={productUnitData?.data
                          ?.slice()
                          .sort((a, b) => a.sort_index - b.sort_index)
                          .map((u) => ({ value: u.uuid, label: u.description }))}
                        filterOption={(input, option) =>
                          (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
            {/* ✅ แถว error แยก — แสดงยาวเต็มพื้นที่ใต้ label + input (ไม่ถูกบีบใต้ช่อง 72px) */}
            <Form.Item noStyle shouldUpdate>
              {() => {
                const errors = form.getFieldError(["sku_items", name, "quantity_per_unit"]);
                if (!errors.length) return null;
                return (
                  <Row gutter={20} style={{ marginTop: 4 }}>
                    <Col span={8} />
                    <Col span={14}>
                      <div
                        style={{
                          color: "#ff4d4f",
                          fontSize: 14,
                          lineHeight: 1.5,
                          whiteSpace: "normal",
                        }}
                      >
                        {errors[0]}
                      </div>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
            <Row gutter={22} style={{ marginTop: 15 }} align="middle">
              <Col span={8}>
                <Typography.Text>ราคาขายเฉลี่ย :</Typography.Text>
              </Col>
              <Col span={14}>
                <Row gutter={8} align="middle" wrap={false}>
                  <Col flex="auto">
                    <Form.Item
                      name={[name, "unit_factory_price"]}
                      initialValue="0"
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (isDraftSave) return Promise.resolve();
                            if (value && value.length > 10) {
                              return Promise.reject(
                                new Error("ราคาต่อหน่วยต้องไม่เกิน 10 ตัวอักษร")
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        // ✅ ราคาต่อหน่วย: เปิดให้กรอกได้เสมอใน create/edit/copy mode
                        // ✅ ปรับปรุงใหม่: เป็น readOnly — ระบบคำนวณอัตโนมัติจาก factory_price / quantityPerUnit
                        readOnly
                        style={{
                          width: "100%",
                          padding: "4px 8px",
                          textAlign: "right",
                          height: "24px", // ✅ เพิ่ม height ให้เท่ากับ InputNumber size="small"
                          backgroundColor: "#f5f5f5", // ✅ พื้นหลังเทาเพื่อสื่อว่า readOnly
                        }}
                        size="small"
                        placeholder="0.00"
                        maxLength={10}
                      // ❌ onFocus/onChange เดิมถูกปิดไว้ (ราคาต่อหน่วยถูกคำนวณโดยระบบ)
                      // onFocus={() => handleFocusUnitPrice("unit_factory_price")}
                      // onChange={(e) => {
                      //   const processedValue = removeLeadingZeros(e.target.value);
                      //   // อัปเดตค่าใน form
                      //   form.setFieldValue(["sku_items", name, "unit_factory_price"], processedValue);
                      //   // อัปเดตค่าใน input
                      //   e.target.value = processedValue;
                      // }}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="none">
                    <Typography.Text>(ต่อ{selectedProductUnitLabel})</Typography.Text>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row gutter={20} style={{ marginTop: 12 }} align="middle">
              <Col span={8}>
                <Typography.Text>ราคาขายแนะนำ :</Typography.Text>
              </Col>
              <Col span={14}>
                <Row gutter={8} align="middle" wrap={false}>
                  <Col flex="auto">
                    <Form.Item
                      name={[name, "unit_retail_price"]}
                      initialValue="0"
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (isDraftSave) return Promise.resolve();
                            if (value && value.length > 10) {
                              return Promise.reject(
                                new Error("ราคาต่อหน่วยต้องไม่เกิน 10 ตัวอักษร")
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        // ✅ ราคาต่อหน่วย: เปิดให้กรอกได้เสมอใน create/edit/copy mode
                        // ✅ ปรับปรุงใหม่: เป็น readOnly — ระบบคำนวณอัตโนมัติจาก retail_price / quantityPerUnit
                        readOnly
                        style={{
                          width: "100%",
                          padding: "4px 8px",
                          textAlign: "right",
                          height: "24px", // ✅ เพิ่ม height ให้เท่ากับ InputNumber size="small"
                          backgroundColor: "#f5f5f5", // ✅ พื้นหลังเทาเพื่อสื่อว่า readOnly
                        }}
                        size="small"
                        placeholder="0.00"
                        maxLength={10}
                      // ❌ onFocus/onChange เดิมถูกปิดไว้ (ราคาต่อหน่วยถูกคำนวณโดยระบบ)
                      // onFocus={() => handleFocusUnitPrice("unit_retail_price")}
                      // onChange={(e) => {
                      //   const processedValue = removeLeadingZeros(e.target.value);
                      //   // อัปเดตค่าใน form
                      //   form.setFieldValue(["sku_items", name, "unit_retail_price"], processedValue);
                      //   // อัปเดตค่าใน input
                      //   e.target.value = processedValue;
                      // }}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="none">
                    <Typography.Text>(ต่อ{selectedProductUnitLabel})</Typography.Text>
                  </Col>
                </Row>
              </Col>
            </Row>
            {/* ✅ ตาม requirement ล่าสุด: ไม่แสดง "ราคาที่ขายใน Line" และ "ราคา VIP" ในส่วนราคาต่อหน่วยทุกกรณี */}
            {/* {sellThroughLine && (
            <Row gutter={16} style={{ marginTop: 12 }} align="middle">
              <Col span={8}>
                  <Typography.Text>ราคาที่ขายใน Line:</Typography.Text>
              </Col>
              <Col span={10}>
                <Row gutter={8} align="middle" wrap={false}>
                  <Col flex="auto">
               <Form.Item
                 name={[name, "unit_wholesale_price"]}
                 initialValue="0"
                 style={{ marginBottom: 0 }}
                 rules={[
                   {
                     validator: (_, value) => {
                       if (isDraftSave) return Promise.resolve();
                       if (value && value.length > 7) {
                         return Promise.reject(
                           new Error("ราคาต่อหน่วยต้องไม่เกิน 7 ตัวอักษร")
                         );
                       }
                       return Promise.resolve();
                     },
                   },
                 ]}
               >
                 <Input
                        // ✅ ราคาต่อหน่วย: เปิดให้กรอกได้เสมอใน create/edit/copy mode
                   style={{
                     width: "100%",
                          padding: "4px 8px",
                     textAlign: "right",
                          height: "24px",
                   }}
                        size="small"
                   placeholder="0.00"
                   maxLength={7}
                   onFocus={() => handleFocusUnitPrice("unit_wholesale_price")}
                   onChange={(e) => {
                     const processedValue = removeLeadingZeros(e.target.value);
                     form.setFieldValue(["sku_items", name, "unit_wholesale_price"], processedValue);
                     e.target.value = processedValue;
                   }}
                 />
               </Form.Item>
              </Col>
                  <Col flex="none">
                    <Typography.Text>(ต่อหน่วย)</Typography.Text>
              </Col>
            </Row>
              </Col>
            </Row>
            )} */}
            {/* {sellThroughLine && (
            <Row gutter={16} style={{ marginTop: 12 }} align="middle">
              <Col span={8}>
                  <Typography.Text>ราคา VIP:</Typography.Text>
              </Col>
              <Col span={10}>
                <Row gutter={8} align="middle" wrap={false}>
                  <Col flex="auto">
               <Form.Item
                      name={[name, "unit_vip_price"]}
                 initialValue="0"
                 style={{ marginBottom: 0 }}
                 rules={[
                   {
                     validator: (_, value) => {
                       if (isDraftSave) return Promise.resolve();
                       if (value && value.length > 7) {
                         return Promise.reject(
                           new Error("ราคาต่อหน่วยต้องไม่เกิน 7 ตัวอักษร")
                         );
                       }
                       return Promise.resolve();
                     },
                   },
                 ]}
               >
                 <Input
                        // ✅ ราคาต่อหน่วย: เปิดให้กรอกได้เสมอใน create/edit/copy mode
                   style={{
                     width: "100%",
                          padding: "4px 8px",
                     textAlign: "right",
                          height: "24px",
                   }}
                        size="small"
                   placeholder="0.00"
                   maxLength={7}
                        onFocus={() => handleFocusUnitPrice("unit_vip_price")}
                   onChange={(e) => {
                     const processedValue = removeLeadingZeros(e.target.value);
                          form.setFieldValue(["sku_items", name, "unit_vip_price"], processedValue);
                     e.target.value = processedValue;
                   }}
                 />
               </Form.Item>
              </Col>
                  <Col flex="none">
                    <Typography.Text>(ต่อหน่วย)</Typography.Text>
              </Col>
            </Row>
              </Col>
            </Row>
            )} */}
          </Col>
        </Row>
      </Card>

      {/* ✅ การจัดเตรียมสินค้า (Production Time) */}
      <Card style={{ width: "95%", padding: 15, marginTop: 15 }} bodyStyle={{ padding: "16px" }}>
        <Typography.Title
          level={5}
          style={{ marginTop: 0, marginBottom: 15 }}
        >
          ระยะเวลาจัดเตรียมสินค้า
        </Typography.Title>
        <Row gutter={16}>
          {/* ฝั่งซ้าย: input */}
          <Col span={12}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues?.sku_items?.[name]?.preparation_time !== currentValues?.sku_items?.[name]?.preparation_time
              }
            >
              {({ getFieldValue, setFieldValue }) => {
                const prepTime = getFieldValue(["sku_items", name, "preparation_time"]) || 1;
                const isImmediate = prepTime === 1;

                return (
                  <Form.Item
                    name={[name, "preparation_time"]}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (isDraftSave) return Promise.resolve();
                          if (!value || value < 1) {
                            return Promise.reject(new Error("กรุณาระบุระยะเวลาจัดเตรียมสินค้า (เริ่มต้นที่ 1 วัน)"));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Radio.Group
                      style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === 1) {
                          setFieldValue(["sku_items", name, "preparation_time"], 1);
                        } else {
                          setFieldValue(["sku_items", name, "preparation_time"], isImmediate ? 2 : prepTime);
                        }
                      }}
                    >
                      {/* 1. พร้อมส่งทันที (ค่า = 1) */}
                      <Radio value={1}>
                        พร้อมส่งทันที
                      </Radio>

                      {/* 2. พรีออเดอร์ (ค่า >= 2) */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Radio value={isImmediate ? 2 : prepTime}>
                          ระบุ
                        </Radio>
                        <InputNumber
                          min={2}
                          max={365} // สมมติ max 1 ปีไปก่อน
                          value={isImmediate ? 2 : prepTime}
                          onChange={(val) => {
                            if (val && val >= 2) {
                              setFieldValue(["sku_items", name, "preparation_time"], val);
                            }
                          }}
                          disabled={isImmediate}
                          controls={false} // ✅ เพิ่ม controls={false} ตามที่ user ขอ
                          style={{ width: 80, textAlign: 'center' }}
                        />
                        <Typography.Text>วัน</Typography.Text>
                      </div>
                    </Radio.Group>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>

          {/* ฝั่งขวา: คำอธิบาย */}
          <Col span={12}>
            <div style={{
              border: "1px dashed rgb(200, 199, 199)",
              borderRadius: "4px",
              padding: "12px 16px",
              height: "100%",
            }}>
              <Typography.Title level={5} style={{ color: "#1890ff", marginTop: 0 }} onClick={handleOpenPasswordForm} >
                ℹ️ หมายเหตุ
              </Typography.Title>
              <Typography.Paragraph style={{ marginBottom: 0, paddingLeft: 16 }}>
                ในกรณีที่เลือกส่งกับ Partner แบ่งเบา เมื่อกดพิมพ์ Label EMS รถขนส่งจะเข้ามารับสินค้าภายใน 1 - 2 วัน แล้วแต่พื้นที่และบริษัทขนส่ง
              </Typography.Paragraph>
            </div>
          </Col>
        </Row>
      </Card>

      {/*วิธีคำนวณราคาสุดท้าย - ย้ายออกนอกกรอบ*/}


      <Modal
        title="พบข้อมูลโรงงานไม่ครบถ้วน"
        open={isMissingEmailModalOpen}
        onCancel={() => setIsMissingEmailModalOpen(false)}
        onOk={() => {
          if (!missingEmailInput) {
            message.warning("กรุณาระบุ E-Mail Address");
            return;
          }
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(missingEmailInput)) {
            message.warning("รูปแบบ E-Mail Address ไม่ถูกต้อง");
            return;
          }
          setIsMissingEmailModalOpen(false);
          handleRegisterMerchant(missingEmailInput);
        }}
        okText="ลงทะเบียน"
        cancelText="ยกเลิก"
        confirmLoading={isReplacingMerchant}
      >
        <Alert
          type="warning"
          message="ไม่พบข้อมูล E-Mail Address สำหรับลงทะเบียนใช้บริการบริษัทขนส่ง Partner แบ่งเบา กรุณาระบุ E-Mail Address เพื่อลงทะเบียน"
          style={{ marginBottom: 16 }}
        />
        <Input
          placeholder="example@email.com"
          value={missingEmailInput}
          onChange={(e) => setMissingEmailInput(e.target.value)}
          type="email"
        />
      </Modal>

    </FormWrapper>
  );
};

export default VariationOptionForm;

// ใช้ร่วมกับ Modal SKU (ProductSKUCard / SkuModalStockEditShell)
export {
  SKU_MODAL_TITLE,
  getSkuModalTitle,
  handleSkuModalDrag,
  SkuDraggableModalTitle,
} from "./skuModalShared";

const BlinkingButton = styled(Button) <{ $shouldBlink: boolean }>`
  ${props => props.$shouldBlink && `
    animation: blink 1s ease-in-out infinite;
  `}
  
  @keyframes blink {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(1);
    }
  }
`;

const FormWrapper = styled.div`
  .ant-input-number {
    .ant-input-number-input {
      text-align: right !important;
    }
  }
`;

/* ✅ ใช้กับ Select "หน่วย" ของ "จำนวนสินค้าใน 1 ชุด" — ต้อง override .ant-select-selector
   ตรงๆ เพราะ background ของ Select ปกติไม่รับค่าจาก prop style ของ root element โดยตรง */
const PinkBgSelect = styled(Select)`
  .ant-select-selector {
    background-color: #fff0f6 !important;
  }
`;

/* ✅ ช่อง "จำนวนชิ้นต่อหน่วย" กว้างแค่ 72px ข้อความ error เลยตัดบรรทัดหลายบรรทัด
   และดัน Input ขึ้น (เพราะ Row เป็น align="middle")
   → ลอย error ออกนอก flow และบังคับให้อยู่บรรทัดเดียว Input จะได้ไม่ขยับ */
const QuantityPerUnitField = styled.div`
  position: relative;

  .ant-form-item-explain {
    position: absolute;
    top: 100%;
    left: 0;
    white-space: nowrap;
    font-size: 12px;
    line-height: 16px;
    pointer-events: none;
    z-index: 1;
  }

  .ant-form-item-margin-offset {
    margin-bottom: 0 !important;
  }
`;